import { useState, useRef, useEffect, useCallback, Fragment } from "react";
import { ChevronDown, ChevronRight, FileText, Download, Send, Bot, Edit2, Check, X, FlaskConical, Activity, GitBranch, DollarSign, Sparkles, Loader2, CheckCircle2, ChevronLeft, TrendingUp, Zap, Play, RotateCcw, Plus, Upload, FileUp, AlertTriangle, Eye, User, Users, Beaker, Database, Shield, Sun, Moon, BarChart2, Table2, Info, Pencil, Trash2 } from "lucide-react";
import useUseCaseStore from "./useUseCaseStore";
import { safeSetItem } from "./storage";
import ConfirmationCard from "./ConfirmationCard";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import "./print.css";

// ============================================================
// STATIC: GNQ Platform Capability Blocks (shared across all use cases)
// ============================================================
const GNQ_CAPABILITY_BLOCKS = [
  { id: "data_integration", title: "Multi-Modal Data Integration", Icon: Database, iconCls: "text-cyan-400", dotCls: "bg-cyan-500/60",
    items: ["Genomic pathway databases (Reactome, KEGG, BioCarta)", "Pharmacokinetic and pharmacodynamic modeling", "Clinical trial data and endpoint validation", "Real-world evidence from post-market surveillance", "Biomarker correlation analysis"] },
  { id: "pathway_analysis", title: "Pathway Analysis", Icon: GitBranch, iconCls: "text-emerald-400", dotCls: "bg-emerald-500/60",
    items: ["Disease-relevant pathway identification and enrichment", "Gene-drug interaction mapping", "Cross-pathway interaction modeling", "Quantitative pathway scoring by clinical relevance", "Topology-aware signal propagation (SCC-based)"] },
  { id: "causal_engine", title: "Causal Inference Engine", Icon: Zap, iconCls: "text-amber-400", dotCls: "bg-amber-500/60",
    items: ["Mechanism of action mapping via directed acyclic graphs", "Drug synergy and interaction analysis", "Pathway coverage quantification per component", "Counterfactual reasoning for alternative strategies", "Uncertainty quantification with confidence intervals"] },
  { id: "comparative", title: "Comparative Therapy Assessment", Icon: BarChart2, iconCls: "text-violet-400", dotCls: "bg-violet-500/60",
    items: ["Multi-tier dose scenario scoring and optimization", "Cross-therapy efficacy and safety benchmarking", "Relative risk-benefit analysis across populations", "Cost-effectiveness considerations"] },
  { id: "risk_reward", title: "Risk-Reward Domain Analysis", Icon: Shield, iconCls: "text-rose-400", dotCls: "bg-rose-500/60",
    items: ["Efficacy domain modeling across endpoints", "Safety domain assessment for adverse events", "Temporal risk profiling for treatment duration", "Population-specific risk stratification", "Multi-criteria benefit-risk integration"] },
  { id: "stratification", title: "Patient Stratification & Optimization", Icon: Users, iconCls: "text-sky-400", dotCls: "bg-sky-500/60",
    items: ["Biomarker-driven patient subgroup identification", "Treatment response prediction per subgroup", "Dose optimization for individual characteristics", "Adaptive treatment strategies", "Clinical decision support for therapy selection"] },
];

// ============================================================
// HELPER: Parse drug components from sec3 static text
// ============================================================
const parseComponents = (text) => {
  if (!text) return [];
  return text.split('\n')
    .filter(line => line.trim().startsWith('\u2022'))
    .map(line => {
      const match = line.match(/\u2022\s*(.+?)\s*\((.+?)\):\s*(.+)/);
      if (match) return { name: match[1].trim(), role: match[2].trim(), mechanism: match[3].trim() };
      return null;
    })
    .filter(Boolean)
    .slice(0, 3);
};

// ============================================================
// HELPER: Render formatted text with bold/bullet support
// ============================================================
const renderText = (text, textClass = "text-slate-300") => {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (!line.trim()) return <br key={i} />;
    const isBold = line.startsWith('**') && line.includes('**');
    const isBullet = line.startsWith('\u2022');
    const isNumbered = /^\d+\./.test(line.trim());
    if (isBold) return <div key={i} className="text-sm font-semibold text-cyan-400 mt-3 mb-1">{line.replace(/\*\*/g, '')}</div>;
    if (isBullet) return <div key={i} className={`text-xs ${textClass} ml-4 mb-0.5`}>{line}</div>;
    if (isNumbered) return <div key={i} className={`text-xs ${textClass} ml-2 mb-0.5`}>{line}</div>;
    return <div key={i} className={`text-xs ${textClass} mb-1`}>{line}</div>;
  });
};

// ============================================================
// NAV CONFIG - Driven by available sections in YAML
// ============================================================
const navSections = [
  { id: "overview", label: "Overview", icon: FileText, items: [{ id: "executive", label: "1. Executive Summary" }] },
  { id: "causal", label: "GNQ Platform", icon: GitBranch, items: [
    { id: "pathway-scores", label: "2. Pathway Landscape" },
    { id: "mechanism-mapping", label: "3. Drug Composition & MOA" },
    { id: "causal-inference", label: "4. Real World Evidence" }
  ]},
  { id: "clinical", label: "Clinical Projections", icon: Activity, items: [
    { id: "stratification", label: "5. Patient Stratification" },
    { id: "efficacy", label: "6. Efficacy Projections" },
    { id: "safety", label: "7. Safety Projections" },
    { id: "clinical-radar", label: "Clinical Radar (5-7)" },
    { id: "summary", label: "8. Comparative Analysis" }
  ]},
  { id: "commercial", label: "Commercial", icon: DollarSign, items: [
    { id: "market", label: "9. Competitor Analysis" },
    { id: "valuation", label: "10. Commercial Opportunity" },
    { id: "valuation-framework", label: "11. Valuation Framework" }
  ]}
];

const SECTION_NAV_MAP = {
  sec1: "executive", sec2: "pathway-scores", sec3: "mechanism-mapping",
  sec4: "causal-inference", sec5: "stratification", sec6: "efficacy",
  sec7: "safety", sec8: "summary", sec9: "market", sec10: "valuation", sec11: "valuation-framework",
};

// ============================================================
// CONFIG CHANGE -> SECTION IMPACT MAPPING (granular)
// Mirrors backend SECTION_DEPENDENCIES in pipeline_cache.py
// sec1 is NOT listed — it auto-cascades when any upstream section changes
// ============================================================
const CONFIG_SECTION_MAP = {
  "therapy.identity":     ["sec2","sec3","sec4","sec5","sec6","sec7","sec8","sec9","sec10","sec11"],
  "therapy.components":   ["sec3","sec5","sec6","sec7","sec8"],
  "therapy.comparators":  ["sec6","sec7","sec8"],
  indication:             ["sec2","sec4","sec5","sec9","sec10","sec11"],
  clinical_endpoints:     ["sec2","sec3","sec5","sec6","sec7","sec8"],
  dose_config:            ["sec3","sec5","sec6","sec7","sec8"],
  dose_scenarios:         ["sec3","sec5","sec6","sec7","sec8"],
  competitive_landscape:  ["sec9"],
  commercial_assumptions: ["sec10","sec11"],
};

const CATEGORY_LABELS = {
  "therapy.identity": "Therapy Name / Company",
  "therapy.components": "Drug Components",
  "therapy.comparators": "Comparators",
  indication: "Indication / Disease",
  clinical_endpoints: "Clinical Endpoints",
  dose_config: "Dose Choices",
  dose_scenarios: "Dose Scenarios",
  competitive_landscape: "Competitive Landscape",
  commercial_assumptions: "Commercial Assumptions",
};

const SECTION_LABELS = {
  sec1: "1. Executive Summary", sec2: "2. Pathway Landscape", sec3: "3. Drug Composition & MOA",
  sec4: "4. Real World Evidence", sec5: "5. Patient Stratification", sec6: "6. Efficacy Projections",
  sec7: "7. Safety Projections", sec8: "8. Comparative Analysis", sec9: "9. Competitor Analysis",
  sec10: "10. Commercial Opportunity", sec11: "11. Valuation Framework",
};


const deepMerge = (target, source) => {
  const out = { ...target };
  for (const key in source) {
    if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
      out[key] = deepMerge(target[key] || {}, source[key]);
    } else if (Array.isArray(source[key])) {
      // Only overwrite with non-empty arrays (don't wipe existing data with [])
      if (source[key].length > 0) out[key] = source[key];
    } else if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
      out[key] = source[key];
    }
  }
  return out;
};

const ONBOARDING_OPENING = "Welcome! I'm GNQ InSilico, your drug assessment assistant.\n\nI'll guide you through a quick setup — I'll ask a few questions about your therapy, run a full drug assessment, and then you can explore the results interactively and ask me anything. When you're done, export the full report.\n\nLet's get started. What therapy would you like to evaluate?";

// Context-aware welcome message based on use case state
function buildWelcomeMessage({ useCase, hasDocuments, hasSavedConfigs, hasReports, configLoaded, latestReportName, partialConfig }) {
  const name = useCase?.name;
  const indication = useCase?.indication;
  if (!name) return ONBOARDING_OPENING;

  const label = indication ? `**${name}** (${indication})` : `**${name}**`;

  // Has reports + config loaded
  if (hasReports && configLoaded) {
    return `Welcome back to ${label}. Your configuration is loaded in the left panel and your latest report${latestReportName ? ` (${latestReportName})` : ""} is available in the **Reports & History** tab at the bottom.\n\nWhat would you like to do?`;
  }

  // Has reports but config didn't load
  if (hasReports) {
    return `Welcome back to ${label}. Your latest report${latestReportName ? ` (${latestReportName})` : ""} is available in the **Reports & History** tab at the bottom.\n\nYou can:\n- **View the report** by clicking it in the **Reports & History** tab at the bottom\n- **Re-run** with updated inputs (restore a configuration from Saved Inputs)\n- **Ask me questions** about the results\n\nWhat would you like to do?`;
  }

  // Has config loaded, no report yet
  if (configLoaded) {
    return `Welcome back to ${label}. Your configuration is loaded in the left panel.\n\nYou can review it, make changes, or click **Run Assessment** when ready.`;
  }

  // Has documents but no config
  if (hasDocuments) {
    return `Welcome back to ${label}. I see you have documents uploaded.\n\nWould you like me to extract configuration from your documents, or would you prefer to enter details manually?`;
  }

  // Returning to a partially configured use case — detect what's filled
  if (partialConfig && typeof partialConfig === 'object') {
    const filled = [];
    if (partialConfig.therapy?.name) filled.push("therapy");
    if (partialConfig.indication?.disease) filled.push("indication");
    if (partialConfig.clinical_endpoints?.efficacy?.length > 0 || partialConfig.clinical_endpoints?.safety?.length > 0) filled.push("clinical endpoints");
    if (partialConfig.dose_scenarios?.length > 0 || partialConfig.dose_config?.therapy_dose_levels?.length > 0) filled.push("dose scenarios");
    if (partialConfig.competitive_landscape?.competitors?.length > 0) filled.push("competitive landscape");
    if (partialConfig.commercial_assumptions?.tam?.base_tam) filled.push("commercial assumptions");

    if (filled.length >= 2) {
      // Determine what's still missing
      const allSections = ["therapy", "indication", "clinical endpoints", "dose scenarios", "competitive landscape", "commercial assumptions"];
      const missing = allSections.filter(s => !filled.includes(s));
      const missingText = missing.length > 0
        ? `\n\nStill needed: **${missing.join("**, **")}**`
        : "\n\nAll sections are filled. You can review and click **Run Assessment** when ready.";
      return `Welcome back to ${label}. I have your ${filled.join(", ")} from last time.${missingText}\n\nWould you like to continue where you left off?`;
    }
  }

  // Brand new use case with indication known
  if (indication) {
    return `Welcome! Let's set up your assessment for ${label}.\n\nI already know the therapy name and indication. What company is developing ${name}?`;
  }

  return `Welcome! Let's set up your assessment for ${label}.\n\nWhat indication or disease is ${name} targeting?`;
}

const EMPTY_THERAPY_CONFIG = {
  therapy: { name: "", company: "", components: [], comparators: [] },
  indication: { disease: "", therapeutic_area: "" },
  dose_mode: "fixed",  // "fixed" = use exact UI doses, "optimize" = generate dose range for optimization
  dose_config: { therapy_dose_levels: [], comparators: [] },
  clinical_endpoints: { efficacy: [], safety: [] },
  competitive_landscape: { competitors: [], pipeline_threats: [] },
  commercial_assumptions: {
    tam: { base_tam: "", cagr: "" },
    market_share: { bear: "", base: "", bull: "" },
    pricing: { therapy_annual_cost: "", comparator_annual_cost: "" },
    development_stage: "", regulatory_pathway: "",
    success_probability: "",
    revenue_projection: { peak_year_post_launch: "" },
    valuation: { gross_margin: "", wacc: "", development_cost: "", terminal_growth_rate: "" },
  },
};

// Default pre-config therapy ID (used for initial load when no therapy is selected yet)
const DEFAULT_THERAPY_ID = "mlb001";

// Derive config filename from therapy name/ID: "MLB-001" → "mlb001", "Wegovy" → "wegovy"
const therapyIdToConfigPath = (id) => `/data/${(id || "").toLowerCase().replace(/[-_\s]+/g, "")}-config.json`;

// Fetch therapy config by ID, with fallback to null (no pre-config available)
const fetchPreConfig = async (therapyId) => {
  const path = therapyIdToConfigPath(therapyId);
  try {
    const r = await fetch(path);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = await r.json();
    console.log(`[preConfig] loaded ${path}:`, Object.keys(data));
    return data;
  } catch (e) {
    console.warn(`[preConfig] no config found at ${path}:`, e.message);
    return null;
  }
};

// ============================================================
// CHAT MARKDOWN RENDERER (lightweight, no dependencies)
// ============================================================
const renderChatMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  const CHART_COLORS = ["#06b6d4", "#f59e0b", "#10b981", "#8b5cf6", "#f43f5e", "#3b82f6", "#ec4899", "#14b8a6"];

  while (i < lines.length) {
    const line = lines[i];

    // Detect ```chart code fence
    if (line.trim().startsWith('```chart')) {
      let j = i + 1;
      let jsonStr = '';
      while (j < lines.length && !lines[j].trim().startsWith('```')) {
        jsonStr += lines[j];
        j++;
      }
      if (j < lines.length) j++; // skip closing ```
      try {
        const chartDef = JSON.parse(jsonStr);
        const { type, data, title, xKey = "name", bars, lines: lineKeys } = chartDef;
        const chartEl = (
          <div key={`chart-${i}`} className="my-3 p-3 bg-slate-800/60 rounded-lg border border-slate-700">
            {title && <div className="text-[11px] font-semibold text-cyan-400 mb-2">{title}</div>}
            <ResponsiveContainer width="100%" height={220}>
              {type === "bar" ? (
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "6px", fontSize: "11px" }} />
                  {(bars || [{ key: "value" }]).map((b, bi) => (
                    <Bar key={bi} dataKey={b.key} fill={b.color || CHART_COLORS[bi % CHART_COLORS.length]} radius={[3, 3, 0, 0]} />
                  ))}
                  {(bars || []).length > 1 && <Legend wrapperStyle={{ fontSize: "10px" }} />}
                </BarChart>
              ) : type === "line" ? (
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey={xKey} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "6px", fontSize: "11px" }} />
                  {(lineKeys || [{ key: "value" }]).map((l, li) => (
                    <Line key={li} type="monotone" dataKey={l.key} stroke={l.color || CHART_COLORS[li % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                  ))}
                  {(lineKeys || []).length > 1 && <Legend wrapperStyle={{ fontSize: "10px" }} />}
                </LineChart>
              ) : type === "pie" ? (
                <PieChart>
                  <Pie data={data} dataKey="value" nameKey={xKey} cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: "#64748b" }} style={{ fontSize: "10px" }}>
                    {data.map((_, di) => <Cell key={di} fill={CHART_COLORS[di % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "6px", fontSize: "11px" }} />
                </PieChart>
              ) : (
                <div className="text-xs text-red-400">Unsupported chart type: {type}</div>
              )}
            </ResponsiveContainer>
          </div>
        );
        elements.push(chartEl);
      } catch {
        elements.push(<div key={i} className="text-xs text-red-400 my-1">Failed to render chart</div>);
      }
      i = j;
      continue;
    }

    // Detect markdown table (line with | characters, followed by separator |---|)
    if (line.includes('|') && i + 1 < lines.length && lines[i + 1].match(/^\|?[\s-:|]+\|/)) {
      const tableLines = [];
      let j = i;
      while (j < lines.length && lines[j].includes('|')) {
        tableLines.push(lines[j]);
        j++;
      }
      // Parse header, skip separator, parse rows
      const parseRow = (row) => row.split('|').map(c => c.trim()).filter(c => c);
      const headers = parseRow(tableLines[0]);
      const rows = tableLines.slice(2).filter(r => !r.match(/^\|?[\s-:|]+\|?$/)).map(parseRow);

      elements.push(
        <div key={i} className="my-2 overflow-x-auto">
          <table className="w-full text-[10px] border-collapse">
            <thead>
              <tr className="bg-slate-700/50">{headers.map((h, hi) => <th key={hi} className="border border-slate-600 px-2 py-1 text-left text-cyan-400 font-semibold">{applyInline(h)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? "bg-slate-800/30" : "bg-slate-800/60"}>
                  {row.map((cell, ci) => <td key={ci} className="border border-slate-700 px-2 py-1 text-slate-300">{applyInline(cell)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j;
      continue;
    }

    // Headers
    if (line.startsWith('### ')) { elements.push(<div key={i} className="text-[11px] font-semibold text-cyan-400 mt-2 mb-0.5">{line.slice(4)}</div>); }
    else if (line.startsWith('## ')) { elements.push(<div key={i} className="text-xs font-semibold text-cyan-400 mt-2 mb-0.5">{line.slice(3)}</div>); }
    // Bullets
    else if (line.match(/^[-•]\s/)) { elements.push(<div key={i} className="text-xs text-slate-300 ml-2 mb-0.5 flex gap-1"><span className="text-cyan-400/60">-</span><span>{applyInline(line.slice(2))}</span></div>); }
    // Numbered lists
    else if (line.match(/^\d+\.\s/)) { elements.push(<div key={i} className="text-xs text-slate-300 ml-2 mb-0.5">{applyInline(line)}</div>); }
    // Empty lines
    else if (!line.trim()) { elements.push(<div key={i} className="h-1" />); }
    // Normal text
    else { elements.push(<div key={i} className="text-xs text-slate-300 mb-0.5">{applyInline(line)}</div>); }

    i++;
  }
  return elements;
};

const applyInline = (text) => {
  // Bold **text** and *text*
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="text-slate-100 font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*')) return <em key={i} className="text-slate-200">{part.slice(1, -1)}</em>;
    return part;
  });
};

// ============================================================
// AGENT STATUS BADGE
// ============================================================
function AgentBadge({ status }) {
  const colors = { complete: "bg-green-500/20 text-green-400 border-green-500/30", processing: "bg-amber-500/20 text-amber-400 border-amber-500/30 animate-pulse", pending: "bg-slate-500/20 text-slate-500 border-slate-500/30", error: "bg-red-500/20 text-red-400 border-red-500/30" };
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${colors[status] || colors.pending}`}>{status}</span>;
}

// ============================================================
// INSIGHT CARD (for document analysis results)
// ============================================================
function InsightCard({ insight, decision, modification, onAccept, onReject, onModify }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(typeof insight.value === "string" ? insight.value : JSON.stringify(insight.value, null, 2));
  const isAccepted = decision === "accepted";
  const isRejected = decision === "rejected";
  const isModified = decision === "modified";
  const displayValue = isModified && modification != null
    ? (typeof modification === "string" ? modification : JSON.stringify(modification, null, 2))
    : (typeof insight.value === "string" ? insight.value : JSON.stringify(insight.value, null, 2));
  const confColor = { high: "text-green-400 bg-green-500/20", medium: "text-amber-400 bg-amber-500/20", low: "text-red-400 bg-red-500/20" }[insight.confidence] || "text-slate-400 bg-slate-700";

  return (
    <div className={`rounded-lg p-2 border transition-all ${isRejected ? "border-red-500/20 opacity-40" : isModified ? "border-amber-500/30 bg-amber-500/5" : isAccepted ? "border-green-500/20 bg-green-500/5" : "border-slate-700"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[10px] font-medium text-slate-200">{insight.description}</span>
            <span className={`text-[8px] px-1 py-0.5 rounded ${confColor}`}>{insight.confidence}</span>
          </div>
          <div className="text-[9px] text-slate-500 mb-1">
            Field: <span className="text-cyan-400/70 font-mono">{insight.field_path}</span>
          </div>
          {!editing ? (
            <div className="text-[10px] text-slate-300 bg-slate-800/50 rounded px-2 py-1 font-mono break-all whitespace-pre-wrap">{displayValue}</div>
          ) : (
            <textarea value={editVal} onChange={e => setEditVal(e.target.value)} className="w-full text-[10px] bg-slate-800 border border-cyan-500/30 rounded px-2 py-1 text-slate-200 font-mono resize-none" rows={3} autoFocus />
          )}
          {insight.source && <div className="text-[8px] text-slate-600 mt-0.5 italic truncate">Source: {insight.source}</div>}
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button onClick={() => { if (editing) setEditing(false); onAccept(); }} className={`p-1 rounded ${isAccepted ? "bg-green-500/20 text-green-400" : "text-slate-600 hover:text-green-400"}`} title="Accept"><Check size={10} /></button>
          <button onClick={onReject} className={`p-1 rounded ${isRejected ? "bg-red-500/20 text-red-400" : "text-slate-600 hover:text-red-400"}`} title="Reject"><X size={10} /></button>
          <button onClick={() => { if (editing) { try { onModify(JSON.parse(editVal)); } catch { onModify(editVal); } setEditing(false); } else { setEditing(true); } }} className={`p-1 rounded ${isModified ? "bg-amber-500/20 text-amber-400" : "text-slate-600 hover:text-amber-400"}`} title="Modify"><Edit2 size={10} /></button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CHART EXPORT HELPERS
// ============================================================
function exportChartAsJSON(data, filename = "chart-data") {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function exportChartAsPNG(containerEl, filename = "chart") {
  const svg = containerEl?.querySelector("svg");
  if (!svg) return;
  const { width, height } = svg.getBoundingClientRect();
  const svgData = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([svgData], { type: "image/svg+xml" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = width || 600;
    canvas.height = height || 400;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${filename}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };
  img.src = url;
}

// ============================================================
// SECTION AGENT MAP
// ============================================================
const SECTION_AGENTS = {
  "executive":           "Executive AI",
  "pathway-scores":      "Pathway Analyzer",
  "mechanism-mapping":   "MOA Agent",
  "causal-inference":    "Evidence AI",
  "stratification":      "Stratification AI",
  "efficacy":            "Efficacy Analyst",
  "safety":              "Safety Analyst",
  "clinical-radar":      "Synthesis AI",
  "summary":             "Synthesis AI",
  "market":              "Market Analyst",
  "valuation":           "Valuation AI",
  "valuation-framework": "Framework AI",
};

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function GNQAssessmentPlatform() {
  // ── Flow state ──
  const [flowStep, setFlowStep] = useState("query"); // query | configPrep | review | pipeline | stopped | complete
  const flowStepRef = useRef("query");
  const [maxReachedStep, setMaxReachedStep] = useState(0); // tracks furthest step index for nav
  const [userInput, setUserInput] = useState("");
  const [queryData, setQueryData] = useState({ originalQuery: "" });

  // ── Config state (single source of truth for all config data) ──
  const [activeConfig, setActiveConfig] = useState({});
  useEffect(() => { flowStepRef.current = flowStep; }, [flowStep]);
  const [configPrepStatuses, setConfigPrepStatuses] = useState({});
  // Backward-compat aliases — TODO: remove after full migration
  const therapyConfig = activeConfig;
  const setTherapyConfig = setActiveConfig;
  const collectedConfig = activeConfig;
  const setCollectedConfig = setActiveConfig;

  // ── Review state ──
  const [expandedConfigSections, setExpandedConfigSections] = useState(["therapy"]);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  // Inline add-forms for Dose Choices
  const [addingTherapyDose, setAddingTherapyDose] = useState(false);
  const [newTherapyRoute, setNewTherapyRoute] = useState("");
  const [newTherapyComponents, setNewTherapyComponents] = useState([{ name: "", dose: "" }]);
  const [addingComparator, setAddingComparator] = useState(false);
  const [newCompName, setNewCompName] = useState("");
  const [newCompRoute, setNewCompRoute] = useState("");
  const [newCompDoses, setNewCompDoses] = useState("");

  // ── Pipeline & report state ──
  const [assessmentData, setAssessmentData] = useState(null);
  const [agentStatuses, setAgentStatuses] = useState({});
  const [readySections, setReadySections] = useState(new Set()); // nav item ids unlocked as sections complete
  const [flashingSections, setFlashingSections] = useState(new Set()); // nav item ids currently showing ping animation
  const [generatingSections, setGeneratingSections] = useState(new Set()); // nav item ids currently being generated (pulsing amber)

  // ── Use case switch confirmation ──
  const [pendingSwitchId, setPendingSwitchId] = useState(null); // non-null = confirmation dialog is open

  // ── UI state ──
  const [resetConfirm, setResetConfirm] = useState(false);
  const [clearCollectionConfirm, setClearCollectionConfirm] = useState(false);
  const [resetConfigConfirm, setResetConfigConfirm] = useState(false);
  const [pipelineError, setPipelineError] = useState(null); // set when pipeline fails — shown in stopped panel
  const [importLoading, setImportLoading] = useState(false); // true while zip import POST is in flight
  const [importError, setImportError] = useState(null);     // non-null string = import failed, shown inline
  const [activeSection, setActiveSection] = useState("executive");
  const [expandedNav, setExpandedNav] = useState(["overview", "causal", "clinical", "commercial"]);
  const [chatMessages, setChatMessages] = useState([{ role: "assistant", content: "Hello! Complete the assessment query to load the report. I can then help you explore pathway scores, efficacy projections, or any section." }]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [contextSent, setContextSent] = useState(false);
  const [showYAMLPreview, setShowYAMLPreview] = useState(false);

  // ── Theme state (Task 66/68) ──
  const [theme, setTheme] = useState(() => localStorage.getItem("gnq_theme") || "dark");
  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === "dark" ? "light" : "dark";
      safeSetItem("gnq_theme", next);
      return next;
    });
  }, []);
  const isDark = theme === "dark";

  // ── Table/Visual toggle (Task 72) — visual-first default ──
  const [viewMode, setViewMode] = useState("visual"); // "visual" | "table"

  // ── Bottom panel tab ──
  const [bottomPanelTab, setBottomPanelTab] = useState("chat"); // "chat" | "workspace"

  // ── Bottom panel resizable height ──
  const [bottomPanelHeight, setBottomPanelHeight] = useState(384); // default 24rem = 384px
  const bottomDragRef = useRef(null);
  const handleBottomDragStart = useCallback((e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = bottomPanelHeight;
    const onMove = (ev) => {
      const delta = startY - ev.clientY;
      const newH = Math.min(Math.max(startH + delta, 200), window.innerHeight * 0.7);
      setBottomPanelHeight(newH);
    };
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [bottomPanelHeight]);

  // ── Metric drill-down (Task 69/73) ──
  const [expandedMetric, setExpandedMetric] = useState(null); // index of expanded metric card

  // ── Clinical Projections interactivity ──
  const [expandedGroup, setExpandedGroup] = useState(null);       // Sec 5: expanded patient group card
  const [expandedEndpoint, setExpandedEndpoint] = useState(null); // Sec 6/7: drilled-into endpoint row
  const [visibleDoses, setVisibleDoses] = useState({});           // Sec 6/7/radar: { therapyName: bool }
  const [radarHiddenScenarios, setRadarHiddenScenarios] = useState({}); // Sec 3.4B: { dataKey: bool }

  // ── Use case rename ──
  const [renamingUcId, setRenamingUcId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  // ── Commercial section interactive state ──
  const [expandedCompetitor, setExpandedCompetitor] = useState(null);
  const [revenueView, setRevenueView] = useState("chart");
  const [valView, setValView] = useState("chart");

  // ── Theme-aware class helpers ──
  const themeCard = isDark ? "bg-slate-800/50 border-cyan-500/20" : "bg-white border-slate-300 shadow-sm";
  const themeText = isDark ? "text-slate-300" : "text-slate-700";
  const themeTextMuted = isDark ? "text-slate-400" : "text-slate-500";
  const themeTextBright = isDark ? "text-slate-200" : "text-slate-900";
  const themeBorder = isDark ? "border-slate-700" : "border-slate-300";
  const themeTableRow = (i) => isDark ? (i % 2 === 0 ? "bg-slate-800/30" : "bg-slate-800/60") : (i % 2 === 0 ? "bg-white" : "bg-slate-50");
  const themeTableHead = isDark ? "bg-slate-800/80" : "bg-slate-200";
  const themeExportBtn = isDark
    ? "text-[9px] px-1.5 py-0.5 rounded bg-slate-700/80 text-slate-400 hover:text-slate-200 border border-slate-600/50"
    : "text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 hover:text-slate-900 border border-slate-300";

  // ── Config change tracking ──
  const [originalConfig, setOriginalConfig] = useState(null);
  const [configChanges, setConfigChanges] = useState(null);
  const [affectedSections, setAffectedSections] = useState([]);
  const [isRerun, setIsRerun] = useState(false);
  const [rerunSummary, setRerunSummary] = useState(null);

  // ── Use Case management ──
  const deriveUseCaseId = (name) => (name || "").replace(/[-_\s]+/g, "").toLowerCase();
  const [useCases, setUseCases] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gnq_use_cases") || "[]"); } catch { return []; }
  }); // { id, name, indication, createdAt, lastRunAt }
  const [activeUseCaseId, setActiveUseCaseId] = useState(() => {
    try { return localStorage.getItem("gnq_active_use_case") || null; } catch { return null; }
  });
  const [showUseCaseDropdown, setShowUseCaseDropdown] = useState(false);
  const [showNewUseCaseModal, setShowNewUseCaseModal] = useState(false);
  const [newUseCaseName, setNewUseCaseName] = useState("");
  const [newUseCaseIndication, setNewUseCaseIndication] = useState("");
  const useCaseDropdownRef = useRef(null);

  // ── Document upload state ──
  const [uploadedFiles, setUploadedFiles] = useState(() => { try { return JSON.parse(localStorage.getItem("gnq_documents") || "[]"); } catch { return []; } });
  const [extractedDocs, setExtractedDocs] = useState([]);
  const [documentInsights, setDocumentInsights] = useState([]);
  const [insightDecisions, setInsightDecisions] = useState({});
  const [insightModifications, setInsightModifications] = useState({});
  const [documentAnalysisError, setDocumentAnalysisError] = useState(null);
  const [documentSummary, setDocumentSummary] = useState("");
  const [unmappedFindings, setUnmappedFindings] = useState([]);
  const fileInputRef = useRef(null);
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [docAnalyzing, setDocAnalyzing] = useState(false);

  // ── Onboarding chat state ──
  const [onboardingMessages, setOnboardingMessages] = useState([{ role: "assistant", content: ONBOARDING_OPENING }]);
  const [onboardingStep, setOnboardingStep] = useState("therapy");
  const [shouldAutoStart, setShouldAutoStart] = useState(false);
  // collectedConfig is now an alias for activeConfig (declared above)
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingInput, setOnboardingInput] = useState("");
  const [onboardingFiles, setOnboardingFiles] = useState([]);
  const onboardingFileInputRef = useRef(null);
  const onboardingEndRef = useRef(null);
  const onboardingInputRef = useRef(null);
  const sectionRendererRef = useRef(null);
  useEffect(() => { onboardingEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [onboardingMessages]);
  // Re-focus the chat input whenever the bot finishes responding
  useEffect(() => { if (!onboardingLoading) onboardingInputRef.current?.focus(); }, [onboardingLoading]);

  // ── Pre-loaded reference config (fetched at startup, used as suggestions in onboarding chat) ──
  const [preConfig, setPreConfig] = useState(null);
  const preConfigRef = useRef(null);
  useEffect(() => { preConfigRef.current = preConfig; }, [preConfig]);
  useEffect(() => {
    fetchPreConfig(DEFAULT_THERAPY_ID).then(d => { if (d) { setPreConfig(d); } });
  }, []);
  // preConfig is kept as a reference for the onboarding flow to pull from step-by-step.
  // Do NOT auto-fill collectedConfig here — it contaminates new therapy sessions with stale data.

  // ── Workspace panel sub-section collapse state ──
  const [panelSections, setPanelSections] = useState({ docs: true, configs: true, reports: true });
  const togglePanelSection = (key) => setPanelSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Generated reports (version history) ──
  const [savedConfigs, setSavedConfigs] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("gnq_configs") || "[]");
      return raw.map((sc, i) => {
        // Fix missing useCaseId — derive from therapy name in the config
        if (!sc.useCaseId || sc.useCaseId === "undefined" || sc.useCaseId === "") {
          const therapyName = sc.config?.therapy?.name || sc.name?.split(" Input")[0] || "";
          if (therapyName) sc.useCaseId = therapyName.replace(/[-_\s]+/g, "").toLowerCase();
        }
        // Remove legacy draft flag (Save Draft button was removed)
        if (sc.isDraft) delete sc.isDraft;
        // Only assign a name if completely missing — never overwrite user-renamed entries
        if (!sc.name) {
          const therapyName = sc.config?.therapy?.name || "Config";
          return { ...sc, name: `${therapyName} Input v${i + 1}` };
        }
        return sc;
      });
    } catch { return []; }
  }); // { id, version, name, datetime, config }
  const [editingConfigName, setEditingConfigName] = useState(null); // configId being renamed
  const [configNameValue, setConfigNameValue] = useState("");
  const [editingFileName, setEditingFileName] = useState(null); // file index being renamed
  const [fileNameValue, setFileNameValue] = useState("");
  const [editingReportName, setEditingReportName] = useState(null); // reportId being renamed
  const [reportNameValue, setReportNameValue] = useState("");
  const pendingConfigIdRef = useRef(null); // carries configId from handleRunPipeline → report creation

  const [generatedReports, setGeneratedReports] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("gnq_reports") || "[]");
      const fixed = raw.map((r) => {
        // Fix missing useCaseId
        if (!r.useCaseId || r.useCaseId === "undefined" || r.useCaseId === "") {
          const therapyName = r.name?.split(" Report")[0] || "";
          if (therapyName) r.useCaseId = therapyName.replace(/[-_\s]+/g, "").toLowerCase();
        }
        return r;
      });
      // Only assign names to reports that have no name — never overwrite user-renamed entries
      const counters = {};
      for (const r of fixed) {
        if (!r.name) {
          const base = "Assessment";
          counters[base] = (counters[base] || 0) + 1;
          r.name = `${base} Report V${counters[base]}`;
        }
      }
      return fixed;
    } catch { return []; }
  });
  useEffect(() => {
    if (flowStep === "complete" && assessmentData && !reportSavedForRunRef.current) {
      reportSavedForRunRef.current = true;
      const name = therapyConfig?.therapy?.name || "Assessment";
      const reportId = `${name}-${Date.now()}`;
      setGeneratedReports(prev => {
        const next = [...prev, {
          id: reportId,
          name: `${name} Report V${(Math.max(0, ...prev.filter(r => r.name?.startsWith(name)).map(r => parseInt(r.name?.match(/V(\d+)/)?.[1] || "0"))) + 1)}`,
          datetime: new Date().toISOString(),
          configId: pendingConfigIdRef.current,
          useCaseId: activeUseCaseId || deriveUseCaseId(name),
        }];
        return next; // no auto-deletion — user manages entries manually via X button
      });
      // Save assessmentData for this report so it can be re-displayed later.
      // This is the largest and fastest-growing entry in localStorage — one
      // full payload per run — so safeSetItem evicts older ones to make room.
      safeSetItem(`gnq_report_data_${reportId}`, JSON.stringify(assessmentData));
    }
  }, [flowStep, assessmentData]);

  // ── Persist Workspace data to localStorage ──
  // All writes go through safeSetItem: these run inside passive effects, so an
  // unguarded QuotaExceededError here unmounts the whole app rather than
  // failing the one write. See storage.js.
  useEffect(() => { safeSetItem("gnq_configs", JSON.stringify(savedConfigs)); }, [savedConfigs]);
  useEffect(() => { safeSetItem("gnq_reports", JSON.stringify(generatedReports)); }, [generatedReports]);
  // Files: persist metadata + extracted content (File objects aren't serializable, but extracted text is)
  useEffect(() => {
    safeSetItem("gnq_documents", JSON.stringify(
      uploadedFiles.map(f => ({
        name: f.name, size: f.size || 0, type: f.type || "", file_type: f.file_type || "",
        datetime: f.datetime || "", useCaseId: f.useCaseId || "",
        extracted_content: f.extracted_content || null,
      }))
    ));
  }, [uploadedFiles]);

  // ── Persist Use Case data ──
  useEffect(() => { safeSetItem("gnq_use_cases", JSON.stringify(useCases)); }, [useCases]);
  useEffect(() => { if (activeUseCaseId) safeSetItem("gnq_active_use_case", activeUseCaseId); }, [activeUseCaseId]);

  // ── Sync chat messages with use case store ──
  const chatSyncRef = useRef(false);

  // Save onboardingMessages to store when they change (ongoing sync)
  useEffect(() => {
    if (!activeUseCaseId || !chatSyncRef.current) return;
    ucStore.setChatHistory(onboardingMessages);
  }, [onboardingMessages]); // eslint-disable-line

  // Save activeConfig to ucStore when it changes
  useEffect(() => {
    if (!activeUseCaseId || !chatSyncRef.current) return;
    if (activeConfig && Object.keys(activeConfig).length > 0) {
      ucStore.setConfirmedConfig(activeConfig);
    }
  }, [activeConfig]); // eslint-disable-line

  // Helper: get the best available config for a use case
  const getBestConfig = useCallback((ucId) => {
    console.log(`[GNQ] getBestConfig(${ucId}): searching...`);
    // 1. Try ucStore (confirmed + extracted) from localStorage directly
    try {
      const raw = localStorage.getItem(`gnq_uc_state_${ucId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        const confirmed = parsed.confirmedConfig || {};
        const extracted = parsed.extractedConfig || {};
        const merged = { ...extracted, ...confirmed };
        if (Object.keys(merged).length > 0 && merged.therapy?.name) {
          console.log(`[GNQ] getBestConfig(${ucId}): found in ucStore (${Object.keys(merged).length} keys)`);
          return merged;
        }
      }
    } catch { /* ignore */ }

    // 2. Fallback: read savedConfigs directly from localStorage (avoids React state timing)
    let allConfigs = [];
    try {
      allConfigs = JSON.parse(localStorage.getItem("gnq_configs") || "[]");
    } catch { /* ignore */ }
    console.log(`[GNQ] getBestConfig(${ucId}): checking ${allConfigs.length} saved configs...`);

    // 2a. Match by useCaseId
    const byUcId = allConfigs.filter(sc => sc.useCaseId === ucId);
    if (byUcId.length > 0) {
      const latest = [...byUcId].sort((a, b) => new Date(b.datetime) - new Date(a.datetime))[0];
      if (latest?.config && Object.keys(latest.config).length > 0) {
        console.log(`[GNQ] getBestConfig(${ucId}): found via useCaseId (${latest.name})`);
        return latest.config;
      }
    }

    // 2b. Match by therapy name (for older configs without useCaseId)
    const byName = allConfigs.filter(sc => {
      const therapyName = sc.config?.therapy?.name;
      return therapyName && deriveUseCaseId(therapyName) === ucId;
    });
    console.log(`[GNQ] getBestConfig(${ucId}): ${byName.length} matched by therapy name`);
    if (byName.length > 0) {
      const latest = [...byName].sort((a, b) => new Date(b.datetime) - new Date(a.datetime))[0];
      if (latest?.config && Object.keys(latest.config).length > 0) {
        console.log(`[GNQ] getBestConfig(${ucId}): found via name match (${latest.name})`);
        return latest.config;
      }
    }

    console.log(`[GNQ] getBestConfig(${ucId}): no saved config found, falling back to metadata`);
    // 3. Fallback: use case metadata (name + indication)
    const uc = useCases.find(u => u.id === ucId);
    if (uc?.name || uc?.indication) {
      const preFill = {};
      if (uc.name) preFill.therapy = { name: uc.name };
      if (uc.indication) preFill.indication = { disease: uc.indication };
      return preFill;
    }
    return {};
  }, [useCases]);

  // Helper: set onboarding step based on config completeness
  const determineOnboardingStep = (config) => {
    if (!config || Object.keys(config).length === 0) return { step: "therapy", sections: ["therapy"] };
    const has = (key) => {
      const val = config[key];
      if (!val) return false;
      if (typeof val === "object" && !Array.isArray(val) && Object.values(val).every(v => !v || v === "")) return false;
      return true;
    };
    if (has("commercial_assumptions")) return { step: "confirm", sections: ["therapy", "indication", "endpoints", "doses", "competitive", "commercial"] };
    if (has("competitive_landscape")) return { step: "commercial", sections: ["therapy", "indication", "endpoints", "doses", "competitive", "commercial"] };
    if (has("dose_config") || has("dose_scenarios")) return { step: "competitive", sections: ["therapy", "indication", "endpoints", "doses", "competitive"] };
    if (has("clinical_endpoints")) return { step: "doses", sections: ["therapy", "indication", "endpoints", "doses"] };
    if (has("indication")) return { step: "endpoints", sections: ["therapy", "indication", "endpoints"] };
    if (has("therapy")) return { step: "indication", sections: ["therapy", "indication"] };
    return { step: "therapy", sections: ["therapy"] };
  };

  // First mount only: restore chat + config from store, or set context-aware welcome
  useEffect(() => {
    if (chatSyncRef.current) return; // already initialized
    if (activeUseCaseId) {
      const storedChat = ucStore.chatHistory;
      const config = getBestConfig(activeUseCaseId);
      const hasConfig = Object.keys(config).length > 0;

      // Restore config first (so welcome message knows if it loaded)
      if (hasConfig) {
        setCollectedConfig(config);
        setPreConfig(config);
        ucStore.setConfirmedConfig(config); // sync back to store
        const { step, sections } = determineOnboardingStep(config);
        setOnboardingStep(step);
        setExpandedConfigSections(sections);
      }

      // Restore chat
      if (storedChat && storedChat.length > 0) {
        setOnboardingMessages(storedChat);
      } else {
        setOnboardingMessages([{ role: "assistant", content: getWelcomeMessage(activeUseCaseId, hasConfig) }]);
      }
    }
    chatSyncRef.current = true;
  }, []); // eslint-disable-line

  // ── Migrate existing data: derive useCaseId from therapy name, auto-create use cases ──
  useEffect(() => {
    const migrated = localStorage.getItem("gnq_use_cases_migrated");
    if (migrated) return;

    const ucMap = {};
    // Derive use cases from existing saved configs
    savedConfigs.forEach(sc => {
      const therapyName = sc.config?.therapy?.name;
      if (!therapyName) return;
      const ucId = deriveUseCaseId(therapyName);
      if (!ucMap[ucId]) {
        ucMap[ucId] = {
          id: ucId,
          name: therapyName,
          indication: sc.config?.indication?.disease || "",
          createdAt: sc.datetime || new Date().toISOString(),
          lastRunAt: sc.datetime || new Date().toISOString(),
        };
      }
    });

    const derivedUseCases = Object.values(ucMap);
    if (derivedUseCases.length > 0) {
      setUseCases(derivedUseCases);
      // Backfill useCaseId onto existing configs
      setSavedConfigs(prev => prev.map(sc => {
        if (sc.useCaseId) return sc;
        const name = sc.config?.therapy?.name;
        return name ? { ...sc, useCaseId: deriveUseCaseId(name) } : sc;
      }));
      // Backfill useCaseId onto existing reports
      setGeneratedReports(prev => prev.map(r => {
        if (r.useCaseId) return r;
        const linked = savedConfigs.find(sc => sc.id === r.configId);
        const name = linked?.config?.therapy?.name;
        return name ? { ...r, useCaseId: deriveUseCaseId(name) } : r;
      }));
      // Auto-select the most recent use case
      if (!activeUseCaseId) {
        const sorted = derivedUseCases.sort((a, b) => new Date(b.lastRunAt) - new Date(a.lastRunAt));
        setActiveUseCaseId(sorted[0].id);
      }
    }
    safeSetItem("gnq_use_cases_migrated", "1");
  }, []); // migration runs once on mount

  // ── Close use case dropdown on outside click ──
  useEffect(() => {
    const handler = (e) => {
      if (useCaseDropdownRef.current && !useCaseDropdownRef.current.contains(e.target)) {
        setShowUseCaseDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Use Case State Store (confirmed/extracted split + chat persistence) ──
  const ucStore = useUseCaseStore(activeUseCaseId);

  // ── Switch use case: atomic workspace swap ──
  const requestSwitchUseCase = useCallback((newId) => {
    if (newId === activeUseCaseId) return;
    // If pipeline is running, show confirmation
    if (flowStep === "pipeline") {
      setPendingSwitchId(newId);
      return;
    }
    // Otherwise switch immediately
    executeSwitchUseCase(newId);
  }, [activeUseCaseId, flowStep]); // eslint-disable-line

  const executeSwitchUseCase = useCallback((newId) => {
    setPendingSwitchId(null);
    console.log(`[GNQ] Switching use case: ${activeUseCaseId} → ${newId}`);

    // 1. Save FULL current workspace to localStorage
    if (activeUseCaseId) {
      try {
        const key = `gnq_uc_state_${activeUseCaseId}`;
        const existing = JSON.parse(localStorage.getItem(key) || "{}");
        const updated = {
          ...existing,
          chatHistory: onboardingMessages,
          confirmedConfig: activeConfig,
          flowStep: flowStep,
          activeSection: activeSection,
          readySections: [...(readySections || [])],
          lastUpdated: new Date().toISOString(),
        };
        safeSetItem(key, JSON.stringify(updated));
        console.log(`[GNQ] Saved outgoing use case ${activeUseCaseId}: flow=${flowStep}, ${onboardingMessages.length} messages, ${Object.keys(activeConfig).length} config keys`);
      } catch (e) {
        console.warn("[GNQ] Failed to save outgoing use case:", e);
      }
    }

    // 2. Read incoming use case state from localStorage
    let incoming = {};
    try {
      const raw = localStorage.getItem(`gnq_uc_state_${newId}`);
      if (raw) incoming = JSON.parse(raw);
    } catch { /* ignore */ }

    const incomingChat = incoming.chatHistory || [];
    const incomingFlowStep = incoming.flowStep || "query";
    const incomingActiveSection = incoming.activeSection || "executive";
    const incomingReadySections = new Set(incoming.readySections || []);

    // 3. Get best available config
    const incomingConfig = getBestConfig(newId);
    const hasConfig = Object.keys(incomingConfig).length > 0;
    console.log(`[GNQ] Loaded incoming use case ${newId}: flow=${incomingFlowStep}, ${incomingChat.length} msgs, config=${hasConfig}`);

    // 4. Restore workspace
    chatSyncRef.current = false;

    // Chat history
    if (incomingChat.length > 0) {
      setOnboardingMessages(incomingChat);
    } else {
      setOnboardingMessages([{ role: "assistant", content: getWelcomeMessage(newId, hasConfig) }]);
    }

    // Config — single source of truth
    if (hasConfig) {
      setActiveConfig(incomingConfig);
      setPreConfig(incomingConfig);
      const { step, sections } = determineOnboardingStep(incomingConfig);
      setOnboardingStep(step);
      setExpandedConfigSections(sections);
    } else {
      setActiveConfig({});
      setPreConfig(null);
      setOnboardingStep("therapy");
      setExpandedConfigSections(["therapy"]);
    }
    setActiveSection(incomingActiveSection);
    setReadySections(incomingReadySections);
    setAgentStatuses({});
    setGeneratingSections(new Set());
    setPipelineError(null);

    // If the incoming use case had a completed report, try to load assessmentData
    if (incomingFlowStep === "complete") {
      // Find the most recent report for this use case
      const ucReports = generatedReports.filter(r => r.useCaseId === newId).sort((a, b) => (b.datetime || "").localeCompare(a.datetime || ""));
      if (ucReports.length > 0) {
        try {
          const reportData = localStorage.getItem(`gnq_report_data_${ucReports[0].id}`);
          if (reportData) {
            setAssessmentData(JSON.parse(reportData));
            reportSavedForRunRef.current = true;
          } else {
            setAssessmentData(null);
          }
        } catch { setAssessmentData(null); }
      } else {
        setAssessmentData(null);
      }
    } else {
      setAssessmentData(null);
    }

    setFlowStep(incomingFlowStep === "pipeline" ? "review" : incomingFlowStep); // don't resume a running pipeline
    setActiveUseCaseId(newId);
    setTimeout(() => { chatSyncRef.current = true; }, 300);
  }, [activeUseCaseId, onboardingMessages, collectedConfig, flowStep, therapyConfig, activeSection, readySections, generatedReports]); // eslint-disable-line

  // Legacy alias
  const switchUseCase = requestSwitchUseCase;

  // ── Use Case helpers ──
  const createUseCase = useCallback((name, indication) => {
    const id = deriveUseCaseId(name);
    const existing = useCases.find(uc => uc.id === id);
    if (existing) { switchUseCase(id); return id; }
    const uc = { id, name, indication, createdAt: new Date().toISOString(), lastRunAt: new Date().toISOString() };
    setUseCases(prev => [...prev, uc]);
    setActiveUseCaseId(id); // new use case — no prior state to restore
    return id;
  }, [useCases, switchUseCase]);

  const activeUseCase = useCases.find(uc => uc.id === activeUseCaseId) || null;

  const commitRename = useCallback((ucId) => {
    if (!renameValue.trim()) { setRenamingUcId(null); return; }
    const updated = useCases.map(uc => uc.id === ucId ? { ...uc, name: renameValue.trim() } : uc);
    setUseCases(updated);
    safeSetItem("gnq_use_cases", JSON.stringify(updated));
    setRenamingUcId(null);
  }, [renameValue, useCases]);

  const clearChatHistory = useCallback((ucId) => {
    const key = `gnq_uc_state_${ucId}`;
    const stored = JSON.parse(localStorage.getItem(key) || "{}");
    stored.chatHistory = [];
    stored.flowStep = "query";
    stored.confirmedConfig = null;
    stored.readySections = [];
    safeSetItem(key, JSON.stringify(stored));
    if (ucId === activeUseCaseId) {
      setOnboardingMessages([{ role: "assistant", content: ONBOARDING_OPENING }]);
      setFlowStep("query");
      setActiveConfig({});
      setReadySections(new Set());
    }
  }, [activeUseCaseId]);

  // Filtered collection for active use case — sorted newest first
  const _parseDate = (d) => { if (!d) return 0; const t = new Date(d).getTime(); return isNaN(t) ? 0 : t; };
  const _sortNewest = (a, b) => _parseDate(b.datetime) - _parseDate(a.datetime);
  const filteredConfigs = (activeUseCaseId ? savedConfigs.filter(sc => sc.useCaseId === activeUseCaseId) : savedConfigs).slice().sort(_sortNewest);
  const filteredReports = (activeUseCaseId ? generatedReports.filter(r => r.useCaseId === activeUseCaseId) : generatedReports).slice().sort(_sortNewest);
  const filteredFiles = (activeUseCaseId ? uploadedFiles.filter(f => f.useCaseId === activeUseCaseId) : uploadedFiles).slice().sort(_sortNewest);

  // ── Context-aware welcome message builder ──
  const getWelcomeMessage = useCallback((ucId, configLoaded = false) => {
    const id = ucId || activeUseCaseId;
    const uc = id ? useCases.find(u => u.id === id) : activeUseCase;
    // Match configs by useCaseId OR by therapy name
    let configs = savedConfigs.filter(sc => sc.useCaseId === id);
    if (configs.length === 0 && uc?.name) {
      configs = savedConfigs.filter(sc => sc.config?.therapy?.name && deriveUseCaseId(sc.config.therapy.name) === id);
    }
    let reports = generatedReports.filter(r => r.useCaseId === id);
    if (reports.length === 0 && uc?.name) {
      reports = generatedReports.filter(r => {
        const linked = savedConfigs.find(sc => sc.id === r.configId);
        return linked?.config?.therapy?.name && deriveUseCaseId(linked.config.therapy.name) === id;
      });
    }
    const files = uploadedFiles.filter(f => f.useCaseId === id);
    const latestReport = reports.length > 0 ? [...reports].sort((a, b) => new Date(b.datetime) - new Date(a.datetime))[0] : null;
    // Get partial config from localStorage for this use case (even if not fully loaded)
    let partial = null;
    try {
      const raw = localStorage.getItem(`gnq_uc_state_${id}`);
      if (raw) partial = JSON.parse(raw).confirmedConfig || null;
    } catch { /* ignore */ }
    return buildWelcomeMessage({
      useCase: uc,
      hasDocuments: files.length > 0,
      hasSavedConfigs: configs.length > 0,
      hasReports: reports.length > 0,
      configLoaded,
      latestReportName: latestReport?.name,
      partialConfig: partial,
    });
  }, [activeUseCase, useCases, savedConfigs, generatedReports, uploadedFiles, activeUseCaseId]);

  const [isDragOver, setIsDragOver] = useState(false);

  // ── Auto-run pipeline when coming from onboarding (skip manual click) ──
  // Use a ref (not state) so React Strict Mode double-invoke doesn't cancel the timer
  const autoRunRef = useRef(false);
  // Incremented on every reset — lets startConfigPrep and sendOnboardingMessage discard stale async results
  const resetGenRef = useRef(0);
  // Tracks previous flowStep value to distinguish initial mount from actual transitions
  const prevFlowStepRef = useRef(null);
  // Tracks whether a report has been saved for the current assessment run (prevents duplicates)
  const reportSavedForRunRef = useRef(false);
  // Tracks whether we've already auto-navigated to the first ready section this run.
  // Avoids stale-closure issues (reading readySections inside the SSE callback).
  const hasAutoNavigatedRef = useRef(false);
  const currentSessionIdRef = useRef(null); // tracks active run's session_id for cancel calls
  const abortRunRef = useRef(null);          // stores AbortController.abort() for the active SSE fetch
  const runGenRef = useRef(0);              // increments on each new run; old AbortError catch checks this to avoid hijacking flowStep
  // Both start collapsed during review; Chat auto-opens when report is ready
  const [rightSectionCollapsed, setRightSectionCollapsed] = useState({ workspace: true, chat: true });
  const [chatBarExpanded, setChatBarExpanded] = useState(false);
  useEffect(() => {
    if (flowStep === "complete") {
      setRightSectionCollapsed({ workspace: true, chat: false }); // open Chat when report lands
    }
    if (flowStep === "query") {
      // Don't overwrite if switchUseCase already restored (chatSyncRef is false during switch)
      // Also skip on initial mount — prevFlowStepRef is null until after first render
      if (chatSyncRef.current && prevFlowStepRef.current !== null) {
        setOnboardingMessages([{ role: "assistant", content: getWelcomeMessage() }]);
        setCollectedConfig({});
        setOnboardingStep("therapy");
        setExpandedConfigSections(["therapy"]);
      }
      setShouldAutoStart(false);
    }
    prevFlowStepRef.current = flowStep;
  }, [flowStep]);
  useEffect(() => {
    if (flowStep === "review" && autoRunRef.current) {
      autoRunRef.current = false;
      const t = setTimeout(() => handleRunPipeline(), 500);
      return () => clearTimeout(t);
    }
  }, [flowStep]);

  // Auto-start assessment when chat bot sends next_step:"done".
  // useEffect runs after render so collectedConfig is fully updated before handleConfirmAndStart reads it.
  useEffect(() => {
    if (shouldAutoStart && flowStep === "query") {
      setShouldAutoStart(false);
      handleConfirmAndStart();
    }
  }, [shouldAutoStart]);

  const chatEndRef = useRef(null);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  // ── Step navigation ──
  // All possible steps in order (used for stepper display and nav)
  const allSteps = ["query", "configPrep", "documentInsights", "review", "pipeline", "complete"];
  // Steps the user can navigate to (skip transient animation steps)
  const navSteps = ["query", "review", "complete"];
  useEffect(() => {
    const idx = allSteps.indexOf(flowStep);
    if (idx > maxReachedStep) setMaxReachedStep(idx);
  }, [flowStep]);

  const goBack = () => {
    const curNav = navSteps.indexOf(flowStep);
    if (curNav > 0) setFlowStep(navSteps[curNav - 1]);
    else if (curNav === -1) {
      // On a transient step (configPrep, pipeline, documentInsights) — go to previous navigable
      const curAll = allSteps.indexOf(flowStep);
      for (let i = curAll - 1; i >= 0; i--) {
        if (navSteps.includes(allSteps[i])) { setFlowStep(allSteps[i]); return; }
      }
    }
  };
  const goForward = () => {
    const curNav = navSteps.indexOf(flowStep);
    if (curNav === -1) return;
    const nextNav = navSteps[curNav + 1];
    if (nextNav && allSteps.indexOf(nextNav) <= maxReachedStep) setFlowStep(nextNav);
  };
  const canGoBack = flowStep !== "query";
  const canGoForward = (() => {
    const curNav = navSteps.indexOf(flowStep);
    if (curNav === -1) return false;
    const nextNav = navSteps[curNav + 1];
    return nextNav && allSteps.indexOf(nextNav) <= maxReachedStep;
  })();

  // ── Seed YAML (simplified) ──
  const createSeedYAML = (query, drug, indication, comparator) => ({
    meta: { id: `${drug}-${Date.now()}`, status: "processing", engine_version: "Causal Engine v2" },
    query: { original_input: query, drug, indication, comparator }
  });

  // ── Zip config import handler (shared by both import buttons) ──
  const handleImportConfig = async (file) => {
    if (!file) return;
    setImportLoading(true);
    setImportError(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const resp = await fetch("/api/import-config", { method: "POST", body: formData });
      if (!resp.ok) {
        // Surface the backend error message to the user
        let msg = `Import failed (HTTP ${resp.status})`;
        try { const e = await resp.json(); msg = e.error || msg; } catch { /* ignore */ }
        setImportError(msg);
        return;
      }
      const data = await resp.json();
      if (data.error) {
        setImportError(data.error);
        return;
      }
      if (!data.therapy_config || Object.keys(data.therapy_config).length === 0) {
        setImportError("The zip file did not contain a valid therapy_config.json.");
        return;
      }
      // Load imported config and navigate to review
      setTherapyConfig(data.therapy_config);
      setCollectedConfig(data.therapy_config);
      setFlowStep("review");
      setImportError(null);
      console.log("[GNQ] Imported config for:", data.therapy_id, "refs:", data.imported_references);
    } catch (err) {
      setImportError(`Import failed: ${err.message}`);
      console.error("[GNQ] Import config failed:", err);
    } finally {
      setImportLoading(false);
    }
  };

  // ── File upload handlers ──
  const addFiles = async (files) => {
    const validExts = ["pdf", "docx", "doc", "png", "jpg", "jpeg", "gif", "webp"];
    const maxSize = 20 * 1024 * 1024;
    const now = new Date().toISOString();
    const ucId = activeUseCaseId || "";
    const valid = files.filter(f => {
      const ext = f.name.split(".").pop().toLowerCase();
      return validExts.includes(ext) && f.size <= maxSize;
    });
    if (valid.length === 0) return;

    // Add files immediately with extracting=true placeholder
    const placeholders = valid.map(f => ({
      name: f.name, size: f.size, type: f.type, datetime: now, useCaseId: ucId,
      extracted_content: null, extracting: true,
    }));
    setUploadedFiles(prev => {
      const newNames = new Set(valid.map(f => f.name));
      return [...prev.filter(f => !newNames.has(f.name)), ...placeholders];
    });

    // Extract content in background
    try {
      const formData = new FormData();
      valid.forEach(f => formData.append("files", f));
      const res = await fetch("/api/upload-files", { method: "POST", body: formData });
      if (res.ok) {
        const data = await res.json();
        const extracted = data.files || [];
        // Update uploadedFiles with extracted content
        setUploadedFiles(prev => prev.map(f => {
          const match = extracted.find(e => e.filename === f.name);
          if (match && !match.error) {
            return { ...f, extracted_content: match.extracted_content, file_type: match.file_type, extracting: false };
          } else if (match && match.error) {
            return { ...f, extracted_content: null, extract_error: match.error, extracting: false };
          }
          return f;
        }));
        // Also update extractedDocs for backward compatibility
        const good = extracted.filter(e => !e.error && e.extracted_content);
        if (good.length > 0) setExtractedDocs(prev => {
          const existing = new Set(prev.map(d => d.filename));
          return [...prev, ...good.filter(g => !existing.has(g.filename))];
        });
        console.log(`[GNQ] Extracted ${extracted.length} files on upload: ${extracted.map(e => `${e.filename}(${(e.extracted_content||'').length}ch)`).join(', ')}`);
      }
    } catch (e) {
      console.warn("[GNQ] File extraction on upload failed:", e);
      // Mark as failed
      setUploadedFiles(prev => prev.map(f => placeholders.find(p => p.name === f.name) ? { ...f, extracting: false, extract_error: String(e) } : f));
    }
  };
  const handleFileDrop = (e) => { e.preventDefault(); e.currentTarget.classList.remove("border-cyan-400"); addFiles(Array.from(e.dataTransfer.files)); };
  const handleFileSelect = (e) => { addFiles(Array.from(e.target.files)); e.target.value = ""; };
  const removeFile = (file) => setUploadedFiles(prev => prev.filter(f => f !== file));

  // ── Query submit handler ──
  const handleQuerySubmit = () => {
    if (!userInput.trim()) return;
    setQueryData({ originalQuery: userInput });
    setFlowStep("configPrep");
    startConfigPrep();
  };

  // ── Config preparation (simulates 4 pre-config agents) ──
  const startConfigPrep = () => {
    const gen = resetGenRef.current; // snapshot — if reset fires before timeout, gen will differ
    const agents = [
      { key: "query_parsing", delay: 1500 },
      { key: "endpoint_discovery", delay: 3000 },
      { key: "competitive_scan", delay: 5000 },
      { key: "commercial_params", delay: 7000 }
    ];
    setConfigPrepStatuses({});
    agents.forEach(({ key, delay }) => {
      setTimeout(() => { if (resetGenRef.current !== gen) return; setConfigPrepStatuses(prev => ({ ...prev, [key]: "processing" })); }, delay - 800);
      setTimeout(() => { if (resetGenRef.current !== gen) return; setConfigPrepStatuses(prev => ({ ...prev, [key]: "complete" })); }, delay);
    });
    // Load therapy config after all agents complete → go to review step
    setTimeout(() => {
      if (resetGenRef.current !== gen) return; // reset fired — discard
      // Try to load pre-config matching the therapy name from chatbot, fall back to default
      const therapyName = collectedConfig?.therapy?.name || queryData?.originalQuery || "";
      const configId = therapyName ? therapyName.replace(/[-_\s]+/g, "").toLowerCase() : DEFAULT_THERAPY_ID;
      fetchPreConfig(configId).then(data => {
          if (resetGenRef.current !== gen) return;
          if (!data) { setFlowStep("review"); return; } // no pre-config — chatbot-built config is enough
          if (!data.dose_config) {
            const components = data.therapy?.components || [];
            const emptyDoses = {};
            components.forEach(c => { emptyDoses[c] = ""; });
            data.dose_config = {
              therapy_components: components,
              therapy_dose_levels: [{ route: "", component_doses: emptyDoses }],
              comparators: (data.therapy?.comparators || []).map(c => ({ name: c.name, route: "", doses: [""] }))
            };
          }
          setTherapyConfig(prev => prev || data);
          setFlowStep("review");
        }).catch(err => { console.error("[GNQ] Failed to load config:", err); setFlowStep("review"); });
    }, 7000);
  };

  // ── Run pipeline handler (called from Review) ──
  const handleRunPipeline = (sectionsOverride = null, resumeSessionId = null) => {
    // Reset report-saved flag so a new report entry will be created when this run completes
    reportSavedForRunRef.current = false;
    // Stop any in-progress run before starting a new one
    if (abortRunRef.current) abortRunRef.current();
    if (currentSessionIdRef.current) {
      fetch(`/api/cancel-run/${encodeURIComponent(currentSessionIdRef.current)}`, { method: "POST" })
        .catch(e => console.warn("[GNQ] Cancel request failed:", e));
    }

    // Single config state — no merging needed
    const effectiveConfig = activeConfig;

    // If re-running, compute diff and affected sections
    let pendingSummary = null;
    console.log(`[GNQ] handleRunPipeline: isRerun=${isRerun}, hasOriginalConfig=${!!originalConfig}, effectiveConfigKeys=${Object.keys(effectiveConfig || {})}`);
    if (isRerun && originalConfig) {
      const changes = computeConfigDiff(originalConfig, effectiveConfig);
      const affected = deriveAffectedSections(changes);
      console.log(`[GNQ] Diff result: ${changes.length} changes, ${affected.length} affected sections`, changes.map(c => c.summary));
      setConfigChanges(changes);
      setAffectedSections(affected);
      if (changes.length > 0) {
        pendingSummary = {
          changedCategories: [...new Set(changes.map(c => c.category))],
          updatedSections: affected,
          changes,
          timestamp: new Date().toLocaleTimeString(),
        };
      }
    }
    // Snapshot current config — dedup: if identical to last saved config for this therapy, reuse it
    const therapyName = effectiveConfig?.therapy?.name || "Config";
    const configSnapshot = JSON.stringify(effectiveConfig);
    // Auto-create use case if none active
    const ucId = activeUseCaseId || createUseCase(therapyName, effectiveConfig?.indication?.disease || "");
    // Update use case lastRunAt
    setUseCases(prev => prev.map(uc => uc.id === ucId ? { ...uc, lastRunAt: new Date().toISOString() } : uc));
    const existingMatch = (() => {
      // Find last saved config for this therapy with identical content
      const candidates = [];
      // We need to check against current savedConfigs — use a ref-style read via callback
      return null; // will check inside setSavedConfigs
    })();
    const configId = `cfg-${Date.now()}`;
    pendingConfigIdRef.current = configId;
    setSavedConfigs(prev => {
      // Check if any existing config has identical content
      const duplicate = prev.find(sc => JSON.stringify(sc.config) === configSnapshot);
      if (duplicate) {
        // Reuse existing config — just update timestamp
        pendingConfigIdRef.current = duplicate.id;
        return prev.map(sc => sc.id === duplicate.id ? { ...sc, datetime: new Date().toISOString() } : sc);
      }
      const next = [...prev, {
        id: configId,
        version: prev.length + 1,
        name: `${therapyName} Input v${(Math.max(0, ...prev.filter(sc => sc.name?.startsWith(therapyName)).map(sc => parseInt(sc.name?.match(/v(\d+)/)?.[1] || "0"))) + 1)}`,
        datetime: new Date().toISOString(),
        config: JSON.parse(configSnapshot),
        useCaseId: ucId,
      }];
      return next; // no auto-deletion — user manages entries manually via X button
    });

    // Build competitor_selection from threat_level: high → primary (deep analysis), medium → secondary (summary), low → excluded
    const competitors = effectiveConfig?.competitive_landscape?.competitors || [];
    const comparatorName = effectiveConfig?.therapy?.comparators?.[0]?.name || "";
    const primaryComps = competitors.filter(c => c.threat_level === "high" && c.name !== comparatorName).map(c => c.name).slice(0, 3);
    const secondaryComps = competitors.filter(c => c.threat_level === "medium" && c.name !== comparatorName).map(c => c.name).slice(0, 5);
    const pipelineNames = (effectiveConfig?.competitive_landscape?.pipeline_threats || []).map(p => p.name);
    // Build competitor_selection as a transient object for the API call only — do NOT persist into therapyConfig state
    const competitorSelection = { primary: primaryComps, secondary: secondaryComps, pipeline: pipelineNames };
    // Sync therapy.comparators from dose_config.comparators so the backend gets the correct dose/route/frequency
    const doseConfigComps = effectiveConfig?.dose_config?.comparators || [];
    const syncedTherapyComps = doseConfigComps.length > 0
      ? doseConfigComps.map(c => ({ name: c.name, dose: (c.doses || []).join(", "), route: c.route || "", frequency: c.frequency || "", duration: c.duration || "" }))
      : effectiveConfig?.therapy?.comparators || [];
    const configForPipeline = { ...effectiveConfig, competitor_selection: competitorSelection, therapy: { ...effectiveConfig?.therapy, comparators: syncedTherapyComps } };

    setFlowStep("pipeline");
    // Insert phase divider into chat history
    setOnboardingMessages(prev => [...prev, { role: "system", type: "divider", content: `Assessment Started — ${new Date().toLocaleDateString([], { month: "short", day: "numeric" })}`, timestamp: new Date().toISOString() }]);
    const seed = createSeedYAML(
      queryData.originalQuery,
      effectiveConfig?.therapy?.name || "Therapy",
      effectiveConfig?.indication?.disease || "Disease",
      effectiveConfig?.therapy?.comparators?.[0]?.name || "Comparator"
    );
    runAgentPipeline(seed, pendingSummary, sectionsOverride, configForPipeline, resumeSessionId);
  };

  // ── Stop running pipeline ──
  const handleStopPipeline = () => {
    const sessionId = currentSessionIdRef.current;
    // Fire both simultaneously — do NOT await the cancel fetch.
    // Awaiting caused cleanup_cancel (in event_gen's finally) to run before
    // set_cancel, wiping the flag before the section generator could read it.
    if (abortRunRef.current) abortRunRef.current();
    if (sessionId) {
      fetch(`/api/cancel-run/${encodeURIComponent(sessionId)}`, { method: "POST" })
        .catch(e => console.warn("[GNQ] Cancel request failed:", e));
    }
  };

  // ── Agent pipeline (report generation) ──
  const runAgentPipeline = async (seed, pendingSummary = null, sectionsOverride = null, configOverride = null, resumeSessionId = null) => {
    const myGen = ++runGenRef.current; // tag this run; old AbortErrors check this before setting "stopped"
    const isResume = !!(resumeSessionId && sectionsOverride);
    setAgentStatuses(isResume
      ? { query_builder: "complete", kg_api: "complete", tissue_mapping: "complete", dose_scenario_generator: "complete", dose_translator: "complete", causal_scoring: "complete", section_generator: "processing" }
      : { query_builder: "processing" }); // optimistic — agent completes before first poll
    setFlashingSections(new Set());
    setGeneratingSections(new Set());
    setPipelineError(null);  // clear any previous error
    if (sectionsOverride) {
      // Partial run (Complete Run): keep existing completed sections and their nav dots.
      // Only un-ready the specific sections being re-generated so they animate correctly.
      const navIds = sectionsOverride.map(s => SECTION_NAV_MAP[s]).filter(Boolean);
      setReadySections(prev => { const next = new Set(prev); navIds.forEach(id => next.delete(id)); return next; });
      // Do NOT wipe assessmentData — existing sections stay visible while missing ones generate
    } else {
      // Full run: clear everything
      setReadySections(new Set());
      setAssessmentData(null);
      setActiveSection(null);
    }
    currentSessionIdRef.current = null;
    abortRunRef.current = null;
    hasAutoNavigatedRef.current = false;

    const failWithError = (msg) => {
      console.error("[GNQ] Pipeline error:", msg);
      setPipelineError(msg);
      setFlowStep("review"); // return to config view so user can see error + retry
    };

    const abortController = new AbortController();
    abortRunRef.current = () => abortController.abort();

    try {
      // Include uploaded documents with extracted text for sec4 study extraction
      const docsForSec4 = uploadedFiles
        .filter(f => f.extracted_content && !f.extract_error)
        .map(f => ({ filename: f.name, text: f.extracted_content }));

      let startRes;
      if (isResume) {
        // Try resume endpoint first — skips pipeline, generates only missing sections
        startRes = await fetch("/api/resume-sections", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            prior_session_id: resumeSessionId,
            sections_to_update: sectionsOverride,
            uploaded_documents: docsForSec4.length > 0 ? docsForSec4 : null,
          }),
          signal: abortController.signal,
        });
        if (startRes.status === 404) {
          // No cached state — fall back to full pipeline
          console.warn("[GNQ] Resume not available, falling back to full pipeline");
          startRes = null;
        }
      }

      if (!startRes) {
        // Full pipeline (normal run or resume fallback)
        setAgentStatuses({ query_builder: "processing" });
        startRes = await fetch("/api/start-report", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            therapy_config: configOverride || therapyConfig,
            sections_to_update: sectionsOverride ?? (isRerun && affectedSections.length > 0 ? affectedSections : null),
            uploaded_documents: docsForSec4.length > 0 ? docsForSec4 : null,
          }),
          signal: abortController.signal,
        });
      }

      if (!startRes.ok) {
        const errText = await startRes.text().catch(() => "");
        return failWithError(`Server error ${startRes.status}${errText ? `: ${errText.slice(0, 200)}` : ""}`);
      }

      const { session_id } = await startRes.json();
      currentSessionIdRef.current = session_id;

      // Poll for events every 3 seconds
      let cursor = 0;
      let pipelineDone = false;

      const processEvent = (event) => {
        if (event.type === "started") {
          currentSessionIdRef.current = event.session_id;
        } else if (event.type === "cancelled") {
          setGeneratingSections(new Set());
          setFlowStep("stopped");
          pipelineDone = true;
        } else if (event.type === "agent_status") {
          const AGENT_ORDER = ["query_builder", "kg_api", "tissue_mapping", "dose_scenario_generator", "dose_translator", "causal_scoring", "section_generator"];
          setAgentStatuses(prev => {
            const next = { ...prev, [event.agent]: event.status };
            // Auto-complete earlier agents that were skipped (e.g. dose_translator)
            if (event.status === "processing") {
              const idx = AGENT_ORDER.indexOf(event.agent);
              for (let i = 0; i < idx; i++) {
                if (!next[AGENT_ORDER[i]] || next[AGENT_ORDER[i]] === "pending") {
                  next[AGENT_ORDER[i]] = "complete";
                }
              }
            }
            return next;
          });
        } else if (event.type === "section_progress" && event.status === "batch_start") {
          const ids = event.content?.section_ids || [];
          if (ids.length > 0) {
            setGeneratingSections(prev => {
              const next = new Set(prev);
              ids.forEach(sid => { const navId = SECTION_NAV_MAP[sid]; if (navId) next.add(navId); });
              return next;
            });
          }
        } else if (event.type === "section_progress" && event.status === "generating") {
          const navId = SECTION_NAV_MAP[event.section_id];
          if (navId) {
            setGeneratingSections(prev => { const next = new Set(prev); next.add(navId); return next; });
          }
        } else if (event.type === "section_progress" && event.status === "completed") {
          setAssessmentData(prev => ({
            ...prev,
            [event.section_id]: { content: event.content, status: "completed", version: 1 }
          }));
        } else if (event.type === "section_progress" && event.status === "batch_complete") {
          const ids = event.content?.section_ids || [];
          ids.forEach(sid => {
            const navId = SECTION_NAV_MAP[sid];
            if (!navId) return;
            setGeneratingSections(prev => { const next = new Set(prev); next.delete(navId); return next; });
            setReadySections(prev => { const next = new Set(prev); next.add(navId); return next; });
            setFlashingSections(prev => { const next = new Set(prev); next.add(navId); return next; });
            setTimeout(() => setFlashingSections(prev => { const next = new Set(prev); next.delete(navId); return next; }), 2500);
          });
          if (!hasAutoNavigatedRef.current && ids.includes("sec2")) {
            hasAutoNavigatedRef.current = true;
            setActiveSection(SECTION_NAV_MAP["sec2"]);
          }
        } else if (event.type === "sections_complete") {
          setAssessmentData(event.sections);
          setReadySections(prev => {
            const next = new Set(prev);
            Object.keys(event.sections || {}).forEach(secId => {
              const navId = SECTION_NAV_MAP[secId];
              if (navId) next.add(navId);
            });
            return next;
          });
          setGeneratingSections(new Set());
          if (!originalConfig) setOriginalConfig(JSON.parse(JSON.stringify(therapyConfig)));
          if (pendingSummary) setRerunSummary(pendingSummary);
          setIsRerun(false);
          setFlowStep("complete");
        } else if (event.type === "done") {
          pipelineDone = true;
        } else if (event.type === "error") {
          failWithError(event.message || "Pipeline returned an error");
          pipelineDone = true;
        }
      };

      let isFirstPoll = true;
      // Tolerate up to 8 consecutive session-not-found responses before
      // declaring the session truly lost. This bridges the gap between:
      //   1. Sticky sessions (lb_cookie) not yet propagated to the ALB
      //      target group after a fresh start-report POST.
      //   2. A transient poll hitting the wrong pod before the cookie sticks.
      // After 8 misses (~24 s) we surface the error so the user can retry.
      let sessionMisses = 0;
      const MAX_SESSION_MISSES = 8;
      while (!pipelineDone) {
        if (abortController.signal.aborted) break;
        await new Promise(r => setTimeout(r, isFirstPoll ? 500 : 3000));
        isFirstPoll = false;
        if (abortController.signal.aborted) break;
        try {
          const pollRes = await fetch(`/api/report-events/${encodeURIComponent(session_id)}?cursor=${cursor}`, {
            signal: abortController.signal,
          });
          if (pollRes.status === 404) {
            // 404 = the backend genuinely has no such route or the session was
            // never created on any pod (shouldn't happen after the 503 fix, but
            // kept as an ultimate safety net with the same miss-counter logic).
            sessionMisses += 1;
            if (sessionMisses >= MAX_SESSION_MISSES) {
              failWithError("Pipeline session lost (server may have restarted). Please try again.");
              break;
            }
            console.warn(`[GNQ] Poll 404 (miss ${sessionMisses}/${MAX_SESSION_MISSES}), retrying in 3s...`);
            continue;
          }
          if (pollRes.status === 503) {
            // 503 = session not found on this pod yet — transient, retry
            sessionMisses += 1;
            if (sessionMisses >= MAX_SESSION_MISSES) {
              failWithError("Pipeline session lost (server may have restarted). Please try again.");
              break;
            }
            console.warn(`[GNQ] Poll 503 session-not-found (miss ${sessionMisses}/${MAX_SESSION_MISSES}), retrying in 3s...`);
            continue;
          }
          if (!pollRes.ok) {
            console.warn(`[GNQ] Poll returned ${pollRes.status}, retrying...`);
            continue;
          }
          // Successful poll — reset miss counter
          sessionMisses = 0;
          const data = await pollRes.json();
          cursor = data.cursor;
          for (const event of data.events) {
            processEvent(event);
          }
          if (data.done) pipelineDone = true;
        } catch (pollErr) {
          if (pollErr.name === "AbortError") break;
          console.warn("[GNQ] Poll error, retrying...", pollErr);
        }
      }
      // If we broke out of the loop due to abort (not normal completion), set stopped state
      if (abortController.signal.aborted && runGenRef.current === myGen) {
        setGeneratingSections(new Set());
        setFlowStep("stopped");
      }
    } catch (err) {
      if (err.name === "AbortError") {
        setGeneratingSections(new Set());
        if (runGenRef.current === myGen) setFlowStep("stopped");
      } else {
        failWithError(err.message || "Failed to connect to backend");
      }
    }
  };

  // ── Review helpers ──
  const toggleConfigSection = (id) => setExpandedConfigSections(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── Full reset — clears all inputs, generated output, and returns to query phase ──
  const handleReset = () => {
    // Invalidate any in-flight startConfigPrep or sendOnboardingMessage callbacks
    resetGenRef.current += 1;
    autoRunRef.current = false;
    pendingConfigIdRef.current = null;
    reportSavedForRunRef.current = false;

    // Reset flow to initial state
    setFlowStep("query");
    setMaxReachedStep(0);
    setPreConfig(null); preConfigRef.current = null;
    setActiveConfig({});
    setUserInput("");
    setQueryData({ originalQuery: "" });
    setConfigPrepStatuses({});
    setOriginalConfig(null);
    setConfigChanges(null);
    setAffectedSections([]);
    setIsRerun(false);
    setRerunSummary(null);
    setOnboardingMessages([{ role: "assistant", content: getWelcomeMessage() }]);
    setOnboardingStep("therapy");
    setShouldAutoStart(false);
    setEditingField(null);
    setEditValue("");
    setResetConfirm(false);

    // Clear generated output and nav state (preserve My Collection)
    setAssessmentData(null);
    setAgentStatuses({});
    setReadySections(new Set());
    setFlashingSections(new Set());
    setGeneratingSections(new Set());
    setActiveSection(null);
    setChatSessionId(null);
    setContextSent(false);
    setChatMessages([{ role: "assistant", content: "Hello! Complete the assessment query to load the report. I can then help you explore pathway scores, efficacy projections, or any section." }]);
    setRightSectionCollapsed({ workspace: true, chat: true });

    // Clear document analysis state but preserve uploaded files
    // Documents are inputs — they survive config resets
    setExtractedDocs([]);
    setDocumentInsights([]);
    setInsightDecisions({});
    setInsightModifications({});
    setDocumentAnalysisError(null);
    setDocumentSummary("");
    setUnmappedFindings([]);
    setShowUploadZone(false);
    setDocAnalyzing(false);
    setOnboardingLoading(false);
    setOnboardingInput("");
    setOnboardingFiles([]);
    pendingOnboardingFilesRef.current = [];
    setPendingFileNames([]);
    docExtractionRef.current = null;
    setExpandedConfigSections(["therapy"]);
  };

  // ── Load demo data from static sections.json ──
  const loadDemo = async () => {
    try {
      const res = await fetch("/demo-sections.json");
      const data = await res.json();
      setAssessmentData(data);
      const navIds = Object.keys(data).map(k => SECTION_NAV_MAP[k]).filter(Boolean);
      setReadySections(new Set(navIds));
      setFlowStep("complete");
      setActiveSection("executive");
    } catch (e) { console.error("Failed to load demo data:", e); }
  };

  // ── Config field updater (generic, handles nested paths like "therapy.comparators.0.name") ──
  const updateConfigField = (fieldPath, value) => {
    setTherapyConfig(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = fieldPath.split(".");
      let obj = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = /^\d+$/.test(keys[i]) ? parseInt(keys[i]) : keys[i];
        if (!obj[key] || typeof obj[key] !== "object") obj[key] = {};
        obj = obj[key];
      }
      const lastKey = /^\d+$/.test(keys[keys.length - 1]) ? parseInt(keys[keys.length - 1]) : keys[keys.length - 1];
      // Convert comma-separated string to array for array fields
      const ARRAY_FIELDS = new Set(["components", "active_ingredients"]);
      obj[lastKey] = ARRAY_FIELDS.has(lastKey) ? value.split(",").map(s => s.trim()).filter(Boolean) : value;
      return updated;
    });
    setEditingField(null);
  };

  // ── TC mutator + inline-edit helpers (Phase 2 equivalent of updateCC/ccEditField) ──
  const updateTC = (fn) => setTherapyConfig(prev => { const draft = JSON.parse(JSON.stringify(prev)); fn(draft); return draft; });
  const deleteTCEndpoint = (cat, i) => updateTC(d => { d.clinical_endpoints[cat].splice(i, 1); });
  const addTCEndpoint = (cat) => {
    const idx = (therapyConfig?.clinical_endpoints?.[cat] || []).length;
    updateTC(d => { (d.clinical_endpoints = d.clinical_endpoints || {}); (d.clinical_endpoints[cat] = d.clinical_endpoints[cat] || []); d.clinical_endpoints[cat].push({ name: "", type: "secondary", enabled: true }); });
    setEditingField(`tc_ep_${cat}_${idx}_name`); setEditValue("");
  };
  const deleteTCCompetitor = (i) => updateTC(d => { d.competitive_landscape.competitors.splice(i, 1); });
  const addTCCompetitor = () => {
    const idx = (therapyConfig?.competitive_landscape?.competitors || []).length;
    updateTC(d => { d.competitive_landscape.competitors.push({ name: "", threat_level: "medium" }); });
    setEditingField(`tc_competitor_${idx}`); setEditValue("");
  };
  const cycleTCCompetitorTier = (i) => updateTC(d => {
    const c = d.competitive_landscape.competitors[i];
    const levels = ["high", "medium", "low"];
    c.threat_level = levels[(levels.indexOf(c.threat_level || "medium") + 1) % 3];
  });
  const deleteTCPipelineThreat = (i) => updateTC(d => { d.competitive_landscape.pipeline_threats.splice(i, 1); });
  const addTCPipelineThreat = () => {
    const idx = (therapyConfig?.competitive_landscape?.pipeline_threats || []).length;
    updateTC(d => { (d.competitive_landscape.pipeline_threats = d.competitive_landscape.pipeline_threats || []); d.competitive_landscape.pipeline_threats.push({ name: "", stage: "Phase 2" }); });
    setEditingField(`tc_pipeline_${idx}`); setEditValue("");
  };
  const saveTCEdit = (key, val) => {
    const v = val.trim();
    if (v) {
      if (/^tc_scenario_(dose|route|frequency|duration)_\d+$/.test(key)) {
        const parts = key.split("_"); const field = parts[2]; const i = +parts[3];
        updateTC(d => { d.dose_scenarios[i][field] = v; });
      } else if (/^tc_ep_(efficacy|safety)_\d+_name$/.test(key)) {
        const parts = key.split("_"); const cat = parts[2]; const idx = +parts[3]; updateTC(d => { d.clinical_endpoints = d.clinical_endpoints || {}; d.clinical_endpoints[cat] = d.clinical_endpoints[cat] || []; if (d.clinical_endpoints[cat][idx]) d.clinical_endpoints[cat][idx].name = v; });
      } else if (/^tc_dose_route_\d+$/.test(key)) {
        const i = +key.split("_")[3]; updateTC(d => { d.dose_config.therapy_dose_levels[i].route = v; });
      } else if (/^tc_dose_comp_\d+_.+$/.test(key)) {
        const parts = key.split("_"); const i = +parts[3]; const comp = parts.slice(4).join("_"); updateTC(d => { d.dose_config.therapy_dose_levels[i].component_doses[comp] = v; });
      } else if (/^tc_comp_name_\d+$/.test(key)) {
        const i = +key.split("_")[3]; updateTC(d => { d.dose_config.comparators[i].name = v; });
      } else if (/^tc_comp_route_\d+$/.test(key)) {
        const i = +key.split("_")[3]; updateTC(d => { d.dose_config.comparators[i].route = v; });
      } else if (/^tc_comp_doses_\d+$/.test(key)) {
        const i = +key.split("_")[3]; updateTC(d => { d.dose_config.comparators[i].doses = v.split(",").map(s => s.trim()); });
      } else if (/^tc_comp_frequency_\d+$/.test(key)) {
        const i = +key.split("_")[3]; updateTC(d => { d.dose_config.comparators[i].frequency = v; });
      } else if (/^tc_comp_duration_\d+$/.test(key)) {
        const i = +key.split("_")[3]; updateTC(d => { d.dose_config.comparators[i].duration = v; });
      } else if (/^tc_competitor_\d+$/.test(key)) {
        const i = +key.split("_")[2]; updateTC(d => { d.competitive_landscape.competitors[i].name = v; });
      } else if (/^tc_pipeline_\d+$/.test(key)) {
        const i = +key.split("_")[2]; updateTC(d => { d.competitive_landscape.pipeline_threats[i].name = v; });
      }
    }
    setEditingField(null);
  };
  const tcEditField = (key, value, className = "text-[9px] text-slate-300") => editingField === key ? (
    <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
      onBlur={() => saveTCEdit(key, editValue)}
      onKeyDown={e => { if (e.key === "Enter") saveTCEdit(key, editValue); if (e.key === "Escape") setEditingField(null); }}
      className="text-[9px] bg-slate-700 border border-cyan-500/30 rounded px-1 py-0.5 text-slate-200 font-mono w-20 outline-none flex-shrink-0" />
  ) : (
    <button onClick={() => { setEditingField(key); setEditValue(value); }} className={`${className} hover:text-cyan-400 flex items-center gap-0.5 group/ef text-left w-full`}>
      {value || "—"}<Edit2 size={7} className="text-slate-600 opacity-0 group-hover/ef:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
  const commitTCTherapyDose = () => {
    const component_doses = {};
    newTherapyComponents.forEach(c => { if (c.name.trim()) component_doses[c.name.trim()] = c.dose.trim(); });
    updateTC(d => { (d.dose_config = d.dose_config || {}); (d.dose_config.therapy_dose_levels = d.dose_config.therapy_dose_levels || []); d.dose_config.therapy_dose_levels.push({ route: newTherapyRoute.trim(), component_doses }); });
    setAddingTherapyDose(false); setNewTherapyRoute(""); setNewTherapyComponents([{ name: "", dose: "" }]);
  };
  const commitTCComparator = () => {
    const doses = newCompDoses.split(",").map(s => s.trim()).filter(Boolean);
    updateTC(d => { (d.dose_config = d.dose_config || {}); (d.dose_config.comparators = d.dose_config.comparators || []); d.dose_config.comparators.push({ name: newCompName.trim(), route: newCompRoute.trim(), doses }); });
    setAddingComparator(false); setNewCompName(""); setNewCompRoute(""); setNewCompDoses("");
  };

  // ── Therapy dose level helpers ──
  const removeTherapyDoseLevel = (levelIndex) => {
    setTherapyConfig(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.dose_config.therapy_dose_levels.splice(levelIndex, 1);
      return updated;
    });
  };

  // ── Comparator helpers ──
  const removeComparator = (index) => {
    setTherapyConfig(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.dose_config.comparators.splice(index, 1);
      return updated;
    });
  };

  // ── Config diff computation ──
  const humanize = (s) => s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const computeConfigDiff = (original, current) => {
    if (!original || !current) return [];
    const changes = [];
    const detect = (category, origObj, currObj, path) => {
      if (JSON.stringify(origObj) === JSON.stringify(currObj)) return;
      if (Array.isArray(origObj) || Array.isArray(currObj)) {
        const toggledItems = [];
        const orig = origObj || [], curr = currObj || [];
        for (let i = 0; i < Math.max(orig.length, curr.length); i++) {
          if (orig[i]?.enabled !== curr[i]?.enabled) toggledItems.push(curr[i]?.name || `item ${i + 1}`);
          if (orig[i]?.excluded !== curr[i]?.excluded) toggledItems.push(curr[i]?.name || `item ${i + 1}`);
        }
        const field = humanize(path.split(".").pop());
        const summary = toggledItems.length > 0
          ? `Toggled: ${toggledItems.join(", ")}`
          : orig.length !== curr.length
            ? `${field}: ${orig.length} → ${curr.length} items`
            : `${field} modified`;
        changes.push({ category, field: path, oldValue: origObj, newValue: currObj, summary });
        return;
      }
      if (typeof origObj === "object" && origObj !== null) {
        const allKeys = new Set([...Object.keys(origObj || {}), ...Object.keys(currObj || {})]);
        for (const key of allKeys) detect(category, origObj?.[key], currObj?.[key], path ? `${path}.${key}` : key);
        return;
      }
      if (origObj !== currObj) {
        changes.push({ category, field: path, oldValue: origObj, newValue: currObj, summary: `${humanize(path.split(".").pop())}: "${origObj}" → "${currObj}"` });
      }
    };

    // Granular therapy sub-field detection
    const origTherapy = original.therapy || {};
    const currTherapy = current.therapy || {};
    // therapy.identity: name, company
    const origIdentity = { name: origTherapy.name, company: origTherapy.company };
    const currIdentity = { name: currTherapy.name, company: currTherapy.company };
    detect("therapy.identity", origIdentity, currIdentity, "therapy");
    // therapy.components
    detect("therapy.components", origTherapy.components, currTherapy.components, "therapy.components");
    // therapy.comparators
    detect("therapy.comparators", origTherapy.comparators, currTherapy.comparators, "therapy.comparators");

    // All other top-level categories (skip "therapy" — handled above)
    const otherCategories = ["indication", "clinical_endpoints", "dose_config", "dose_scenarios", "competitive_landscape", "commercial_assumptions"];
    for (const category of otherCategories) {
      detect(category, original[category], current[category], category);
    }
    return changes;
  };

  const deriveAffectedSections = (changes) => {
    const sections = new Set();
    for (const change of changes) {
      const mapping = CONFIG_SECTION_MAP[change.category];
      if (mapping) mapping.forEach(s => sections.add(s));
    }
    // sec1 auto-cascades if any other section is affected
    if (sections.size > 0) sections.add("sec1");
    return [...sections].sort();
  };

  // ── Upload, analyze, and go to insights ──
  const analyzeAndContinue = async () => {
    if (!uploadedFiles.length) return;
    setDocAnalyzing(true);
    setDocumentAnalysisError(null);
    try {
      const formData = new FormData();
      uploadedFiles.filter(f => f instanceof File).forEach(f => formData.append("files", f));
      const uploadRes = await fetch("/api/upload-files", { method: "POST", body: formData });
      if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status}`);
      const uploadData = await uploadRes.json();
      const extracted = uploadData.files.filter(f => !f.error);
      setExtractedDocs(extracted);

      const analyzeRes = await fetch("/api/analyze-documents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          extracted_texts: extracted.map(f => ({ filename: f.filename, file_type: f.file_type, text: f.extracted_content })),
          query: userInput,
        }),
      });
      if (!analyzeRes.ok) throw new Error(`Analysis failed: ${analyzeRes.status}`);
      const analysis = await analyzeRes.json();
      const insights = analysis.insights?.insights || [];
      setDocumentInsights(insights);
      setDocumentSummary(analysis.insights?.document_summary || "");
      setUnmappedFindings(analysis.insights?.unmapped_findings || []);
      const decisions = {};
      insights.forEach(ins => { decisions[ins.id] = "accepted"; });
      setInsightDecisions(decisions);
      setFlowStep("documentInsights");
    } catch (err) {
      console.error("[GNQ] Document analysis error:", err);
      setDocumentAnalysisError(err.message);
    }
    setDocAnalyzing(false);
  };

  // ── Apply document insights to config ──
  const applyInsightsAndContinue = () => {
    setTherapyConfig(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const appliedPaths = [];
      documentInsights.forEach(insight => {
        const decision = insightDecisions[insight.id];
        if (decision === "rejected") return;
        const value = decision === "modified" && insightModifications[insight.id] != null ? insightModifications[insight.id] : insight.value;
        const action = insight.action || "set";
        const path = insight.field_path;
        appliedPaths.push(path);
        const keys = path.split(".");
        if (action === "add") {
          // Navigate to the array and push
          let obj = updated;
          for (const key of keys) { const k = /^\d+$/.test(key) ? parseInt(key) : key; if (obj[k] === undefined) obj[k] = Array.isArray(obj) ? [] : {}; obj = obj[k]; }
          if (Array.isArray(obj)) obj.push(typeof value === "object" ? { ...value, _fromDocument: true } : value);
        } else {
          // Set scalar value
          let obj = updated;
          for (let i = 0; i < keys.length - 1; i++) { const k = /^\d+$/.test(keys[i]) ? parseInt(keys[i]) : keys[i]; if (obj[k] === undefined) obj[k] = {}; obj = obj[k]; }
          obj[keys[keys.length - 1]] = value;
        }
      });
      updated._documentFields = appliedPaths;
      return updated;
    });
    setFlowStep("review");
  };

  // ── Onboarding chat functions ──
  const sendOnboardingMessage = async (text) => {
    if (!text.trim()) return;
    const gen = resetGenRef.current;
    // Clean display text: strip [Attached: ...] tags for the visible chat bubble
    const visibleText = text.replace(/\[Attached: [^\]]+\]\s*/g, "").trim() || text;
    const userMsg = { role: "assistant", content: "" };
    const newHistory = [...onboardingMessages, { role: "user", content: visibleText }, userMsg];
    setOnboardingMessages(newHistory);
    setOnboardingInput("");
    setOnboardingLoading(true);

    try {
      // If there are pending files, extract config from them first
      let llmText = visibleText;
      const pendingFiles = pendingOnboardingFilesRef.current;
      if (pendingFiles.length > 0) {
        pendingOnboardingFilesRef.current = [];
        setPendingFileNames([]);
        // Update the assistant placeholder to show analyzing status
        setOnboardingMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "assistant", content: `Analyzing ${pendingFiles.length} document${pendingFiles.length > 1 ? "s" : ""}...` };
          return updated;
        });
        try {
          const formData = new FormData();
          pendingFiles.forEach(f => formData.append("files", f));
          const extractRes = await fetch("/api/extract-document-config", { method: "POST", body: formData });
          if (extractRes.ok) {
            const result = await extractRes.json();
            const extractedConfig = result.extracted_config || {};
            const sections = result.sections || [];
            const summary = result.summary || "";
            if (Object.keys(extractedConfig).length > 0) {
              // Store extracted config in use case store (pending confirmation)
              ucStore.extractConfig("auto", extractedConfig);
              // Merge into active config — therapyConfig after handoff, collectedConfig during onboarding
              const setConfig = setActiveConfig;
              setConfig(prev => deepMerge(prev || {}, extractedConfig));
              docExtractionRef.current = { loaded: true, summary };

              // Tell the LLM what was extracted so it can continue the onboarding flow naturally
              llmText = `${visibleText}\n\n[Documents were analyzed. The left panel has been updated with extracted data (highlighted as "from document"). Summary: ${summary}. Continue the onboarding conversation naturally — acknowledge what was found and proceed to the next step that needs user input.]`;
            }
            // Poll for background discovery results (ClinicalTrials.gov + web search)
            if (result.discovery_task_id) {
              const taskId = result.discovery_task_id;
              const pollDiscovery = async () => {
                for (let i = 0; i < 30; i++) { // poll up to ~150s
                  await new Promise(r => setTimeout(r, 5000));
                  try {
                    const statusRes = await fetch(`/api/discovery-status/${taskId}`);
                    if (!statusRes.ok) continue;
                    const status = await statusRes.json();
                    if (status.status === "complete" && status.config) {
                      console.log("[GNQ] Background discovery complete, merging enriched config");
                      const setConfig = setActiveConfig;
                      setConfig(prev => deepMerge(prev || {}, status.config));
                      break;
                    } else if (status.status === "failed") {
                      console.warn("[GNQ] Background discovery failed:", status.error);
                      break;
                    }
                  } catch (e) { /* ignore poll errors */ }
                }
              };
              pollDiscovery(); // fire-and-forget — don't await
            }
          }
        } catch (extractErr) {
          console.error("[GNQ] Document extraction failed:", extractErr);
        }
        // Reset assistant placeholder for LLM response
        setOnboardingMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "assistant", content: "" };
          return updated;
        });
      }

      // Don't send preConfig if the collected therapy name doesn't match it.
      let preConfigToSend = preConfigRef.current || {};
      if (preConfigToSend?.therapy?.name) {
        // Use activeUseCase name as primary source (stable), then collectedConfig, then skip user text (unreliable for matching)
        const collectedName = (collectedConfig?.therapy?.name || activeUseCase?.name || "").replace(/[-_\s]+/g, "").toLowerCase();
        const preName = preConfigToSend.therapy.name.replace(/[-_\s]+/g, "").toLowerCase();
        if (collectedName && !collectedName.includes(preName) && !preName.includes(collectedName)) {
          preConfigToSend = {};
        }
      }
      // Build messages for API
      const apiMessages = newHistory.slice(0, -1).filter((m, i) => !(i === 0 && m.role === "assistant")).map(m => ({ role: m.role, content: m.content }));
      // Replace the last user message with llmText (which may include document load notification)
      if (apiMessages.length > 0 && apiMessages[apiMessages.length - 1].role === "user") {
        apiMessages[apiMessages.length - 1] = { role: "user", content: llmText };
      }
      // Build use case context for the backend
      const useCaseContext = {
        name: activeUseCase?.name || "",
        indication: activeUseCase?.indication || "",
        has_reports: filteredReports.length > 0,
        has_saved_configs: filteredConfigs.length > 0,
        has_documents: filteredFiles.length > 0,
        latest_report_name: filteredReports.length > 0 ? [...filteredReports].sort((a, b) => new Date(b.datetime) - new Date(a.datetime))[0]?.name : "",
        latest_config_name: filteredConfigs.length > 0 ? [...filteredConfigs].sort((a, b) => new Date(b.datetime) - new Date(a.datetime))[0]?.name : "",
      };
      // Include uploaded document content — already extracted at upload time
      const docsWithContent = filteredFiles.filter(f => f.extracted_content);
      const uploadedDocuments = docsWithContent.length > 0
        ? docsWithContent.map(f => ({ filename: f.name, file_type: f.file_type || f.type || "unknown", content: (f.extracted_content || "").slice(0, 8000) }))
        : filteredFiles.length > 0
          ? filteredFiles.map(f => ({ filename: f.name || "unknown", file_type: f.file_type || f.type || "unknown", content: null }))
          : null;

      const response = await fetch("/api/onboarding-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: apiMessages,
          current_step: onboardingStep,
          collected_config: collectedConfig,
          pre_config: preConfigToSend,
          use_case_context: useCaseContext,
          uploaded_documents: uploadedDocuments,
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Server error ${response.status}: ${errText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (resetGenRef.current !== gen) { reader.cancel(); break; } // reset fired — discard stream
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;
          try {
            const event = JSON.parse(jsonStr);
            if (event.type === "token") {
              setOnboardingMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: event.content };
                return updated;
              });
            } else if (event.type === "config") {
              const pc = preConfigRef.current; // always latest via ref
              console.log("[onboarding] config event:", { extracted: event.extracted, use_pre_config_for: event.use_pre_config_for, next_step: event.next_step, preConfigLoaded: !!pc });
              if (event.extracted && Object.keys(event.extracted).length > 0) {
                // Normalize clinical_endpoints: LLM may emit strings instead of {name,type,enabled} objects
                if (event.extracted.clinical_endpoints) {
                  const ce = event.extracted.clinical_endpoints;
                  ["efficacy", "safety"].forEach(cat => {
                    if (Array.isArray(ce[cat])) {
                      ce[cat] = ce[cat].map(ep =>
                        typeof ep === "string" ? { name: ep, type: "primary", enabled: true } : ep
                      );
                    }
                  });
                }
                // Normalize dose_config: ensure doses array contains objects
                if (event.extracted.dose_config?.doses && Array.isArray(event.extracted.dose_config.doses)) {
                  event.extracted.dose_config.doses = event.extracted.dose_config.doses.map(d =>
                    typeof d === "string" ? { name: d, amount: "", route: "", frequency: "" } : d
                  );
                }
                // After handoff (therapyConfig exists), write to therapyConfig. Before handoff, write to collectedConfig.
                const setConfig = setActiveConfig;
                setConfig(prev => {
                  // For sections with arrays (endpoints, doses, competitors), replace entirely instead of deep-merging
                  // This ensures LLM corrections (e.g., changing endpoint type) actually take effect
                  const ext = event.extracted;
                  const overrides = {};
                  if (ext.clinical_endpoints) overrides.clinical_endpoints = ext.clinical_endpoints;
                  if (ext.dose_scenarios) overrides.dose_scenarios = ext.dose_scenarios;
                  if (ext.dose_config) overrides.dose_config = ext.dose_config;
                  if (ext.competitive_landscape) overrides.competitive_landscape = ext.competitive_landscape;
                  const merged = { ...deepMerge(prev || {}, ext), ...overrides };
                  // Normalize comparator doses: ensure doses is always an array
                  if (merged.dose_config?.comparators) {
                    merged.dose_config.comparators = merged.dose_config.comparators.map(comp => ({
                      ...comp,
                      doses: Array.isArray(comp.doses) ? comp.doses
                        : typeof comp.doses === "string" ? comp.doses.split(",").map(s => s.trim()).filter(Boolean)
                        : comp.dose ? [comp.dose] : [],
                    }));
                  }
                  return merged;
                });
              }
              // Copy large pre-loaded sections directly from preConfig when bot signals user confirmed as-is.
              // NEVER overwrite keys that were just extracted — extracted data takes priority over preConfig.
              const extractedKeys = new Set(Object.keys(event.extracted || {}));
              if (event.use_pre_config_for?.length && pc) {
                const fromPre = {};
                event.use_pre_config_for.forEach(k => {
                  if (pc[k] && !extractedKeys.has(k)) fromPre[k] = pc[k];
                  else if (extractedKeys.has(k)) console.log(`[onboarding] Skipping use_pre_config_for "${k}" — already in extracted`);
                });
                if (Object.keys(fromPre).length > 0) {
                  const setConfig = setActiveConfig;
                  setConfig(prev => ({ ...(prev || {}), ...fromPre }));
                }
              } else if (event.use_pre_config_for?.length && !pc) {
                console.warn("[onboarding] use_pre_config_for requested but preConfig is null — fetching now:", event.use_pre_config_for);
                const keysToFill = event.use_pre_config_for.filter(k => !extractedKeys.has(k));
                if (keysToFill.length === 0) { console.log("[onboarding] All use_pre_config_for keys already in extracted, skipping fetch"); }
                else {
                  const therapyName = collectedConfig?.therapy?.name || "";
                  const cfgId = therapyName ? therapyName.replace(/[-_\s]+/g, "").toLowerCase() : DEFAULT_THERAPY_ID;
                  fetchPreConfig(cfgId).then(d => {
                    if (!d) return;
                    setPreConfig(d); preConfigRef.current = d;
                    const fromPre = {};
                    keysToFill.forEach(k => { if (d[k]) fromPre[k] = d[k]; });
                    if (Object.keys(fromPre).length > 0) {
                      const setConfig = setActiveConfig;
                      setConfig(prev => ({ ...(prev || {}), ...fromPre }));
                    }
                  }).catch(e => console.error("[onboarding] emergency preConfig fetch failed:", e));
                }
              }
              if (event.next_step) {
                setOnboardingStep(event.next_step);
                // Auto-expand the left panel section for this step so user sees it populate
                const stepSections = { therapy: "therapy", indication: "indication", endpoints: "endpoints", doses: "doses", competitive: "competitive", commercial: "commercial" };
                const sectionId = stepSections[event.next_step];
                if (sectionId) setExpandedConfigSections(prev => prev.includes(sectionId) ? prev : [...prev, sectionId]);
                // When therapy step completes, check if preConfig matches the new therapy.
                // If not, clear it so stale defaults (e.g. MLB-001 endpoints) don't leak into a new use case.
                if (event.next_step === "indication" && pc) {
                  const newName = (event.extracted?.therapy?.name || collectedConfig?.therapy?.name || "").replace(/[-_\s]+/g, "").toLowerCase();
                  const preName = (pc?.therapy?.name || "").replace(/[-_\s]+/g, "").toLowerCase();
                  if (newName && preName && newName !== preName) {
                    console.log(`[onboarding] therapy changed: "${preName}" → "${newName}" — clearing stale preConfig`);
                    setPreConfig(null); preConfigRef.current = null;
                    // Try to fetch a matching config for the new therapy
                    fetchPreConfig(newName).then(d => {
                      if (d) { setPreConfig(d); preConfigRef.current = d; }
                    }).catch(() => {});
                  }
                }
                // Fallback: fill preConfig defaults for (a) just-confirmed section and (b) about-to-present section.
                // User's explicit changes already in prev win via deepMerge (preConfig as target, prev on top).
                // Re-read pc after potential clear above
                const currentPc = preConfigRef.current;
                const stepPreFill = {
                  indication: ["therapy"],
                  endpoints: ["indication", "clinical_endpoints"],
                  doses: ["clinical_endpoints"],
                  competitive: ["dose_config"],
                  commercial: ["competitive_landscape"],
                  documents: [],
                  confirm: [],
                };
                const preKeys = stepPreFill[event.next_step] || [];
                // Only pre-fill keys that are NOT already in the extracted payload
                // (extracted values from LLM take priority over preConfig defaults)
                const extractedKeys = new Set(Object.keys(event.extracted || {}));
                const filteredPreKeys = preKeys.filter(k => !extractedKeys.has(k));
                if (filteredPreKeys.length > 0 && currentPc) {
                  const defaults = {};
                  filteredPreKeys.forEach(k => { if (currentPc[k]) defaults[k] = currentPc[k]; });
                  if (Object.keys(defaults).length > 0)
                    setCollectedConfig(prev => deepMerge(defaults, prev));
                } else if (preKeys.length > 0 && !currentPc) {
                  // preConfig not loaded yet — emergency fetch
                  const fillKeys = [...preKeys];
                  const therapyName2 = collectedConfig?.therapy?.name || "";
                  const cfgId2 = therapyName2 ? therapyName2.replace(/[-_\s]+/g, "").toLowerCase() : DEFAULT_THERAPY_ID;
                  fetchPreConfig(cfgId2).then(d => {
                    if (!d) return;
                    setPreConfig(d); preConfigRef.current = d;
                    const defaults = {};
                    fillKeys.forEach(k => { if (d[k]) defaults[k] = d[k]; });
                    if (Object.keys(defaults).length > 0) setCollectedConfig(prev => deepMerge(defaults, prev));
                  }).catch(() => {});
                }
                // Auto-start: backend sends next_step:"done" when user confirms they're ready
                if (event.next_step === "done") setShouldAutoStart(true);
              }
            } else if (event.type === "error") {
              throw new Error(event.message);
            }
          } catch (parseErr) {
            console.error("[onboarding] SSE parse error:", parseErr, "line:", line);
          }
        }
      }
    } catch (err) {
      setOnboardingMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: "assistant", content: `Connection error: ${err.message}` };
        return updated;
      });
    }
    setOnboardingLoading(false);
  };

  // Pending files staged for extraction — will be processed when user sends a message
  const pendingOnboardingFilesRef = useRef([]);
  // Persisted extraction result — available across all subsequent onboarding messages
  const docExtractionRef = useRef(null);
  const [pendingFileNames, setPendingFileNames] = useState([]);

  const handleOnboardingFiles = (files) => {
    const validExts = ["pdf", "docx", "doc", "png", "jpg", "jpeg", "gif", "webp"];
    const maxSize = 20 * 1024 * 1024;
    const valid = files.filter(f => validExts.includes(f.name.split(".").pop().toLowerCase()) && f.size <= maxSize);
    if (!valid.length) return;
    // Add directly to uploadedFiles so they appear in My Collection immediately
    addFiles(valid);
    // Stage files for extraction when user sends their next message
    pendingOnboardingFilesRef.current = [...pendingOnboardingFilesRef.current, ...valid];
    setPendingFileNames(pendingOnboardingFilesRef.current.map(f => f.name));
  };

  const handleConfirmAndStart = () => {
    // Merge preConfig defaults into activeConfig (fills gaps, activeConfig values win)
    const pc = preConfigRef.current;
    if (pc) {
      const configName = (activeConfig?.therapy?.name || "").replace(/[-_\s]+/g, "").toLowerCase();
      const preName = (pc?.therapy?.name || "").replace(/[-_\s]+/g, "").toLowerCase();
      if (configName && preName && configName === preName) {
        // Pre-config matches — fill gaps only (activeConfig wins on conflicts)
        setActiveConfig(prev => deepMerge(pc, prev));
      }
    }
    // autoRunRef triggers handleRunPipeline() automatically after 500ms in review
    autoRunRef.current = true;
    setFlowStep("review");
  };

  // ── Reset ──
  const resetFlow = () => {
    setFlowStep("query"); setMaxReachedStep(0); setUserInput(""); setAssessmentData(null); setAgentStatuses({});
    setQueryData({ originalQuery: "" }); setActiveConfig({}); setConfigPrepStatuses({}); setPreConfig(null); preConfigRef.current = null;
    setExpandedConfigSections(["therapy"]); setEditingField(null);
    setChatSessionId(null); setContextSent(false);
    setOriginalConfig(null); setConfigChanges(null); setAffectedSections([]); setIsRerun(false); setRerunSummary(null);
    setExtractedDocs([]); setDocumentInsights([]); setInsightDecisions({}); setInsightModifications({});
    setDocumentAnalysisError(null); setDocumentSummary(""); setUnmappedFindings([]);
    setShowUploadZone(false); setDocAnalyzing(false);
    setChatMessages([{ role: "assistant", content: "Hello! Complete the assessment query to load the report. I can then help you explore pathway scores, efficacy projections, or any section." }]);
    setOnboardingMessages([{ role: "assistant", content: getWelcomeMessage() }]);
    setOnboardingStep("therapy"); setOnboardingLoading(false);
    setOnboardingInput(""); setOnboardingFiles([]);
    setExpandedConfigSections(["therapy"]);
    autoRunRef.current = false;
    pendingConfigIdRef.current = null;
    setRightSectionCollapsed({ workspace: true, chat: true });
    // Clear use case store chat (explicit user reset)
    ucStore.clearAllChat();
  };

  // ── Restore a saved config version to the left panel ──
  const restoreConfig = (config, name) => {
    const copy = JSON.parse(JSON.stringify(config));
    setActiveConfig(copy);     // single source of truth
    setPreConfig(copy);        // gives chat a correct base to adjust from
    setOriginalConfig(JSON.parse(JSON.stringify(copy))); // baseline for diff comparison
    setIsRerun(true);
    if (flowStep === "query") {
      // Phase 1: append restore message to existing chat (don't wipe history)
      const therapy = copy.therapy?.name || "your therapy";
      const indication = copy.indication?.disease || "the indicated condition";
      const label = name || "a saved input";
      setOnboardingMessages(prev => [
        ...prev,
        { role: "assistant", content: `I've loaded ${label} — ${therapy} for ${indication}.\n\nThe left panel is now filled with those inputs. Would you like to change anything before running the assessment?` }
      ]);
      setOnboardingStep("confirm");
      setExpandedConfigSections(["therapy", "indication", "endpoints", "doses", "competitive", "commercial"]);
    } else {
      setFlowStep("review");
    }
  };

  // ── Print to PDF (browser native) ──
  const printReport = () => {
    if (!assessmentData) { alert("No assessment data to export."); return; }
    window.print();
  };

  // ── Export PDF (legacy DOCX→PDF via LibreOffice) ──
  const exportReport = async () => {
    if (!assessmentData) { alert("No assessment data to export."); return; }
    const today = new Date();
    const ymd = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
    const drugId = therapyConfig?.therapy?.name || "Assessment";
    const defaultName = `GNQ_${drugId}_Report_${ymd}`;
    const userFilename = prompt("Save report as:", defaultName);
    if (!userFilename) return;
    const baseName = userFilename.replace(/\.(pdf|docx)$/i, "");
    const finalName = `${baseName}.pdf`;
    try {
      const response = await fetch("/api/export-pdf", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sections_data: assessmentData, therapy_config: therapyConfig }),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Export failed (${response.status}): ${errText}`);
      }
      const blob = await response.blob();
      if (!blob.size) throw new Error("Empty response from server");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = finalName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 3000);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    }
  };

  // ── Chat (SSE streaming to backend /api/chat) ──
  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatInput("");
    setChatLoading(true);

    const body = { message: userMessage, session_id: chatSessionId };
    // Always send context when data is available — backend merges it into the session
    if (assessmentData || therapyConfig) {
      body.context = {};
      if (assessmentData) body.context.sections_data = assessmentData;
      if (therapyConfig) body.context.therapy_config = therapyConfig;
    }
    // Include uploaded document content — already extracted at upload time
    const docsWithContent = filteredFiles.filter(f => f.extracted_content);
    if (docsWithContent.length > 0) {
      body.uploaded_documents = docsWithContent.map(f => ({ filename: f.name, file_type: f.file_type || f.type || "unknown", content: (f.extracted_content || "").slice(0, 8000) }));
    } else if (filteredFiles.length > 0) {
      body.uploaded_documents = filteredFiles.map(f => ({ filename: f.name || "unknown", file_type: f.file_type || f.type || "unknown", content: null }));
    }

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = "";
      let buffer = "";

      setChatMessages(prev => [...prev, { role: "assistant", content: "" }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const event = JSON.parse(jsonStr);
            if (event.type === "token") {
              assistantMessage += event.content;
              setChatMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: assistantMessage };
                return updated;
              });
            } else if (event.type === "done") {
              if (event.session_id) { setChatSessionId(event.session_id); if (!contextSent) setContextSent(true); }
            } else if (event.type === "error") {
              assistantMessage = `Sorry, I encountered an error: ${event.message}`;
              setChatMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: "assistant", content: assistantMessage }; return u; });
            }
          } catch (_) { /* skip unparseable lines */ }
        }
      }
    } catch (err) {
      setChatMessages(prev => [
        ...prev.filter(m => m.content !== ""),
        { role: "assistant", content: `Connection error: ${err.message}. Make sure the backend is running on port 8000.` },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const toggleNav = (id) => setExpandedNav(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  // Data access helpers
  const d = assessmentData;
  const sec = (id) => d?.[id]?.content || {};

  // ── Config section render helper ──
  const sectionToStoreKey = { therapy: "therapy", indication: "indication", endpoints: "clinical_endpoints", doses: "dose_scenarios", competitive: "competitive_landscape", commercial: "commercial_assumptions" };
  const renderConfigSection = (id, title, content, readOnly = true) => {
    const isExpanded = expandedConfigSections.includes(id);
    const storeKey = sectionToStoreKey[id];
    const status = storeKey ? ucStore.confirmationStatus[storeKey] : null;
    return (
      <div key={id} className={`border rounded-lg overflow-hidden ${status === "extracted" ? "border-amber-500/30" : isDark ? "border-cyan-500/20" : "border-slate-300"}`}>
        <button onClick={() => toggleConfigSection(id)} className={`w-full flex items-center justify-between px-3 py-2 ${isDark ? "bg-slate-800/50 hover:bg-slate-800/80" : "bg-slate-200 hover:bg-slate-300"}`}>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold ${isDark ? "text-slate-300" : "text-slate-700"}`}>{title}</span>
            {status === "extracted" && (
              <span className="text-[8px] px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-400 font-medium">from document</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isExpanded ? <ChevronDown size={14} className={isDark ? "text-slate-500" : "text-slate-500"} /> : <ChevronRight size={14} className={isDark ? "text-slate-500" : "text-slate-500"} />}
          </div>
        </button>
        {isExpanded && <div className={`px-3 py-2 space-y-1.5 ${isDark ? "bg-slate-900/30" : "bg-slate-50"}`}>{content}</div>}
      </div>
    );
  };

  // ── Editable row helper (works for any field) ──
  const renderEditableRow = (label, fieldPath, value) => {
    const isFromDoc = therapyConfig?._documentFields?.some(df => fieldPath.startsWith(df) || df.startsWith(fieldPath));
    return (
    <div className="flex items-start justify-between gap-2 py-1">
      <span className="text-[10px] text-slate-400 flex items-center gap-1 flex-shrink-0 pt-0.5">{label}{isFromDoc && <span className="text-[7px] bg-purple-500/20 text-purple-400 px-1 py-0.5 rounded border border-purple-500/30">doc</span>}</span>
      {editingField === fieldPath ? (
        <div className="flex items-center gap-1">
          <input value={editValue} onChange={e => setEditValue(e.target.value)} className="w-24 text-[10px] bg-slate-700 border border-cyan-500/30 rounded px-1 py-0.5 text-slate-200 font-mono" autoFocus onKeyDown={e => e.key === "Enter" && updateConfigField(fieldPath, editValue)} />
          <button onClick={() => updateConfigField(fieldPath, editValue)} className="text-green-400"><Check size={10} /></button>
          <button onClick={() => setEditingField(null)} className="text-slate-500"><X size={10} /></button>
        </div>
      ) : (
        <div className="flex items-start gap-1 justify-end">
          <span className="text-[10px] font-mono text-cyan-400 text-right">{value}</span>
          <button onClick={() => { setEditingField(fieldPath); setEditValue(value); }} className="text-slate-500 hover:text-cyan-400 flex-shrink-0 pt-0.5"><Edit2 size={8} /></button>
        </div>
      )}
    </div>
  ); };

  // ── Onboarding panel helpers — write to active config (therapyConfig if it exists, else collectedConfig) ──
  const updateOnboardingField = (path, value) => {
    const keys = path.split(".");
    const lastKey = keys[keys.length - 1];
    const finalValue = lastKey === "components" ? value.split(",").map(s => s.trim()).filter(Boolean) : value;
    setActiveConfig(prev => {
      const u = JSON.parse(JSON.stringify(prev || {}));
      let o = u;
      for (let i = 0; i < keys.length - 1; i++) { if (!o[keys[i]]) o[keys[i]] = {}; o = o[keys[i]]; }
      o[lastKey] = finalValue;
      return u;
    });
    setEditingField(null);
  };

  const renderBotMessage = (text) =>
    text.split("\n").map((line, li) => {
      const parts = line.split(/\*\*(.+?)\*\*/g);
      return (
        <span key={li}>
          {parts.map((part, pi) => pi % 2 === 1 ? <strong key={pi}>{part}</strong> : part)}
          {li < text.split("\n").length - 1 && <br />}
        </span>
      );
    });

  const renderOnboardingRow = (label, path, value) => (
    <div className="flex items-start justify-between gap-2 py-1">
      <span className={`text-[10px] flex-shrink-0 pt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>{label}</span>
      {editingField === `ob_${path}` ? (
        <input value={editValue} onChange={e => setEditValue(e.target.value)}
          className={`w-28 text-[10px] border border-cyan-500/30 rounded px-1.5 py-0.5 font-mono text-right ${isDark ? "bg-slate-700 text-slate-200" : "bg-white text-slate-800"}`}
          autoFocus
          onKeyDown={e => { if (e.key === "Enter") { updateOnboardingField(path, editValue); } if (e.key === "Escape") setEditingField(null); }}
          onBlur={() => { updateOnboardingField(path, editValue); }} />
      ) : (
        <div className="flex items-start gap-1 justify-end">
          <span className="text-[10px] font-mono text-cyan-400 text-right">{value || <span className="text-slate-600">—</span>}</span>
          <button onClick={() => { setEditingField(`ob_${path}`); setEditValue(value || ""); }} className="text-slate-500 hover:text-cyan-400 flex-shrink-0 pt-0.5"><Edit2 size={8} /></button>
        </div>
      )}
    </div>
  );


  // ─── DOCS & REPORTS PANEL (right panel — both phases) ───
  const renderDocsReportsPanel = () => {
    // Use filtered collections when a use case is active
    const visibleFiles = filteredFiles;
    const visibleConfigs = filteredConfigs;
    const visibleReports = filteredReports;

    const downloadFile = (f) => {
      if (!(f instanceof File)) return;
      const url = URL.createObjectURL(f);
      const a = document.createElement("a");
      a.href = url; a.download = f.name; a.click();
      URL.revokeObjectURL(url);
    };

    // Condensed config summary: dose + comparator + indication
    const summarizeConfig = (cfg) => {
      const doses = cfg?.dose_config?.therapy_dose_levels?.[0]?.component_doses;
      const doseParts = doses ? Object.entries(doses).filter(([, v]) => v).map(([k, v]) => `${v} ${k.split(" ")[0]}`).join(" / ") : "";
      const comparator = cfg?.therapy?.comparators?.[0]?.name || "";
      const indication = cfg?.indication?.disease || "";
      return [doseParts, comparator, indication].filter(Boolean).join(" · ") || "No config summary";
    };

    // Safe array helper — ensures we always get an array even from malformed data
    const safeArr = (val) => Array.isArray(val) ? val : [];

    // Diff two configs and return human-readable change list
    const diffConfigs = (prev, curr) => {
      if (!prev) return null; // first version — no diff
      try {
      const changes = [];

      // Dose changes
      const prevDoses = prev?.dose_config?.therapy_dose_levels?.[0]?.component_doses || {};
      const currDoses = curr?.dose_config?.therapy_dose_levels?.[0]?.component_doses || {};
      const allDoseKeys = new Set([...Object.keys(prevDoses || {}), ...Object.keys(currDoses || {})]);
      for (const k of allDoseKeys) {
        const pv = prevDoses[k], cv = currDoses[k];
        const label = k.split(" ")[0];
        if (pv && !cv) changes.push(`Removed ${label} dose`);
        else if (!pv && cv) changes.push(`Added ${label}: ${cv}mg`);
        else if (pv !== cv) changes.push(`${label}: ${pv}mg → ${cv}mg`);
      }

      // Dose level count
      const prevLevels = safeArr(prev?.dose_config?.therapy_dose_levels).length;
      const currLevels = safeArr(curr?.dose_config?.therapy_dose_levels).length;
      if (prevLevels !== currLevels) changes.push(`Dose levels: ${prevLevels} → ${currLevels}`);

      // Comparators
      const prevComps = safeArr(prev?.therapy?.comparators).map(c => c?.name || "").sort();
      const currComps = safeArr(curr?.therapy?.comparators).map(c => c?.name || "").sort();
      if (JSON.stringify(prevComps) !== JSON.stringify(currComps)) {
        const added = currComps.filter(c => !prevComps.includes(c));
        const removed = prevComps.filter(c => !currComps.includes(c));
        if (added.length) changes.push(`+comparator: ${added.join(", ")}`);
        if (removed.length) changes.push(`−comparator: ${removed.join(", ")}`);
      }

      // Efficacy endpoints
      const prevEff = safeArr(prev?.clinical_endpoints?.efficacy).length;
      const currEff = safeArr(curr?.clinical_endpoints?.efficacy).length;
      if (prevEff !== currEff) changes.push(`Efficacy endpoints: ${prevEff} → ${currEff}`);

      // Safety endpoints
      const prevSaf = safeArr(prev?.clinical_endpoints?.safety).length;
      const currSaf = safeArr(curr?.clinical_endpoints?.safety).length;
      if (prevSaf !== currSaf) changes.push(`Safety endpoints: ${prevSaf} → ${currSaf}`);

      // Competitors
      const prevComp = safeArr(prev?.competitive_landscape?.competitors).length;
      const currComp = safeArr(curr?.competitive_landscape?.competitors).length;
      if (prevComp !== currComp) changes.push(`Competitors: ${prevComp} → ${currComp}`);

      // Commercial assumptions (key fields)
      const prevCom = (typeof prev?.commercial_assumptions === 'object' && prev?.commercial_assumptions) || {};
      const currCom = (typeof curr?.commercial_assumptions === 'object' && curr?.commercial_assumptions) || {};
      const comFields = ["avg_annual_price", "market_share_peak", "total_addressable_market"];
      for (const f of comFields) {
        if (prevCom[f] !== currCom[f] && (prevCom[f] || currCom[f])) {
          const label = f.replace(/_/g, " ");
          changes.push(`${label}: ${prevCom[f] || "—"} → ${currCom[f] || "—"}`);
        }
      }

      // Fallback: check top-level keys for any other changes
      if (changes.length === 0) {
        const topKeys = new Set([...Object.keys(prev || {}), ...Object.keys(curr || {})]);
        for (const k of topKeys) {
          if (JSON.stringify(prev?.[k]) !== JSON.stringify(curr?.[k])) {
            changes.push(`${k.replace(/_/g, " ")} updated`);
          }
        }
      }
      return changes.length > 0 ? changes : ["No config changes detected"];
      } catch (e) { return ["Unable to diff configs"]; }
    };

    // Build diff tooltips for visible configs (ordered oldest → newest)
    const configDiffs = {};
    const ordered = [...visibleConfigs].sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    ordered.forEach((sc, i) => {
      if (i === 0) {
        configDiffs[sc.id] = "Initial version\n" + summarizeConfig(sc.config);
      } else {
        const prevSc = ordered[i - 1];
        const diff = diffConfigs(prevSc.config, sc.config);
        const prevVersion = prevSc.name?.match(/v(\d+)$/i)?.[0] || `v${i}`;
        configDiffs[sc.id] = diff ? "Changes from " + prevVersion + ":\n" + diff.join("\n") : summarizeConfig(sc.config);
      }
    });

    const saveConfigName = (id) => {
      if (configNameValue.trim()) {
        setSavedConfigs(prev => prev.map(sc => sc.id === id ? { ...sc, name: configNameValue.trim() } : sc));
      }
      setEditingConfigName(null);
    };

    const saveFileName = (fileRef) => {
      if (fileNameValue.trim()) {
        setUploadedFiles(prev => prev.map(f => {
          if (f !== fileRef) return f;
          // File objects are immutable — store display name as a custom property
          if (f instanceof File) { f._displayName = fileNameValue.trim(); return f; }
          // Plain objects (from localStorage) — update name directly
          return { ...f, name: fileNameValue.trim() };
        }));
      }
      setEditingFileName(null);
    };

    const saveReportName = (id) => {
      if (reportNameValue.trim()) {
        setGeneratedReports(prev => prev.map(r => r.id === id ? { ...r, name: reportNameValue.trim() } : r));
      }
      setEditingReportName(null);
    };

    const deleteConfig = (id) => setSavedConfigs(prev => prev.filter(sc => sc.id !== id));
    const deleteReport = (id) => {
      setGeneratedReports(prev => prev.filter(r => r.id !== id));
      try { localStorage.removeItem(`gnq_report_data_${id}`); } catch {}
    };
    const loadReport = (report) => {
      console.log("[GNQ] loadReport called for:", report.id);
      try {
        const data = localStorage.getItem(`gnq_report_data_${report.id}`);
        console.log("[GNQ] localStorage data found:", !!data, data ? `(${data.length} chars)` : "");
        if (!data) {
          console.warn("[GNQ] No saved data — report was generated before this feature was added");
          return;
        }
        const parsed = JSON.parse(data);
        setAssessmentData(parsed);
        // Load the linked config if available
        const linkedConfig = savedConfigs.find(sc => sc.id === report.configId);
        if (linkedConfig?.config) setTherapyConfig(linkedConfig.config);
        // Switch to report view
        reportSavedForRunRef.current = true; // prevent creating a duplicate report entry
        setFlowStep("complete");
        // Mark all sections as ready (map sec IDs to nav IDs)
        const sectionKeys = Object.keys(parsed);
        const navIds = sectionKeys.map(s => SECTION_NAV_MAP[s]).filter(Boolean);
        setReadySections(new Set(navIds));
        if (navIds.length > 0) setActiveSection(navIds[0]);
        console.log("[GNQ] Report loaded successfully, sections:", sectionKeys, "navIds:", navIds);
      } catch (e) {
        console.error("[GNQ] Failed to load report:", e);
      }
    };

    return (
      <div className="overflow-y-auto p-3 flex flex-col gap-3">
        {/* Uploaded Documents */}
        <div
          className={`border rounded-lg overflow-hidden relative transition-colors ${isDragOver ? "border-cyan-400/60 bg-cyan-500/5" : "border-cyan-500/20"}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false); }}
          onDrop={(e) => { e.preventDefault(); setIsDragOver(false); addFiles(Array.from(e.dataTransfer.files)); }}
        >
          {isDragOver && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center border-2 border-dashed border-cyan-400/60 rounded-lg bg-slate-900/80 pointer-events-none">
              <Upload size={20} className="text-cyan-400 mb-1.5" />
              <span className="text-xs text-cyan-400 font-medium">Drop files here</span>
              <span className="text-[10px] text-slate-400 mt-0.5">PDF, DOCX, PNG, JPG — max 20MB</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 px-2.5 py-2 bg-slate-800/50 border-b border-cyan-500/20">
            <button onClick={() => togglePanelSection("docs")} className="flex items-center gap-1.5 flex-1 hover:opacity-80 transition-opacity">
              <FileText size={11} className="text-slate-400" />
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Uploaded Documents</span>
              {visibleFiles.length > 0 && <span className="text-[9px] text-slate-600">({visibleFiles.length})</span>}
              {panelSections.docs ? <ChevronDown size={11} className="text-slate-600 ml-auto" /> : <ChevronRight size={11} className="text-slate-600 ml-auto" />}
            </button>
            <label className="text-[9px] text-slate-400 hover:text-slate-200 border border-slate-600/50 hover:border-slate-500 rounded px-1.5 py-0.5 flex items-center gap-0.5 flex-shrink-0 whitespace-nowrap transition-colors cursor-pointer">
              <Plus size={9} /> Add file
              <input type="file" multiple accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.gif,.webp" onChange={handleFileSelect} className="hidden" />
            </label>
          </div>
          {panelSections.docs && (
            <div className="p-2">
              {visibleFiles.length === 0 ? (
                <p className="text-[10px] text-slate-600 italic px-1 py-1">No documents yet. Add files or drag them here.</p>
              ) : (
                <div>
                  {visibleFiles.map((f, i) => (
                    <div key={f.name + "-" + i} className="flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-slate-800/50 transition-colors group min-w-0">
                      <FileText size={11} className="text-slate-500 flex-shrink-0" />
                      {editingFileName === f ? (
                        <input
                          autoFocus
                          value={fileNameValue}
                          onChange={e => setFileNameValue(e.target.value)}
                          onBlur={() => saveFileName(f)}
                          onKeyDown={e => { if (e.key === "Enter") saveFileName(f); if (e.key === "Escape") setEditingFileName(null); }}
                          className="text-[10px] bg-slate-700 border border-cyan-500/40 rounded px-1 py-0.5 text-slate-200 flex-1 min-w-0 outline-none"
                        />
                      ) : (
                        <button
                          onClick={() => { setEditingFileName(f); setFileNameValue(f._displayName || f.name); }}
                          className="text-[10px] font-medium text-slate-300 hover:text-cyan-300 flex items-center gap-1 truncate flex-1 min-w-0 text-left"
                        >
                          <span className="truncate">{f._displayName || f.name}</span>
                          <Edit2 size={8} className="text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        </button>
                      )}
                      {f.datetime && (
                        <span className="text-[9px] text-slate-600 flex-shrink-0 whitespace-nowrap">{new Date(f.datetime).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      )}
                      {f instanceof File && (
                        <button onClick={() => downloadFile(f)} title="Download file" className="text-slate-500 hover:text-cyan-400 transition-colors flex-shrink-0 p-0.5">
                          <Download size={11} />
                        </button>
                      )}
                      <button onClick={() => removeFile(f)} title="Delete" className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 p-0.5">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Configuration History */}
        <div className="border border-cyan-500/20 rounded-lg overflow-hidden">
          <button
            onClick={() => togglePanelSection("configs")}
            className="w-full flex items-center justify-between gap-1.5 px-2.5 py-2 bg-slate-800/50 border-b border-cyan-500/20 hover:bg-slate-800/80 transition-colors"
          >
            <div className="flex items-center gap-1.5">
              <GitBranch size={11} className="text-slate-400" />
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Saved Inputs</span>
              {visibleConfigs.length > 0 && <span className="text-[9px] text-slate-600">({visibleConfigs.length})</span>}
            </div>
            {panelSections.configs ? <ChevronDown size={11} className="text-slate-600" /> : <ChevronRight size={11} className="text-slate-600" />}
          </button>
          {panelSections.configs && (
            <div className="p-2">
              {visibleConfigs.length === 0 ? (
                <p className="text-[10px] text-slate-600 italic px-1 py-1">Inputs will appear here after each assessment run.</p>
              ) : (
                <div>
                  {visibleConfigs.map(sc => (
                    <div key={sc.id} className={`flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-slate-800/50 transition-colors group min-w-0 ${sc.isDraft ? "opacity-70" : ""}`}>
                      {sc.isDraft && <span className="text-[8px] bg-slate-700 text-slate-500 px-1 rounded flex-shrink-0">draft</span>}
                      {editingConfigName === sc.id ? (
                        <input
                          autoFocus
                          value={configNameValue}
                          onChange={e => setConfigNameValue(e.target.value)}
                          onBlur={() => saveConfigName(sc.id)}
                          onKeyDown={e => { if (e.key === "Enter") saveConfigName(sc.id); if (e.key === "Escape") setEditingConfigName(null); }}
                          className="text-[10px] bg-slate-700 border border-cyan-500/40 rounded px-1 py-0.5 text-slate-200 w-28 outline-none flex-shrink-0"
                        />
                      ) : (
                        <button
                          onClick={() => { setEditingConfigName(sc.id); setConfigNameValue(sc.name); }}
                          title={configDiffs[sc.id] || summarizeConfig(sc.config)}
                          className={`text-[10px] font-medium flex items-center gap-1 flex-shrink-0 whitespace-nowrap ${sc.isDraft ? "text-slate-400 hover:text-slate-300" : "text-cyan-400 hover:text-cyan-300"}`}
                        >
                          {sc.name}<Edit2 size={8} className="text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      )}
                      <span className="text-[9px] text-slate-600 flex-shrink-0 whitespace-nowrap ml-auto">{new Date(sc.datetime).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      <button
                        onClick={() => restoreConfig(sc.config, sc.name)}
                        title="Restore this input"
                        className="text-slate-500 hover:text-cyan-400 transition-colors flex-shrink-0 p-0.5"
                      >
                        <RotateCcw size={10} />
                      </button>
                      <button
                        onClick={() => deleteConfig(sc.id)}
                        title="Delete"
                        className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Generated Reports */}
        <div className="border border-cyan-500/20 rounded-lg overflow-hidden">
          <button
            onClick={() => togglePanelSection("reports")}
            className="w-full flex items-center justify-between gap-1.5 px-2.5 py-2 bg-slate-800/50 border-b border-cyan-500/20 hover:bg-slate-800/80 transition-colors"
          >
            <div className="flex items-center gap-1.5">
              <Activity size={11} className="text-slate-400" />
              <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Generated Reports</span>
              {visibleReports.length > 0 && <span className="text-[9px] text-slate-600">({visibleReports.length})</span>}
            </div>
            {panelSections.reports ? <ChevronDown size={11} className="text-slate-600" /> : <ChevronRight size={11} className="text-slate-600" />}
          </button>
          {panelSections.reports && (
            <div className="p-2">
              {visibleReports.length === 0 ? (
                <p className="text-[10px] text-slate-600 italic px-1 py-1">Reports will appear here after your assessment runs.</p>
              ) : (
                <div>
                  {visibleReports.map(r => {
                    const linkedConfig = savedConfigs.find(sc => sc.id === r.configId);
                    return (
                    <div key={r.id} className="flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-slate-800/50 transition-colors group min-w-0">
                      {/* Left: report name (editable) — fixed width for alignment */}
                      <div className="w-[140px] flex-shrink-0 flex items-center gap-1">
                      {editingReportName === r.id ? (
                        <input
                          autoFocus
                          value={reportNameValue}
                          onChange={e => setReportNameValue(e.target.value)}
                          onBlur={() => saveReportName(r.id)}
                          onKeyDown={e => { if (e.key === "Enter") saveReportName(r.id); if (e.key === "Escape") setEditingReportName(null); }}
                          className="text-[10px] bg-slate-700 border border-cyan-500/40 rounded px-1 py-0.5 text-slate-200 w-28 outline-none"
                        />
                      ) : (<>
                        <button
                          onClick={() => loadReport(r)}
                          title="View report"
                          className="text-[10px] font-medium text-slate-300 hover:text-cyan-300 flex items-center gap-1 whitespace-nowrap truncate"
                        >
                          <Eye size={10} className="text-slate-500 group-hover:text-cyan-400 transition-colors flex-shrink-0" />
                          {r.name}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingReportName(r.id); setReportNameValue(r.name); }}
                          title="Rename"
                          className="text-slate-600 opacity-0 group-hover:opacity-100 hover:text-cyan-300 transition-all flex-shrink-0 p-0.5"
                        >
                          <Edit2 size={8} />
                        </button>
                      </>)}
                      </div>
                      {/* Center: linked input name — left-aligned at midpoint */}
                      <span className="flex-1 flex justify-start pl-[30%] min-w-0">
                        {linkedConfig && (
                          <span
                            title={summarizeConfig(linkedConfig.config)}
                            className="text-[9px] text-cyan-400/70 whitespace-nowrap truncate"
                          >{linkedConfig.name}</span>
                        )}
                      </span>
                      {/* Right: date + actions */}
                      <span className="flex-1 flex items-center justify-end gap-1 min-w-0">
                        <span className="text-[9px] text-slate-600 whitespace-nowrap">{new Date(r.datetime).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        <button onClick={exportReport} title="Download report" className="text-slate-500 hover:text-cyan-400 transition-colors flex-shrink-0 p-0.5">
                          <Download size={11} />
                        </button>
                        <button
                          onClick={() => deleteReport(r.id)}
                          title="Delete"
                          className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 p-0.5"
                        >
                          <X size={10} />
                        </button>
                      </span>
                    </div>
                  ); })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Clear Collection button moved to header */}
      </div>
    );
  };

  // ─── CHAT WITH ME PANEL (right panel during Phase 2) ───
  const renderChatPanel = () => (
    <>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {chatMessages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
            {m.role === "assistant" && <Bot size={14} className="text-cyan-400 mt-1 flex-shrink-0" />}
            <div className={`text-xs p-2 rounded-lg max-w-[90%] ${m.role === "user" ? "bg-cyan-600 text-white" : isDark ? "bg-slate-800/80 border border-cyan-500/20 text-slate-200" : "bg-slate-50 border border-slate-300 text-slate-700"}`}>
              {m.content ? (m.role === "assistant" ? renderChatMarkdown(m.content) : m.content) : (chatLoading && i === chatMessages.length - 1 ? (
                <span className="flex items-center gap-1 text-cyan-400"><Loader2 size={10} className="animate-spin" /> Thinking...</span>
              ) : m.content)}
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      <div className={`p-3 border-t flex gap-2 ${isDark ? "border-cyan-500/20" : "border-slate-300"}`}>
        <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === "Enter" && sendChat()} placeholder={chatLoading ? "Waiting for response..." : "Ask about the assessment..."} disabled={chatLoading} className={`flex-1 text-xs border rounded px-2 py-1.5 disabled:opacity-50 ${isDark ? "bg-slate-800/50 border-cyan-500/20 text-slate-200 placeholder:text-slate-500" : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400"}`} />
        <button onClick={sendChat} disabled={chatLoading} className="bg-cyan-600 text-white rounded px-2 hover:bg-cyan-500 disabled:opacity-50"><Send size={14} /></button>
      </div>
    </>
  );

  // ─── LEFT PANEL ───
  const renderLeftPanel = () => {
    const tc = therapyConfig;

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-3">
          {/* ── STATE 1: Query — Collected so far (mirrors Review panel) ── */}
          {flowStep === "query" && (() => {
            // Show active config — therapyConfig after handoff, collectedConfig during onboarding
            const cc = therapyConfig || collectedConfig || {};
            // Helper: mutate the active config
            const updateCC = (fn) => {
              setActiveConfig(prev => { const draft = JSON.parse(JSON.stringify(prev || {})); fn(draft); return draft; });
            };
            const deleteCCEndpoint = (cat, i) => updateCC(d => { d.clinical_endpoints[cat].splice(i, 1); });
            const deleteCCCompetitor = (i) => updateCC(d => { d.competitive_landscape.competitors.splice(i, 1); });
            const deleteCCPipelineThreat = (i) => updateCC(d => { d.competitive_landscape.pipeline_threats.splice(i, 1); });
            const deleteCCDoseLevel = (i) => updateCC(d => { d.dose_config.therapy_dose_levels.splice(i, 1); });
            const deleteCCComparatorDose = (i) => updateCC(d => { d.dose_config.comparators.splice(i, 1); });
            const addCCEndpoint = (cat) => {
              const idx = (cc?.clinical_endpoints?.[cat] || []).length;
              updateCC(d => { (d.clinical_endpoints = d.clinical_endpoints || {}); (d.clinical_endpoints[cat] = d.clinical_endpoints[cat] || []); d.clinical_endpoints[cat].push({ name: "", type: "secondary", enabled: true }); });
              setEditingField(`cc_ep_${cat}_${idx}_name`); setEditValue("");
            };
            const commitTherapyDose = () => {
              const component_doses = {};
              newTherapyComponents.forEach(c => { if (c.name.trim()) component_doses[c.name.trim()] = c.dose.trim(); });
              updateCC(d => { (d.dose_config = d.dose_config || {}); (d.dose_config.therapy_dose_levels = d.dose_config.therapy_dose_levels || []); d.dose_config.therapy_dose_levels.push({ route: newTherapyRoute.trim(), component_doses }); });
              setAddingTherapyDose(false); setNewTherapyRoute(""); setNewTherapyComponents([{ name: "", dose: "" }]);
            };
            const commitComparator = () => {
              const doses = newCompDoses.split(",").map(s => s.trim()).filter(Boolean);
              updateCC(d => { (d.dose_config = d.dose_config || {}); (d.dose_config.comparators = d.dose_config.comparators || []); d.dose_config.comparators.push({ name: newCompName.trim(), route: newCompRoute.trim(), doses }); });
              setAddingComparator(false); setNewCompName(""); setNewCompRoute(""); setNewCompDoses("");
            };
            const addCCCompetitor = () => {
              const idx = (cc?.competitive_landscape?.competitors || []).length;
              updateCC(d => { (d.competitive_landscape = d.competitive_landscape || {}); (d.competitive_landscape.competitors = d.competitive_landscape.competitors || []); d.competitive_landscape.competitors.push({ name: "", threat_level: "medium" }); });
              setEditingField(`cc_competitor_${idx}`); setEditValue("");
            };
            const cycleCCCompetitorTier = (i) => updateCC(d => {
              const c = d.competitive_landscape.competitors[i];
              const levels = ["high", "medium", "low"];
              c.threat_level = levels[(levels.indexOf(c.threat_level || "medium") + 1) % 3];
            });
            const addCCPipelineThreat = () => {
              const idx = (cc?.competitive_landscape?.pipeline_threats || []).length;
              updateCC(d => { (d.competitive_landscape = d.competitive_landscape || {}); (d.competitive_landscape.pipeline_threats = d.competitive_landscape.pipeline_threats || []); d.competitive_landscape.pipeline_threats.push({ name: "", stage: "Phase 2" }); });
              setEditingField(`cc_pipeline_${idx}`); setEditValue("");
            };
            // Inline-edit helper: key encodes what to update
            const saveCCEdit = (key, val) => {
              const v = val.trim();
              if (v) {
                if (/^cc_scenario_(dose|route|frequency|duration)_\d+$/.test(key)) {
                  const parts = key.split("_"); const field = parts[2]; const i = +parts[3];
                  updateCC(d => { d.dose_scenarios[i][field] = v; });
                } else if (/^cc_ep_(efficacy|safety)_(\d+)_name$/.test(key)) {
                  const parts = key.split("_"); const cat = parts[2]; const idx = +parts[3]; updateCC(d => { d.clinical_endpoints = d.clinical_endpoints || {}; d.clinical_endpoints[cat] = d.clinical_endpoints[cat] || []; if (d.clinical_endpoints[cat][idx]) d.clinical_endpoints[cat][idx].name = v; });
                } else if (/^cc_dose_route_\d+$/.test(key)) {
                  const i = +key.split("_")[3]; updateCC(d => { d.dose_config.therapy_dose_levels[i].route = v; });
                } else if (/^cc_dose_comp_\d+_.+$/.test(key)) {
                  const parts = key.split("_"); const i = +parts[3]; const comp = parts.slice(4).join("_");
                  updateCC(d => { d.dose_config.therapy_dose_levels[i].component_doses[comp] = v; });
                } else if (/^cc_comp_name_\d+$/.test(key)) {
                  const i = +key.split("_")[3]; updateCC(d => { d.dose_config.comparators[i].name = v; });
                } else if (/^cc_comp_route_\d+$/.test(key)) {
                  const i = +key.split("_")[3]; updateCC(d => { d.dose_config.comparators[i].route = v; });
                } else if (/^cc_comp_doses_\d+$/.test(key)) {
                  const i = +key.split("_")[3]; updateCC(d => { d.dose_config.comparators[i].doses = v.split(",").map(s => s.trim()); });
                } else if (/^cc_comp_frequency_\d+$/.test(key)) {
                  const i = +key.split("_")[3]; updateCC(d => { d.dose_config.comparators[i].frequency = v; });
                } else if (/^cc_comp_duration_\d+$/.test(key)) {
                  const i = +key.split("_")[3]; updateCC(d => { d.dose_config.comparators[i].duration = v; });
                } else if (/^cc_competitor_\d+$/.test(key)) {
                  const i = +key.split("_")[2]; updateCC(d => { d.competitive_landscape.competitors[i].name = v; });
                } else if (/^cc_pipeline_\d+$/.test(key)) {
                  const i = +key.split("_")[2]; updateCC(d => { d.competitive_landscape.pipeline_threats[i].name = v; });
                }
              }
              setEditingField(null);
            };
            const ccEditField = (key, value, className = "text-[9px] text-slate-300") => editingField === key ? (
              <input autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
                onBlur={() => saveCCEdit(key, editValue)}
                onKeyDown={e => { if (e.key === "Enter") saveCCEdit(key, editValue); if (e.key === "Escape") setEditingField(null); }}
                className="text-[9px] bg-slate-700 border border-cyan-500/30 rounded px-1 py-0.5 text-slate-200 font-mono w-20 outline-none flex-shrink-0" />
            ) : (
              <button onClick={() => { setEditingField(key); setEditValue(value); }} className={`${className} hover:text-cyan-400 flex items-center gap-0.5 group/ef text-left w-full`}>
                {value || "—"}<Edit2 size={7} className="text-slate-600 opacity-0 group-hover/ef:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            );
            return (
              <div className="space-y-3">
                {/* 1. Therapy */}
                {renderConfigSection("therapy", "Therapy", (
                  <>
                    {renderOnboardingRow("Name", "therapy.name", cc?.therapy?.name || "")}
                    {renderOnboardingRow("Company", "therapy.company", cc?.therapy?.company || "")}
                    {renderOnboardingRow("Components", "therapy.components", cc?.therapy?.components?.join(", ") || "")}
                    {renderOnboardingRow("Comparator", "therapy.comparators.0.name", cc?.therapy?.comparators?.[0]?.name || "")}
                    {renderOnboardingRow("Comparator Active Ingredients", "therapy.comparators.0.active_ingredients", cc?.therapy?.comparators?.[0]?.active_ingredients?.join(", ") || "")}
                  </>
                ))}

                {/* 2. Indication */}
                {renderConfigSection("indication", "Indication", (
                  <>
                    {renderOnboardingRow("Disease", "indication.disease", cc?.indication?.disease || "")}
                    {renderOnboardingRow("Therapeutic Area", "indication.therapeutic_area", cc?.indication?.therapeutic_area || "")}
                  </>
                ))}

                {/* 3. Clinical Endpoints */}
                {renderConfigSection("endpoints", "Clinical Endpoints", (
                  <>
                    {(cc?.clinical_endpoints?.efficacy || []).length > 0 || (cc?.clinical_endpoints?.safety || []).length > 0 ? (
                      <>
                        <div className="text-[10px] text-cyan-400/80 font-medium mb-1">Efficacy</div>
                        {(cc?.clinical_endpoints?.efficacy || []).map((ep, i) => (
                          <div key={i} className={`flex items-center gap-1.5 py-0.5 group ${ep.enabled !== false ? "" : "opacity-50"}`}>
                            <div className="flex-1 min-w-0">{ccEditField(`cc_ep_efficacy_${i}_name`, ep.name, ep.enabled !== false ? "text-[10px] text-slate-200" : "text-[10px] text-slate-500 line-through")}</div>
                            <span className={`text-[9px] px-1 rounded flex-shrink-0 ${ep.type === "primary" ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-700 text-slate-400"}`}>{ep.type}</span>
                            <button onClick={() => deleteCCEndpoint("efficacy", i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                          </div>
                        ))}
                        <button onClick={() => addCCEndpoint("efficacy")} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors mt-0.5"><Plus size={9} />Add</button>
                        <div className="text-[10px] text-amber-400/80 font-medium mt-2 mb-1">Safety</div>
                        {(cc?.clinical_endpoints?.safety || []).map((ep, i) => (
                          <div key={i} className={`flex items-center gap-1.5 py-0.5 group ${ep.enabled !== false ? "" : "opacity-50"}`}>
                            <div className="flex-1 min-w-0">{ccEditField(`cc_ep_safety_${i}_name`, ep.name, ep.enabled !== false ? "text-[10px] text-slate-200" : "text-[10px] text-slate-500 line-through")}</div>
                            <span className={`text-[9px] px-1 rounded flex-shrink-0 ${ep.type === "primary" ? "bg-amber-500/20 text-amber-400" : "bg-slate-700 text-slate-400"}`}>{ep.type}</span>
                            <button onClick={() => deleteCCEndpoint("safety", i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                          </div>
                        ))}
                        <button onClick={() => addCCEndpoint("safety")} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-amber-400 transition-colors mt-0.5"><Plus size={9} />Add</button>
                      </>
                    ) : <span className="text-[10px] text-slate-600 italic">Not confirmed yet</span>}
                  </>
                ))}

                {/* 4. Dose Choices */}
                {renderConfigSection("doses", "Dose Choices", (
                  <>
                    {/* Dose Mode Toggle */}
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className={`text-[8px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>Mode:</span>
                      <button
                        onClick={() => setActiveConfig(prev => ({...prev, dose_mode: "fixed"}))}
                        className={`text-[8px] px-1.5 py-0.5 rounded ${(cc?.dose_mode || "fixed") === "fixed" ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" : isDark ? "text-slate-600 hover:text-slate-400" : "text-slate-500 hover:text-slate-700"}`}
                      >Fixed</button>
                      <button
                        onClick={() => setActiveConfig(prev => ({...prev, dose_mode: "optimize"}))}
                        className={`text-[8px] px-1.5 py-0.5 rounded ${cc?.dose_mode === "optimize" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : isDark ? "text-slate-600 hover:text-slate-400" : "text-slate-500 hover:text-slate-700"}`}
                      >Optimize</button>
                    </div>
                    {(cc?.dose_config?.therapy_dose_levels || []).length > 0 || (cc?.dose_scenarios || []).length > 0 ? (
                      <>
                        <div className="text-[10px] text-cyan-400/80 font-medium mb-1">Therapy: {cc?.therapy?.name}</div>
                        {/* Dose Scenarios (Scenario A, B, C) */}
                        {(cc?.dose_scenarios || []).length > 0 && (
                          <div className="mb-2">
                            {(cc.dose_scenarios).map((scenario, i) => (
                              <div key={i} className="flex items-start gap-1.5 py-0.5 group">
                                <div className="flex-1 min-w-0 space-y-0.5">
                                  <div className="text-[10px] text-cyan-400 font-medium">{scenario.id}{scenario.name ? ` (${scenario.name})` : ""}</div>
                                  <div className="flex items-center gap-1 text-[9px] text-slate-500">Dose: {ccEditField(`cc_scenario_dose_${i}`, scenario.dose || "")}</div>
                                  <div className="flex items-center gap-1 text-[9px] text-slate-500">Route: {ccEditField(`cc_scenario_route_${i}`, scenario.route || "")}</div>
                                  <div className="flex items-center gap-1 text-[9px] text-slate-500">Frequency: {ccEditField(`cc_scenario_frequency_${i}`, scenario.frequency || "")}</div>
                                  <div className="flex items-center gap-1 text-[9px] text-slate-500">Duration: {ccEditField(`cc_scenario_duration_${i}`, scenario.duration || "")}</div>
                                </div>
                                <button onClick={() => updateCC(d => { d.dose_scenarios.splice(i, 1); })} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Legacy single-dose display (only if no scenarios) */}
                        {(cc?.dose_scenarios || []).length === 0 && (cc.dose_config?.therapy_dose_levels || []).map((level, i) => (
                          <div key={i} className="flex items-start gap-1.5 py-0.5 group">
                            <div className="flex-1 min-w-0 space-y-0.5">
                              <div className="flex items-center gap-1 text-[9px] text-slate-500">Route: {ccEditField(`cc_dose_route_${i}`, level.route || "")}</div>
                              {Object.entries(level.component_doses || {}).map(([comp, dose]) => (
                                <div key={comp} className="flex items-center gap-1 text-[9px] text-slate-500">{comp}: {ccEditField(`cc_dose_comp_${i}_${comp}`, String(dose || ""))}</div>
                              ))}
                            </div>
                            <button onClick={() => deleteCCDoseLevel(i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                          </div>
                        ))}
                        {/* Add Therapy inline form */}
                        {addingTherapyDose ? (
                          <div className="mt-0.5 space-y-0.5">
                            <div className="flex items-center gap-1 text-[9px] text-slate-500">
                              <span className="w-12 flex-shrink-0">Route</span>
                              <input autoFocus value={newTherapyRoute} onChange={e => setNewTherapyRoute(e.target.value)}
                                className="flex-1 text-[9px] bg-transparent border-b border-slate-700 text-slate-200 outline-none placeholder-slate-700"
                                placeholder="e.g. oral" />
                            </div>
                            {newTherapyComponents.map((c, i) => (
                              <div key={i} className="flex items-center gap-1 text-[9px] text-slate-500">
                                <input value={c.name} onChange={e => setNewTherapyComponents(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                                  className="flex-1 bg-transparent border-b border-slate-700 text-slate-200 outline-none placeholder-slate-700"
                                  placeholder="Component" />
                                <span>:</span>
                                <input value={c.dose} onChange={e => setNewTherapyComponents(prev => prev.map((x, j) => j === i ? { ...x, dose: e.target.value } : x))}
                                  className="w-12 bg-transparent border-b border-slate-700 text-slate-200 outline-none placeholder-slate-700"
                                  placeholder="dose" />
                                <button onClick={() => setNewTherapyComponents(prev => prev.filter((_, j) => j !== i))} className="text-slate-700 hover:text-red-400 flex-shrink-0"><X size={7} /></button>
                              </div>
                            ))}
                            <div className="flex items-center gap-2">
                              <button onClick={() => setNewTherapyComponents(prev => [...prev, { name: "", dose: "" }])}
                                className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors"><Plus size={7} />Component</button>
                              <button onClick={commitTherapyDose} className="text-[9px] text-cyan-400 hover:text-cyan-300 transition-colors">Save</button>
                              <button onClick={() => { setAddingTherapyDose(false); setNewTherapyRoute(""); setNewTherapyComponents([{ name: "", dose: "" }]); }}
                                className="text-[9px] text-slate-600 hover:text-slate-400 transition-colors">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setAddingTherapyDose(true)} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors mt-0.5"><Plus size={9} />Add therapy</button>
                        )}
                        <div className="text-[10px] text-amber-400/80 font-medium mt-3 mb-1">Comparators</div>
                        {(cc?.dose_config?.comparators || []).map((comp, i) => (
                          <div key={i} className="flex items-start gap-1.5 py-0.5 group">
                            <div className="flex-1 min-w-0 space-y-0.5">
                              <div>{ccEditField(`cc_comp_name_${i}`, comp.name || "", "text-[10px] font-medium text-amber-400")}</div>
                              <div className="flex items-center gap-1 text-[9px] text-slate-500">Route: {ccEditField(`cc_comp_route_${i}`, comp.route || "")}</div>
                              <div className="flex items-center gap-1 text-[9px] text-slate-500">Doses: {ccEditField(`cc_comp_doses_${i}`, (comp.doses || []).join(", "))}</div>
                              <div className="flex items-center gap-1 text-[9px] text-slate-500">Frequency: {ccEditField(`cc_comp_frequency_${i}`, comp.frequency || "")}</div>
                              <div className="flex items-center gap-1 text-[9px] text-slate-500">Duration: {ccEditField(`cc_comp_duration_${i}`, comp.duration || "")}</div>
                            </div>
                            <button onClick={() => deleteCCComparatorDose(i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                          </div>
                        ))}
                        {/* Add Comparator inline form */}
                        {addingComparator ? (
                          <div className="mt-0.5 space-y-0.5">
                            {[["Name", newCompName, setNewCompName, "e.g. Morphine", true], ["Route", newCompRoute, setNewCompRoute, "e.g. intravenous", false], ["Doses", newCompDoses, setNewCompDoses, "e.g. 10mg, 20mg", false]].map(([label, val, setter, ph, af]) => (
                              <div key={label} className="flex items-center gap-1 text-[9px] text-slate-500">
                                <span className="w-12 flex-shrink-0">{label}</span>
                                <input autoFocus={af} value={val} onChange={e => setter(e.target.value)}
                                  className="flex-1 bg-transparent border-b border-slate-700 text-slate-200 outline-none placeholder-slate-700"
                                  placeholder={ph} />
                              </div>
                            ))}
                            <div className="flex gap-2">
                              <button onClick={commitComparator} className="text-[9px] text-amber-400 hover:text-amber-300 transition-colors">Save</button>
                              <button onClick={() => { setAddingComparator(false); setNewCompName(""); setNewCompRoute(""); setNewCompDoses(""); }}
                                className="text-[9px] text-slate-600 hover:text-slate-400 transition-colors">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => setAddingComparator(true)} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-amber-400 transition-colors mt-0.5"><Plus size={9} />Add comparator</button>
                        )}
                      </>
                    ) : <span className="text-[10px] text-slate-600 italic">Not confirmed yet</span>}
                  </>
                ))}

                {/* 5. Competitive Landscape */}
                {renderConfigSection("competitive", "Competitive Landscape", (
                  <>
                    {(cc?.competitive_landscape?.competitors || []).length > 0 ? (
                      <>
                        <div className="text-[10px] text-slate-500 mb-1 font-medium">Approved Competitors <span className="text-slate-600 font-normal">(click to cycle importance)</span></div>
                        {(cc.competitive_landscape.competitors).map((c, i) => {
                          const level = c.threat_level || "medium";
                          const levelStyle = level === "high" ? "bg-red-500/20 text-red-400" : level === "medium" ? "bg-amber-500/20 text-amber-400" : "bg-slate-600/40 text-slate-500";
                          return (
                            <div key={i} className={`flex items-center gap-1.5 py-0.5 group ${level === "low" ? "opacity-50" : ""}`}>
                              <div className="flex-1 min-w-0">{ccEditField(`cc_competitor_${i}`, c.name, level === "low" ? "text-[10px] text-slate-500" : "text-[10px] text-slate-300")}</div>
                              <button onClick={() => cycleCCCompetitorTier(i)} className={`text-[8px] px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 flex-shrink-0 ${levelStyle}`} title="Click to cycle: high → medium → low">{level}</button>
                              <button onClick={() => deleteCCCompetitor(i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                            </div>
                          );
                        })}
                        <div className="flex items-center gap-2 mt-0.5">
                          <button onClick={addCCCompetitor} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors"><Plus size={9} />Add competitor</button>
                          <span className="text-[8px] text-slate-600">max 3 high, 5 medium</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-2 mb-1 font-medium">Pipeline Threats</div>
                        {(cc.competitive_landscape.pipeline_threats || []).map((p, i) => (
                          <div key={i} className="flex items-center gap-1.5 py-0.5 group">
                            <div className="flex-1 min-w-0">{ccEditField(`cc_pipeline_${i}`, p.name)}</div>
                            <span className="text-[9px] bg-slate-700 text-slate-400 px-1 rounded flex-shrink-0">{p.stage}</span>
                            <button onClick={() => deleteCCPipelineThreat(i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                          </div>
                        ))}
                        <button onClick={addCCPipelineThreat} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors mt-0.5"><Plus size={9} />Add pipeline threat</button>
                      </>
                    ) : <span className="text-[10px] text-slate-600 italic">Not confirmed yet</span>}
                  </>
                ))}

                {/* 6. Commercial Assumptions */}
                {renderConfigSection("commercial", "Commercial Assumptions", (
                  <>
                    {cc?.commercial_assumptions ? (
                      <>
                        <div className="text-[9px] text-slate-500 uppercase tracking-wider mt-1 mb-0.5">Market</div>
                        {renderOnboardingRow("Base Year TAM", "commercial_assumptions.tam.base_tam", cc?.commercial_assumptions?.tam?.base_tam || "")}
                        {renderOnboardingRow("Base Year", "commercial_assumptions.tam.base_year", String(cc?.commercial_assumptions?.tam?.base_year || ""))}
                        {renderOnboardingRow("TAM CAGR", "commercial_assumptions.tam.cagr", cc?.commercial_assumptions?.tam?.cagr || "")}
                        {renderOnboardingRow("Market Share (Bear)", "commercial_assumptions.market_share.bear", cc?.commercial_assumptions?.market_share?.bear || "")}
                        {renderOnboardingRow("Market Share (Base)", "commercial_assumptions.market_share.base", cc?.commercial_assumptions?.market_share?.base || "")}
                        {renderOnboardingRow("Market Share (Bull)", "commercial_assumptions.market_share.bull", cc?.commercial_assumptions?.market_share?.bull || "")}
                        <div className="text-[9px] text-slate-500 uppercase tracking-wider mt-2 mb-0.5">Pricing</div>
                        {renderOnboardingRow("Therapy Annual Cost", "commercial_assumptions.pricing.therapy_annual_cost", cc?.commercial_assumptions?.pricing?.therapy_annual_cost || "")}
                        {renderOnboardingRow("Comparator Annual Cost", "commercial_assumptions.pricing.comparator_annual_cost", cc?.commercial_assumptions?.pricing?.comparator_annual_cost || "")}
                        <div className="text-[9px] text-slate-500 uppercase tracking-wider mt-2 mb-0.5">Development</div>
                        {renderOnboardingRow("Current Stage", "commercial_assumptions.development_stage", cc?.commercial_assumptions?.development_stage || "")}
                        {renderOnboardingRow("Regulatory Pathway", "commercial_assumptions.regulatory_pathway", cc?.commercial_assumptions?.regulatory_pathway || "")}
                        {renderOnboardingRow("Success Probability", "commercial_assumptions.success_probability", cc?.commercial_assumptions?.success_probability || "")}
                        {renderOnboardingRow("Peak Year Post-Launch", "commercial_assumptions.revenue_projection.peak_year_post_launch", String(cc?.commercial_assumptions?.revenue_projection?.peak_year_post_launch || ""))}
                        <div className="text-[9px] text-slate-500 uppercase tracking-wider mt-2 mb-0.5">Valuation (rNPV)</div>
                        {renderOnboardingRow("Gross Margin (%)", "commercial_assumptions.valuation.gross_margin", cc?.commercial_assumptions?.valuation?.gross_margin || "")}
                        {renderOnboardingRow("WACC (%)", "commercial_assumptions.valuation.wacc", cc?.commercial_assumptions?.valuation?.wacc || "")}
                        {renderOnboardingRow("Development Cost ($M)", "commercial_assumptions.valuation.development_cost", cc?.commercial_assumptions?.valuation?.development_cost || "")}
                        {renderOnboardingRow("Terminal Growth (%)", "commercial_assumptions.valuation.terminal_growth_rate", cc?.commercial_assumptions?.valuation?.terminal_growth_rate || "")}
                      </>
                    ) : <span className="text-[10px] text-slate-600 italic">Not confirmed yet</span>}
                  </>
                ))}

                {/* Save Config + Start Assessment */}
                <div className="flex gap-2 mt-2">
                  <button onClick={() => {
                    const config = activeConfig;
                    if (!config || Object.keys(config).length === 0) return;
                    const therapyName = config.therapy?.name || "Config";
                    const ucId = activeUseCaseId || "";
                    const configSnapshot = JSON.stringify(config);
                    setSavedConfigs(prev => {
                      const duplicate = prev.find(sc => JSON.stringify(sc.config) === configSnapshot);
                      if (duplicate) return prev;
                      const next = [...prev, {
                        id: `cfg-${Date.now()}`,
                        version: prev.length + 1,
                        name: `${therapyName} Input v${(Math.max(0, ...prev.filter(sc => sc.name?.startsWith(therapyName)).map(sc => parseInt(sc.name?.match(/v(\d+)/)?.[1] || "0"))) + 1)}`,
                        datetime: new Date().toISOString(),
                        config: JSON.parse(configSnapshot),
                        useCaseId: ucId,
                      }];
                      return next; // no auto-deletion — user manages entries manually via X button
                    });
                  }} className="border border-cyan-500/30 text-cyan-400 rounded py-1.5 px-2 hover:bg-cyan-500/10 flex items-center gap-1 text-[10px]">
                    <Download size={10} /> Save
                  </button>
                  <button onClick={async () => {
                    const tid = (activeConfig?.therapy?.id || activeConfig?.therapy?.name || "unknown").toLowerCase().replace(/\s+/g, "-");
                    try {
                      const resp = await fetch("/api/export-config", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ therapy_config: activeConfig }),
                      });
                      if (!resp.ok) throw new Error("Export failed");
                      const blob = await resp.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${tid}_config_${new Date().toISOString().slice(0,10)}.zip`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) { console.error("Export config failed:", e); }
                  }} className="border border-slate-600 text-slate-400 rounded py-1.5 px-2 hover:bg-slate-700 flex items-center gap-1 text-[10px]" title="Export config bundle (zip)">
                    <Download size={10} /> Export
                  </button>
                  <label className="border border-slate-600 text-slate-400 rounded py-1.5 px-2 hover:bg-slate-700 flex items-center gap-1 text-[10px] cursor-pointer" title="Import config bundle (zip)">
                    {importLoading ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />} Import
                    <input type="file" accept=".zip" className="hidden" disabled={importLoading} onChange={async (e) => {
                      await handleImportConfig(e.target.files?.[0]);
                      e.target.value = "";
                    }} />
                  </label>
                  {importError && (
                    <div className="col-span-full text-[10px] text-red-400 bg-red-900/20 border border-red-700/30 rounded px-2 py-1 flex items-center gap-1">
                      <AlertTriangle size={10} className="flex-shrink-0" />
                      <span className="break-all">{importError}</span>
                      <button onClick={() => setImportError(null)} className="ml-auto flex-shrink-0 text-red-500 hover:text-red-300"><X size={10} /></button>
                    </div>
                  )}
                  <button onClick={handleConfirmAndStart} className="flex-1 bg-cyan-600 text-white rounded py-1.5 text-xs font-medium hover:bg-cyan-500 flex items-center justify-center gap-1">
                    <Play size={12} /> Run
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ── STATE 2: Config Prep (agent animation) ── */}
          {flowStep === "configPrep" && (
            <div className="space-y-3">
              {/* Query echo */}
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-3">
                <div className="text-[10px] text-cyan-400 font-medium mb-1">YOUR QUERY</div>
                <p className="text-xs text-slate-300">{queryData.originalQuery}</p>
              </div>
              {/* Config agent progress */}
              <div className="bg-slate-800/50 border border-cyan-500/20 rounded-lg p-3 space-y-2">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Researching Your Therapy</div>
                {[["query_parsing", "Confirming therapy & indication", Zap], ["endpoint_discovery", "Discovering clinical endpoints", Activity], ["competitive_scan", "Mapping competitive landscape", TrendingUp], ["commercial_params", "Estimating market & pricing", DollarSign]].map(([key, label, Icon]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Icon size={12} className="text-cyan-400/60" /><span className="text-xs text-slate-300">{label}</span></div>
                    <AgentBadge status={configPrepStatuses[key] || "pending"} />
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 text-center animate-pulse">Preparing results for your review...</p>
            </div>
          )}

          {/* ── STATE 2.5: Document Insights ── */}
          {flowStep === "documentInsights" && (
            <div className="space-y-3">
              {/* Header */}
              <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-purple-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FileUp size={14} className="text-purple-400" />
                  <span className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider">Document Insights</span>
                  <span className="text-[10px] text-slate-500 ml-auto">{documentInsights.length} insight{documentInsights.length !== 1 ? "s" : ""} extracted</span>
                </div>
                {documentSummary && <p className="text-[10px] text-slate-400 mt-1">{documentSummary}</p>}
              </div>

              {/* Error state */}
              {documentAnalysisError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={12} className="text-red-400" />
                    <span className="text-[10px] text-red-400">Analysis error: {documentAnalysisError}</span>
                  </div>
                  <button onClick={() => { setDocumentAnalysisError(null); setFlowStep("review"); }} className="text-[10px] text-slate-400 underline mt-1">Skip and continue to review</button>
                </div>
              )}

              {/* Empty state */}
              {!documentAnalysisError && documentInsights.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-xs text-slate-500">No config-relevant insights found in the uploaded documents.</p>
                  <button onClick={() => setFlowStep("review")} className="mt-2 text-[10px] text-cyan-400 underline">Continue to Review</button>
                </div>
              )}

              {/* Insights grouped by category */}
              {Object.entries(documentInsights.reduce((acc, ins) => { if (!acc[ins.category]) acc[ins.category] = []; acc[ins.category].push(ins); return acc; }, {})).map(([category, insights]) => (
                <div key={category} className="border border-cyan-500/20 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-slate-800/50">
                    <span className="text-xs font-semibold text-slate-300">{CATEGORY_LABELS[category] || category}</span>
                    <span className="text-[9px] text-slate-500 ml-2">({insights.length})</span>
                  </div>
                  <div className="px-3 py-2 space-y-2 bg-slate-900/30">
                    {insights.map(insight => (
                      <InsightCard
                        key={insight.id}
                        insight={insight}
                        decision={insightDecisions[insight.id] || "accepted"}
                        modification={insightModifications[insight.id]}
                        onAccept={() => setInsightDecisions(prev => ({ ...prev, [insight.id]: "accepted" }))}
                        onReject={() => setInsightDecisions(prev => ({ ...prev, [insight.id]: "rejected" }))}
                        onModify={(val) => { setInsightDecisions(prev => ({ ...prev, [insight.id]: "modified" })); setInsightModifications(prev => ({ ...prev, [insight.id]: val })); }}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* Unmapped findings */}
              {unmappedFindings.length > 0 && (
                <div className="border border-slate-600/30 rounded-lg p-3">
                  <div className="text-[10px] text-slate-500 font-medium mb-1">Additional Findings (not mapped to config)</div>
                  {unmappedFindings.map((f, i) => <div key={i} className="text-[9px] text-slate-400 ml-2 mb-0.5">• {f}</div>)}
                </div>
              )}

              {/* Actions */}
              {documentInsights.length > 0 && (
                <div className="flex gap-2">
                  <button onClick={applyInsightsAndContinue} className="flex-1 bg-cyan-600 text-white rounded-lg py-2.5 font-medium hover:bg-cyan-500 flex items-center justify-center gap-2">
                    <Check size={16} /> Apply & Continue to Review
                  </button>
                  <button onClick={() => setFlowStep("review")} className="bg-slate-700 text-slate-300 rounded-lg py-2.5 px-4 font-medium hover:bg-slate-600 text-xs">
                    Skip All
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── STATE 3: Review (accordion with editable commercial) ── */}
          {flowStep === "review" && tc && (
            <div className="space-y-3">
              {/* Pipeline error banner — shown after a failed run */}
              {pipelineError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle size={13} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-semibold text-red-400 uppercase tracking-wider mb-1">Run Failed</div>
                    <p className="text-[10px] text-red-300 break-all font-mono">{pipelineError}</p>
                  </div>
                  <button onClick={() => setPipelineError(null)} className="text-red-500 hover:text-red-300 flex-shrink-0"><X size={11} /></button>
                </div>
              )}
              {/* Impact Analysis (only on re-run when a report already exists) */}
              {isRerun && originalConfig && filteredReports.length > 0 && (() => {
                const liveChanges = computeConfigDiff(originalConfig, therapyConfig);
                if (liveChanges.length === 0) return null;
                const liveAffected = deriveAffectedSections(liveChanges);
                const changedCats = [...new Set(liveChanges.map(c => c.category))];
                return (
                  <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Zap size={14} className="text-amber-400" />
                      <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Impact Analysis</span>
                    </div>
                    <div className="space-y-1">
                      <div className="text-[9px] text-slate-500 uppercase tracking-wider">Changes Detected</div>
                      {changedCats.map(cat => {
                        const catChanges = liveChanges.filter(c => c.category === cat);
                        return (
                          <div key={cat} className="bg-slate-800/50 rounded px-2 py-1.5">
                            <div className="text-[10px] font-medium text-amber-400">{CATEGORY_LABELS[cat] || cat}</div>
                            {catChanges.slice(0, 3).map((ch, ci) => (
                              <div key={ci} className="text-[9px] text-slate-400 ml-2">{ch.summary}</div>
                            ))}
                            {catChanges.length > 3 && <div className="text-[9px] text-slate-500 ml-2">+{catChanges.length - 3} more</div>}
                          </div>
                        );
                      })}
                    </div>
                    <div className="space-y-1">
                      <div className="text-[9px] text-slate-500 uppercase tracking-wider">Sections to Update ({liveAffected.length} of 11)</div>
                      <div className="flex flex-wrap gap-1">
                        {liveAffected.map(s => <span key={s} className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded border border-amber-500/30">{SECTION_LABELS[s]}</span>)}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* 1. Therapy */}
              {renderConfigSection("therapy", "Therapy", (
                <>
                  {renderEditableRow("Name", "therapy.name", tc.therapy?.name || "")}
                  {renderEditableRow("Company", "therapy.company", tc.therapy?.company || "")}
                  {renderEditableRow("Components", "therapy.components", tc.therapy?.components?.join(", ") || "")}
                  {renderEditableRow("Comparator", "therapy.comparators.0.name", tc.therapy?.comparators?.[0]?.name || "")}
                  {renderEditableRow("Comparator Active Ingredients", "therapy.comparators.0.active_ingredients", tc.therapy?.comparators?.[0]?.active_ingredients?.join(", ") || "")}
                </>
              ), false)}

              {/* 2. Indication */}
              {renderConfigSection("indication", "Indication", (
                <>
                  {renderEditableRow("Disease", "indication.disease", tc.indication?.disease || "")}
                  {renderEditableRow("Therapeutic Area", "indication.therapeutic_area", tc.indication?.therapeutic_area || "")}
                </>
              ), false)}

              {/* 3. Clinical Endpoints */}
              {renderConfigSection("endpoints", "Clinical Endpoints", (
                <>
                  <div className="text-[10px] text-cyan-400/80 font-medium mb-1">Efficacy</div>
                  {(tc.clinical_endpoints?.efficacy || []).map((ep, i) => (
                    <div key={i} className={`flex items-center gap-1.5 py-0.5 group ${ep.enabled !== false ? "" : "opacity-50"}`}>
                      <div className="flex-1 min-w-0">{tcEditField(`tc_ep_efficacy_${i}_name`, ep.name, ep.enabled !== false ? "text-[10px] text-slate-200" : "text-[10px] text-slate-500 line-through")}</div>
                      <span className={`text-[9px] px-1 rounded flex-shrink-0 ${ep.type === "primary" ? "bg-cyan-500/20 text-cyan-400" : "bg-slate-700 text-slate-400"}`}>{ep.type}</span>
                      {ep._fromDocument && <span className="text-[7px] bg-purple-500/20 text-purple-400 px-1 rounded border border-purple-500/30 flex-shrink-0">doc</span>}
                      <button onClick={() => deleteTCEndpoint("efficacy", i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                    </div>
                  ))}
                  <button onClick={() => addTCEndpoint("efficacy")} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors mt-0.5"><Plus size={9} />Add</button>
                  <div className="text-[10px] text-amber-400/80 font-medium mt-2 mb-1">Safety</div>
                  {(tc.clinical_endpoints?.safety || []).map((ep, i) => (
                    <div key={i} className={`flex items-center gap-1.5 py-0.5 group ${ep.enabled !== false ? "" : "opacity-50"}`}>
                      <div className="flex-1 min-w-0">{tcEditField(`tc_ep_safety_${i}_name`, ep.name, ep.enabled !== false ? "text-[10px] text-slate-200" : "text-[10px] text-slate-500 line-through")}</div>
                      <span className={`text-[9px] px-1 rounded flex-shrink-0 ${ep.type === "primary" ? "bg-amber-500/20 text-amber-400" : "bg-slate-700 text-slate-400"}`}>{ep.type}</span>
                      {ep._fromDocument && <span className="text-[7px] bg-purple-500/20 text-purple-400 px-1 rounded border border-purple-500/30 flex-shrink-0">doc</span>}
                      <button onClick={() => deleteTCEndpoint("safety", i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                    </div>
                  ))}
                  <button onClick={() => addTCEndpoint("safety")} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-amber-400 transition-colors mt-0.5"><Plus size={9} />Add</button>
                </>
              ))}

              {/* 4. Dose Choices */}
              {renderConfigSection("doses", "Dose Choices", (
                <>
                  {/* Dose Mode Toggle */}
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className={`text-[8px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>Mode:</span>
                    <button
                      onClick={() => updateTC(d => { d.dose_mode = "fixed"; })}
                      className={`text-[8px] px-1.5 py-0.5 rounded ${(tc.dose_mode || "fixed") === "fixed" ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" : isDark ? "text-slate-600 hover:text-slate-400" : "text-slate-500 hover:text-slate-700"}`}
                    >Fixed</button>
                    <button
                      onClick={() => updateTC(d => { d.dose_mode = "optimize"; })}
                      className={`text-[8px] px-1.5 py-0.5 rounded ${tc.dose_mode === "optimize" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : isDark ? "text-slate-600 hover:text-slate-400" : "text-slate-500 hover:text-slate-700"}`}
                    >Optimize</button>
                  </div>
                  <div className="text-[10px] text-cyan-400/80 font-medium mb-1">Therapy: {tc.therapy?.name}</div>
                  {/* Dose Scenarios (Scenario A, B, C) */}
                  {(tc.dose_scenarios || []).length > 0 && (
                    <div className="mb-2">
                      {(tc.dose_scenarios).map((scenario, i) => (
                        <div key={i} className="flex items-start gap-1.5 py-0.5 group">
                          <div className="flex-1 min-w-0 space-y-0.5">
                            <div className="text-[10px] text-cyan-400 font-medium">{scenario.id}{scenario.name ? ` (${scenario.name})` : ""}</div>
                            <div className="flex items-center gap-1 text-[9px] text-slate-500">Dose: {tcEditField(`tc_scenario_dose_${i}`, scenario.dose || "")}</div>
                            <div className="flex items-center gap-1 text-[9px] text-slate-500">Route: {tcEditField(`tc_scenario_route_${i}`, scenario.route || "")}</div>
                            <div className="flex items-center gap-1 text-[9px] text-slate-500">Frequency: {tcEditField(`tc_scenario_frequency_${i}`, scenario.frequency || "")}</div>
                            <div className="flex items-center gap-1 text-[9px] text-slate-500">Duration: {tcEditField(`tc_scenario_duration_${i}`, scenario.duration || "")}</div>
                          </div>
                          <button onClick={() => updateTC(d => { d.dose_scenarios.splice(i, 1); })} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Legacy single-dose display (only if no scenarios) */}
                  {(tc.dose_scenarios || []).length === 0 && (tc.dose_config?.therapy_dose_levels || []).map((level, li) => (
                    <div key={li} className="flex items-start gap-1.5 py-0.5 group">
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <div className="flex items-center gap-1 text-[9px] text-slate-500">Route: {tcEditField(`tc_dose_route_${li}`, level.route || "")}</div>
                        {Object.entries(level.component_doses || {}).map(([comp, dose]) => (
                          <div key={comp} className="flex items-center gap-1 text-[9px] text-slate-500">{comp}: {tcEditField(`tc_dose_comp_${li}_${comp}`, String(dose || ""))}</div>
                        ))}
                      </div>
                      <button onClick={() => removeTherapyDoseLevel(li)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                    </div>
                  ))}
                  {addingTherapyDose ? (
                    <div className="mt-0.5 space-y-0.5">
                      <div className="flex items-center gap-1 text-[9px] text-slate-500">
                        <span className="w-12 flex-shrink-0">Route</span>
                        <input autoFocus value={newTherapyRoute} onChange={e => setNewTherapyRoute(e.target.value)}
                          className="flex-1 text-[9px] bg-transparent border-b border-slate-700 text-slate-200 outline-none placeholder-slate-700" placeholder="e.g. oral" />
                      </div>
                      {newTherapyComponents.map((c, i) => (
                        <div key={i} className="flex items-center gap-1 text-[9px] text-slate-500">
                          <input value={c.name} onChange={e => setNewTherapyComponents(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                            className="flex-1 bg-transparent border-b border-slate-700 text-slate-200 outline-none placeholder-slate-700" placeholder="Component" />
                          <span>:</span>
                          <input value={c.dose} onChange={e => setNewTherapyComponents(prev => prev.map((x, j) => j === i ? { ...x, dose: e.target.value } : x))}
                            className="w-12 bg-transparent border-b border-slate-700 text-slate-200 outline-none placeholder-slate-700" placeholder="dose" />
                          <button onClick={() => setNewTherapyComponents(prev => prev.filter((_, j) => j !== i))} className="text-slate-700 hover:text-red-400 flex-shrink-0"><X size={7} /></button>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <button onClick={() => setNewTherapyComponents(prev => [...prev, { name: "", dose: "" }])} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors"><Plus size={7} />Component</button>
                        <button onClick={commitTCTherapyDose} className="text-[9px] text-cyan-400 hover:text-cyan-300 transition-colors">Save</button>
                        <button onClick={() => { setAddingTherapyDose(false); setNewTherapyRoute(""); setNewTherapyComponents([{ name: "", dose: "" }]); }} className="text-[9px] text-slate-600 hover:text-slate-400 transition-colors">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setAddingTherapyDose(true)} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors mt-0.5"><Plus size={9} />Add therapy</button>
                  )}
                  <div className="text-[10px] text-amber-400/80 font-medium mt-3 mb-1">Comparators</div>
                  {(tc.dose_config?.comparators || []).map((comp, ci) => (
                    <div key={ci} className="flex items-start gap-1.5 py-0.5 group">
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <div>{tcEditField(`tc_comp_name_${ci}`, comp.name || "", "text-[10px] font-medium text-amber-400")}</div>
                        <div className="flex items-center gap-1 text-[9px] text-slate-500">Route: {tcEditField(`tc_comp_route_${ci}`, comp.route || "")}</div>
                        <div className="flex items-center gap-1 text-[9px] text-slate-500">Doses: {tcEditField(`tc_comp_doses_${ci}`, (comp.doses || []).join(", "))}</div>
                        <div className="flex items-center gap-1 text-[9px] text-slate-500">Frequency: {tcEditField(`tc_comp_frequency_${ci}`, comp.frequency || "")}</div>
                        <div className="flex items-center gap-1 text-[9px] text-slate-500">Duration: {tcEditField(`tc_comp_duration_${ci}`, comp.duration || "")}</div>
                      </div>
                      <button onClick={() => removeComparator(ci)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                    </div>
                  ))}
                  {addingComparator ? (
                    <div className="mt-0.5 space-y-0.5">
                      {[["Name", newCompName, setNewCompName, "e.g. Morphine", true], ["Route", newCompRoute, setNewCompRoute, "e.g. intravenous", false], ["Doses", newCompDoses, setNewCompDoses, "e.g. 10mg, 20mg", false]].map(([label, val, setter, ph, af]) => (
                        <div key={label} className="flex items-center gap-1 text-[9px] text-slate-500">
                          <span className="w-12 flex-shrink-0">{label}</span>
                          <input autoFocus={af} value={val} onChange={e => setter(e.target.value)}
                            className="flex-1 bg-transparent border-b border-slate-700 text-slate-200 outline-none placeholder-slate-700" placeholder={ph} />
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <button onClick={commitTCComparator} className="text-[9px] text-amber-400 hover:text-amber-300 transition-colors">Save</button>
                        <button onClick={() => { setAddingComparator(false); setNewCompName(""); setNewCompRoute(""); setNewCompDoses(""); }} className="text-[9px] text-slate-600 hover:text-slate-400 transition-colors">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setAddingComparator(true)} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-amber-400 transition-colors mt-0.5"><Plus size={9} />Add comparator</button>
                  )}
                </>
              ), false)}

              {/* 5. Competitive Landscape */}
              {renderConfigSection("competitive", "Competitive Landscape", (
                <>
                  <div className="text-[10px] text-slate-500 mb-1 font-medium">Approved Competitors <span className="text-slate-600 font-normal">(click to cycle importance)</span></div>
                  {(tc.competitive_landscape?.competitors || []).map((c, i) => {
                    const level = c.threat_level || "medium";
                    const levelStyle = level === "high" ? "bg-red-500/20 text-red-400" : level === "medium" ? "bg-amber-500/20 text-amber-400" : "bg-slate-600/40 text-slate-500";
                    return (
                      <div key={i} className={`flex items-center gap-1.5 py-0.5 group ${level === "low" ? "opacity-50" : ""}`}>
                        <div className="flex-1 min-w-0">{tcEditField(`tc_competitor_${i}`, c.name, level === "low" ? "text-[10px] text-slate-500" : "text-[10px] text-slate-300")}</div>
                        <button onClick={() => cycleTCCompetitorTier(i)} className={`text-[8px] px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 flex-shrink-0 ${levelStyle}`} title="Click to cycle: high → medium → low">{level}</button>
                        <button onClick={() => deleteTCCompetitor(i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                      </div>
                    );
                  })}
                  <div className="flex items-center gap-2 mt-0.5">
                    <button onClick={addTCCompetitor} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors"><Plus size={9} />Add competitor</button>
                    <span className="text-[8px] text-slate-600">max 3 high, 5 medium</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-2 mb-1 font-medium">Pipeline Threats</div>
                  {(tc.competitive_landscape?.pipeline_threats || []).map((p, i) => (
                    <div key={i} className="flex items-center gap-1.5 py-0.5 group">
                      <div className="flex-1 min-w-0">{tcEditField(`tc_pipeline_${i}`, p.name)}</div>
                      <span className="text-[9px] bg-slate-700 text-slate-400 px-1 rounded flex-shrink-0">{p.stage}</span>
                      <button onClick={() => deleteTCPipelineThreat(i)} className="text-slate-600 hover:text-red-400 transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"><X size={9} /></button>
                    </div>
                  ))}
                  <button onClick={addTCPipelineThreat} className="flex items-center gap-0.5 text-[9px] text-slate-600 hover:text-cyan-400 transition-colors mt-0.5"><Plus size={9} />Add pipeline threat</button>
                </>
              ))}

              {/* 6. Commercial Assumptions (editable) */}
              {renderConfigSection("commercial", "Commercial Assumptions", (
                <>
                  {renderEditableRow("Base Year TAM", "commercial_assumptions.tam.base_tam", tc.commercial_assumptions?.tam?.base_tam || "")}
                  {renderEditableRow("Base Year", "commercial_assumptions.tam.base_year", String(tc.commercial_assumptions?.tam?.base_year || ""))}
                  {renderEditableRow("TAM CAGR", "commercial_assumptions.tam.cagr", tc.commercial_assumptions?.tam?.cagr || "")}
                  {renderEditableRow("Market Share (Bear)", "commercial_assumptions.market_share.bear", String(tc.commercial_assumptions?.market_share?.bear || ""))}
                  {renderEditableRow("Market Share (Base)", "commercial_assumptions.market_share.base", String(tc.commercial_assumptions?.market_share?.base || ""))}
                  {renderEditableRow("Market Share (Bull)", "commercial_assumptions.market_share.bull", String(tc.commercial_assumptions?.market_share?.bull || ""))}
                  {renderEditableRow("Therapy Annual Cost", "commercial_assumptions.pricing.therapy_annual_cost", tc.commercial_assumptions?.pricing?.therapy_annual_cost || "")}
                  {renderEditableRow("Comparator Annual Cost", "commercial_assumptions.pricing.comparator_annual_cost", tc.commercial_assumptions?.pricing?.comparator_annual_cost || "")}
                  {renderEditableRow("Current Stage", "commercial_assumptions.development_stage", tc.commercial_assumptions?.development_stage || "")}
                  {renderEditableRow("Regulatory Pathway", "commercial_assumptions.regulatory_pathway", tc.commercial_assumptions?.regulatory_pathway || "")}
                  {renderEditableRow("Success Probability", "commercial_assumptions.success_probability", tc.commercial_assumptions?.success_probability || "")}
                  {renderEditableRow("Peak Year Post-Launch", "commercial_assumptions.revenue_projection.peak_year_post_launch", String(tc.commercial_assumptions?.revenue_projection?.peak_year_post_launch || ""))}
                  {renderEditableRow("Gross Margin (%)", "commercial_assumptions.valuation.gross_margin", tc.commercial_assumptions?.valuation?.gross_margin || "")}
                  {renderEditableRow("WACC (%)", "commercial_assumptions.valuation.wacc", tc.commercial_assumptions?.valuation?.wacc || "")}
                  {renderEditableRow("Dev Cost ($M)", "commercial_assumptions.valuation.development_cost", tc.commercial_assumptions?.valuation?.development_cost || "")}
                  {renderEditableRow("Terminal Growth (%)", "commercial_assumptions.valuation.terminal_growth_rate", tc.commercial_assumptions?.valuation?.terminal_growth_rate || "")}
                </>
              ), false)}

              {/* Save Config + Export/Import + Run Assessment buttons */}
              <div className="flex flex-col gap-2 mt-2">
                <div className="flex gap-2">
                  <button onClick={() => {
                    const config = activeConfig;
                    if (!config || Object.keys(config).length === 0) return;
                    const therapyName = config.therapy?.name || "Config";
                    const ucId = activeUseCaseId || "";
                    const configSnapshot = JSON.stringify(config);
                    setSavedConfigs(prev => {
                      const duplicate = prev.find(sc => JSON.stringify(sc.config) === configSnapshot);
                      if (duplicate) return prev;
                      const next = [...prev, {
                        id: `cfg-${Date.now()}`,
                        version: prev.length + 1,
                        name: `${therapyName} Input v${(Math.max(0, ...prev.filter(sc => sc.name?.startsWith(therapyName)).map(sc => parseInt(sc.name?.match(/v(\d+)/)?.[1] || "0"))) + 1)}`,
                        datetime: new Date().toISOString(),
                        config: JSON.parse(configSnapshot),
                        useCaseId: ucId,
                      }];
                      return next;
                    });
                  }} className={`flex-1 border rounded-lg py-2 font-medium flex items-center justify-center gap-1.5 text-xs ${isDark ? "border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10" : "border-cyan-400 text-cyan-600 hover:bg-cyan-50"}`}>
                    <Download size={13} /> Save
                  </button>
                  <button onClick={async () => {
                    const therapyId = (activeConfig?.therapy?.id || activeConfig?.therapy?.name || "unknown").toLowerCase().replace(/\s+/g, "-");
                    try {
                      const resp = await fetch("/api/export-config", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ therapy_config: activeConfig }),
                      });
                      if (!resp.ok) throw new Error("Export failed");
                      const blob = await resp.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${therapyId}_config_${new Date().toISOString().slice(0,10)}.zip`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) { console.error("Export failed:", e); }
                  }} className={`flex-1 border rounded-lg py-2 font-medium flex items-center justify-center gap-1.5 text-xs ${isDark ? "border-slate-600 text-slate-400 hover:bg-slate-700" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`} title="Export config bundle (zip)">
                    <Download size={13} /> Export
                  </button>
                  <label className={`flex-1 border rounded-lg py-2 font-medium flex items-center justify-center gap-1.5 text-xs cursor-pointer ${importLoading ? "opacity-50 pointer-events-none" : ""} ${isDark ? "border-slate-600 text-slate-400 hover:bg-slate-700" : "border-slate-300 text-slate-500 hover:bg-slate-50"}`} title="Import config bundle (zip)">
                    {importLoading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Import
                    <input type="file" accept=".zip" className="hidden" disabled={importLoading} onChange={async (e) => {
                      await handleImportConfig(e.target.files?.[0]);
                      e.target.value = "";
                    }} />
                  </label>
                  {importError && (
                    <div className="col-span-full mt-1 text-[10px] text-red-400 bg-red-900/20 border border-red-700/30 rounded px-2 py-1 flex items-center gap-1">
                      <AlertTriangle size={10} className="flex-shrink-0" />
                      <span className="break-all">{importError}</span>
                      <button onClick={() => setImportError(null)} className="ml-auto flex-shrink-0 text-red-500 hover:text-red-300"><X size={10} /></button>
                    </div>
                  )}
                </div>
                <button onClick={() => handleRunPipeline()} className="w-full bg-cyan-600 text-white rounded-lg py-2.5 font-medium hover:bg-cyan-500 flex items-center justify-center gap-2">
                  <Play size={16} /> Run Assessment
                </button>
              </div>
            </div>
          )}

          {/* ── STATE 4: Pipeline (agent progress) ── */}
          {flowStep === "pipeline" && (
            <div className="space-y-3">
              {/* Agent pipeline */}
              <div className="bg-slate-800/50 border border-cyan-500/20 rounded-lg p-3 space-y-2">
                <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Building Your Assessment</div>
                {(() => {
                  // Derive composite status for the dose/tissue group
                  const doseGroupStatus = (() => {
                    const tm = agentStatuses["tissue_mapping"];
                    const dsg = agentStatuses["dose_scenario_generator"];
                    const dt = agentStatuses["dose_translator"];
                    if (tm === "processing" || dsg === "processing" || dt === "processing") return "processing";
                    if (agentStatuses["causal_scoring"]) return "complete"; // next stage started
                    if (tm === "complete" || dsg === "complete" || dt === "complete") return "processing";
                    return "pending";
                  })();
                  return [
                    ["query_builder", "Configuring your input", Zap, agentStatuses["query_builder"]],
                    ["kg_api", "Analyzing biological pathways", GitBranch, agentStatuses["kg_api"]],
                    ["dose_group", "Preparing dose scenarios", Activity, doseGroupStatus],
                    ["causal_scoring", "Scoring causal pathways", TrendingUp, agentStatuses["causal_scoring"]],
                    ["section_generator", "Writing report sections", FileText, agentStatuses["section_generator"]],
                  ].map(([key, label, Icon, status]) => (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><Icon size={12} className="text-cyan-400/60" /><span className="text-xs text-slate-300">{label}</span></div>
                      <AgentBadge status={status || "pending"} />
                    </div>
                  ));
                })()}
              </div>
              <p className="text-xs text-slate-500 text-center animate-pulse">Your assessment report is being prepared...</p>
              {isRerun && affectedSections.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  <div className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider mb-1">Updating {affectedSections.length} Section{affectedSections.length !== 1 ? "s" : ""}</div>
                  <div className="flex flex-wrap gap-1">
                    {affectedSections.map(s => <span key={s} className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded border border-amber-500/30">{SECTION_LABELS[s]}</span>)}
                  </div>
                </div>
              )}
              {/* Stop button */}
              <button
                onClick={handleStopPipeline}
                className="w-full border border-slate-600/50 text-slate-500 rounded-lg py-2 text-xs font-medium hover:text-red-400/70 hover:border-red-500/20 flex items-center justify-center gap-2 transition-colors"
              >
                <X size={12} /> Stop Run
              </button>
            </div>
          )}

          {/* ── STATE 4b: Stopped ── */}
          {flowStep === "stopped" && (() => {
            const allSections = ["sec1","sec2","sec3","sec4","sec5","sec6","sec7","sec8","sec9","sec10","sec11"];
            const missingSections = allSections.filter(s => !assessmentData?.[s] || assessmentData[s].status !== "completed");
            const completedCount = allSections.length - missingSections.length;
            return (
              <div className="space-y-3">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={14} className="text-amber-400" />
                    <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Run Stopped</span>
                  </div>
                  {completedCount === 0
                    ? <p className="text-xs text-slate-400">Run stopped before any sections completed.</p>
                    : <p className="text-xs text-slate-400">{completedCount} of {allSections.length} sections completed. You can review the partial results below.</p>
                  }
                </div>
                {completedCount === 0 ? (
                  <button onClick={() => handleRunPipeline()} className="w-full bg-cyan-600 text-white rounded-lg py-2 text-xs font-medium hover:bg-cyan-500 flex items-center justify-center gap-2">
                    <Play size={12} /> Retry
                  </button>
                ) : (
                  <>
                    <button onClick={() => handleRunPipeline(missingSections, currentSessionIdRef.current)} className="w-full bg-cyan-600 text-white rounded-lg py-2 text-xs font-medium hover:bg-cyan-500 flex items-center justify-center gap-2">
                      <Play size={12} /> Complete Run
                    </button>
                    <button onClick={() => handleRunPipeline()} className="w-full text-slate-500 hover:text-slate-300 text-xs transition-colors text-center">
                      Re-run all sections
                    </button>
                  </>
                )}
              </div>
            );
          })()}

          {/* ── STATE 5: Complete ── */}
          {flowStep === "complete" && (
            <div className="space-y-3">
              {/* Success card */}
              <div className="bg-gradient-to-br from-green-500/10 to-cyan-500/10 border border-green-500/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2"><CheckCircle2 size={14} className="text-green-400" /><span className="text-[10px] font-semibold text-green-400 uppercase tracking-wider">Assessment Complete</span></div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className={themeTextMuted}>Therapy:</span><span className={`${themeTextBright} font-medium`}>{tc?.therapy?.name}</span></div>
                  <div className="flex justify-between"><span className={themeTextMuted}>Indication:</span><span className={`${themeTextBright} font-medium`}>{tc?.indication?.disease}</span></div>
                  <div className="flex justify-between"><span className={themeTextMuted}>Comparator:</span><span className={`${themeTextBright} font-medium`}>{tc?.therapy?.comparators?.[0]?.name}</span></div>
                </div>
              </div>

              {/* Re-run Comparison Summary */}
              {rerunSummary && (
                <div className="bg-gradient-to-br from-amber-500/10 to-cyan-500/10 border border-amber-500/20 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <RotateCcw size={14} className="text-amber-400" />
                    <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Update Summary</span>
                    <span className="text-[9px] text-slate-500 ml-auto">{rerunSummary.timestamp}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Changes Applied</div>
                    {rerunSummary.changedCategories.map(cat => (
                      <div key={cat} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        <span className="text-[10px] text-slate-300">{CATEGORY_LABELS[cat]}</span>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Sections Regenerated ({rerunSummary.updatedSections.length})</div>
                    <div className="flex flex-wrap gap-1">
                      {rerunSummary.updatedSections.map(s => <span key={s} className="text-[9px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded border border-green-500/30">{SECTION_LABELS[s]}</span>)}
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      </div>
    );
  };

  // ─── CONTENT RENDERER (reads from sections.json via sec() helper) ───
  const renderContent = () => {
    // Show loading during pipeline if no data yet, or if auto-navigate hasn't fired yet
    if (flowStep === "pipeline" && (!d || !activeSection)) {
      return (
        <div className="text-center py-16">
          <Loader2 size={32} className="animate-spin mx-auto text-cyan-400 mb-4" />
          <p className="text-slate-400">Running assessment pipeline...</p>
          <p className="text-xs text-slate-500 mt-1">Generating assessment sections...</p>
        </div>
      );
    }

    // Guided onboarding chat during query step
    if (flowStep === "query") {
      return (
        <div className="flex flex-col h-full" style={{height: "calc(100vh - 120px)"}}>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto space-y-3 pb-4">
            {onboardingMessages.map((msg, i) => {
              // Phase divider
              if (msg.type === "divider") {
                return (
                  <div key={i} className="flex items-center gap-2 py-2">
                    <div className="flex-1 border-t border-slate-700/50" />
                    <span className="text-[9px] text-slate-500 uppercase tracking-wider font-medium px-2">{msg.content}</span>
                    <div className="flex-1 border-t border-slate-700/50" />
                  </div>
                );
              }
              // Input selector — clickable saved input buttons
              if (msg.type === "input_selector") {
                return (
                  <div key={i} className="flex justify-start">
                    <div className="w-5 h-5 rounded-full bg-cyan-600/30 border border-cyan-500/40 flex items-center justify-center mr-1.5 mt-0.5 flex-shrink-0"><Bot size={11} className="text-cyan-400" /></div>
                    <div className="max-w-[82%] flex flex-col gap-1.5">
                      {(msg.inputs || []).map((inp) => (
                        <button
                          key={inp.id}
                          onClick={() => {
                            const sc = savedConfigs.find(c => c.id === inp.id);
                            if (sc?.config) {
                              restoreConfig(sc.config, sc.name);
                              // Mark this selector as used
                              setOnboardingMessages(prev => prev.map((m, idx) => idx === i ? { ...m, selected: inp.id } : m));
                            }
                          }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-colors ${msg.selected === inp.id
                            ? "border-cyan-500/50 bg-cyan-500/10"
                            : "border-cyan-500/20 bg-slate-800/60 hover:bg-slate-800/80 hover:border-cyan-500/40"
                          }`}
                        >
                          <RotateCcw size={11} className={msg.selected === inp.id ? "text-cyan-400" : "text-slate-500"} />
                          <div className="flex-1 min-w-0">
                            <span className={`text-[11px] font-medium ${msg.selected === inp.id ? "text-cyan-300" : "text-slate-300"}`}>{inp.name}</span>
                          </div>
                          <span className="text-[9px] text-slate-600 flex-shrink-0">{inp.datetime ? new Date(inp.datetime).toLocaleDateString([], { month: "short", day: "numeric" }) : ""}</span>
                          {msg.selected === inp.id && <Check size={11} className="text-cyan-400 flex-shrink-0" />}
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          setOnboardingMessages(prev => [...prev.filter(m => m.type !== "input_selector"), { role: "assistant", content: "Starting fresh. What company is developing " + (activeUseCase?.name || "this therapy") + "?" }]);
                          setOnboardingStep("therapy");
                          setCollectedConfig(activeUseCase?.name ? { therapy: { name: activeUseCase.name }, indication: { disease: activeUseCase?.indication || "" } } : {});
                          setExpandedConfigSections(["therapy"]);
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700/50 bg-slate-800/30 hover:bg-slate-800/60 text-left transition-colors"
                      >
                        <Plus size={11} className="text-slate-500" />
                        <span className="text-[10px] text-slate-500">Start fresh configuration</span>
                      </button>
                    </div>
                  </div>
                );
              }
              // Confirmation card
              if (msg.type === "confirmation_card") {
                return (
                  <div key={i} className="flex justify-start">
                    <div className="w-5 h-5 rounded-full bg-cyan-600/30 border border-cyan-500/40 flex items-center justify-center mr-1.5 mt-0.5 flex-shrink-0"><Bot size={11} className="text-cyan-400" /></div>
                    <div className="max-w-[82%]">
                      <ConfirmationCard
                        sectionKey={msg.sectionKey}
                        extractedData={msg.data}
                        confidence={msg.confidence || "medium"}
                        sources={msg.sources || []}
                        status={msg.status || "pending"}
                        onConfirm={(key, data) => {
                          ucStore.confirmSection(key);
                          setOnboardingMessages(prev => prev.map((m, idx) => idx === i ? { ...m, status: "confirmed" } : m));
                        }}
                        onEdit={(key) => {
                          ucStore.confirmSection(key);
                          setOnboardingMessages(prev => prev.map((m, idx) => idx === i ? { ...m, status: "modified" } : m));
                          setExpandedConfigSections(prev => [...new Set([...prev, key])]);
                        }}
                        onReject={(key) => {
                          ucStore.rejectSection(key);
                          setOnboardingMessages(prev => prev.map((m, idx) => idx === i ? { ...m, status: "rejected" } : m));
                        }}
                      />
                    </div>
                  </div>
                );
              }
              // Regular message
              return (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && <div className="w-5 h-5 rounded-full bg-cyan-600/30 border border-cyan-500/40 flex items-center justify-center mr-1.5 mt-0.5 flex-shrink-0"><Bot size={11} className="text-cyan-400" /></div>}
                <div className={`max-w-[82%] rounded-xl px-3 py-2 text-xs leading-relaxed ${msg.role === "user" ? isDark ? "bg-slate-700/80 text-slate-200 rounded-tr-sm" : "bg-cyan-600 text-white rounded-tr-sm" : isDark ? "bg-slate-800/60 border border-cyan-500/20 text-slate-200 rounded-tl-sm" : "bg-slate-50 border border-slate-300 text-slate-700 rounded-tl-sm"}`}>
                  {msg.content
                    ? (msg.role === "assistant" ? renderBotMessage(msg.content) : msg.content)
                    : (onboardingLoading && i === onboardingMessages.length - 1 ? <span className="flex gap-1 items-center"><span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay:"0ms"}} /><span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay:"150ms"}} /><span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay:"300ms"}} /></span> : "")}
                </div>
              </div>
              );
            })}
            <div ref={onboardingEndRef} />
          </div>

          {/* Attached files indicator — shown above divider when files are staged */}
          {pendingFileNames.length > 0 && (
            <div className="flex items-center gap-1.5 px-1 pb-1.5">
              <FileUp size={10} className="text-amber-400 flex-shrink-0" />
              <span className="text-[10px] text-amber-400 truncate">{pendingFileNames.join(", ")}</span>
              <button onClick={() => { pendingOnboardingFilesRef.current = []; setPendingFileNames([]); }} className="text-slate-500 hover:text-red-400 flex-shrink-0"><X size={9} /></button>
            </div>
          )}
          {/* Input area — always visible */}
          <div className="border-t border-cyan-500/20 pt-2">
            {!["therapy", "indication"].includes(onboardingStep) && onboardingStep !== "confirm" && onboardingStep !== "done" && (
              <button onClick={() => sendOnboardingMessage("skip")} className="text-[10px] text-slate-400 hover:text-cyan-400 mb-1.5 flex items-center gap-1">
                <ChevronRight size={10} /> Skip this step
              </button>
            )}
            <div
              className="flex gap-1.5 items-end"
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("ring-1", "ring-cyan-500/50", "rounded-lg"); }}
              onDragEnter={e => { e.preventDefault(); e.currentTarget.classList.add("ring-1", "ring-cyan-500/50", "rounded-lg"); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) e.currentTarget.classList.remove("ring-1", "ring-cyan-500/50", "rounded-lg"); }}
              onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("ring-1", "ring-cyan-500/50", "rounded-lg"); handleOnboardingFiles(Array.from(e.dataTransfer.files)); }}
            >
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.gif,.webp" onChange={handleFileSelect} className="hidden" />
              <input ref={onboardingFileInputRef} type="file" multiple accept=".pdf,.docx,.doc,.png,.jpg,.jpeg" onChange={e => { handleOnboardingFiles(Array.from(e.target.files)); e.target.value = ""; }} className="hidden" />
              <button onClick={() => onboardingFileInputRef.current?.click()} className={`p-1.5 hover:text-cyan-400 border rounded-lg flex-shrink-0 ${isDark ? "text-slate-400 border-slate-700 hover:border-cyan-500/40" : "text-slate-500 border-slate-300 hover:border-cyan-400"}`} title="Attach files">
                <Upload size={13} />
              </button>
              <textarea
                ref={onboardingInputRef}
                value={onboardingInput}
                onChange={e => setOnboardingInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendOnboardingMessage(onboardingInput); } }}
                placeholder="Type your answer..."
                disabled={onboardingLoading}
                rows={3}
                className={`flex-1 border rounded-lg px-2.5 py-1.5 text-xs resize-none focus:ring-1 disabled:opacity-50 ${isDark ? "bg-slate-800/50 border-cyan-500/20 text-slate-200 placeholder:text-slate-500 focus:ring-cyan-500/50 focus:border-cyan-500/50" : "bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus:ring-cyan-400/30 focus:border-cyan-400"}`}
              />
              <button onClick={() => sendOnboardingMessage(onboardingInput)} disabled={!onboardingInput.trim() || onboardingLoading} className="p-1.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white rounded-lg flex-shrink-0">
                <Send size={13} />
              </button>
            </div>
            {/* Reset Configuration */}
            {onboardingMessages.length > 1 && (
              <div className="flex justify-end mt-1.5">
                {resetConfigConfirm ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] text-slate-500">Reset chat & config for {activeUseCase?.name || "this use case"}?</span>
                    <button
                      onClick={() => {
                        setOnboardingMessages([{ role: "assistant", content: getWelcomeMessage() }]);
                        setCollectedConfig({});
                        setPreConfig(null);
                        setOnboardingStep("therapy");
                        setExpandedConfigSections(["therapy"]);
                        ucStore.clearAllChat();
                        ucStore.setConfirmedConfig({});
                        setResetConfigConfirm(false);
                      }}
                      className="text-[9px] text-red-400 hover:text-red-300 font-medium px-1.5 py-0.5 rounded hover:bg-red-500/20 transition-colors"
                    >
                      Yes, reset
                    </button>
                    <button onClick={() => setResetConfigConfirm(false)} className="text-[9px] text-slate-500 hover:text-slate-300 px-1 py-0.5 rounded hover:bg-slate-700/50 transition-colors">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setResetConfigConfirm(true)}
                    className="text-[9px] text-slate-600 hover:text-slate-400 flex items-center gap-1 transition-colors"
                  >
                    <RotateCcw size={8} /> Reset Configuration
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Show placeholder only when no report data exists yet
    if (!d) {
      return (
        <div className={`text-center py-16 ${themeTextMuted}`}>
          <Sparkles size={48} className="mx-auto mb-4 opacity-30" />
          <p className={`text-lg ${themeTextMuted}`}>GNQ InSilico Assessment Platform</p>
          <p className={`text-sm mt-2 ${themeTextMuted}`}>Enter your query to begin the assessment workflow</p>
        </div>
      );
    }

    // Data Preview
    if (showYAMLPreview) {
      return (
        <div className="space-y-4">
          <h2 className={`text-lg font-semibold ${themeTextBright}`}>Sections Data Preview</h2>
          <div className={`border rounded-lg p-4 overflow-auto max-h-[70vh] ${themeCard}`}>
            <pre className={`text-xs ${themeText} font-mono whitespace-pre-wrap`}>{JSON.stringify(d, null, 2)}</pre>
          </div>
        </div>
      );
    }

    // Helper: section agent badge
    const SectionAgentBadge = ({ nk }) => {
      const agent = SECTION_AGENTS[nk];
      if (!agent) return null;
      return (
        <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full border align-middle ${isDark ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400" : "bg-cyan-50 border-cyan-200 text-cyan-600"}`}>
          {agent}
        </span>
      );
    };

    // ── Section renderer — used by both interactive view and print view ──
    const renderSectionByNav = (navKey) => {
    if (!d) return null;

    // Helper: parse a numeric value that might be a range ("8.1-11.5") or single ("12.4")
    const parseNumericValue = (str) => {
      if (!str) return NaN;
      const s = String(str).replace(/,/g, '');
      const rangeMatch = s.match(/^(\d+\.?\d*)\s*[-–]\s*(\d+\.?\d*)/);
      if (rangeMatch) {
        const lo = parseFloat(rangeMatch[1]);
        const hi = parseFloat(rangeMatch[2]);
        return (!isNaN(lo) && !isNaN(hi)) ? (lo + hi) / 2 : lo;
      }
      return parseFloat(s);
    };

    // Commercial section helpers
    const threatScore = (t) => ({ high: 3, medium: 2, low: 1 }[t?.toLowerCase()] ?? 0);
    const ratingScore = (r) => {
      if (!r) return 0;
      const s = r.toLowerCase();
      if (s.includes("strong advantage")) return 5;
      if (s.includes("advantage")) return 4;
      if (s.includes("comparable")) return 3;
      if (s.includes("disadvantage")) return 1;
      return 2;
    };
    const parseValStr = (v) => {
      if (!v) return 0;
      const s = String(v).replace(/[$,\s]/g, "");
      const b = s.match(/([\d.]+)B/i);
      if (b) return parseFloat(b[1]) * 1000;
      const m = s.match(/([\d.]+)M/i);
      if (m) return parseFloat(m[1]);
      return parseFloat(s) || 0;
    };

    // Helper: detect currency unit from revenue table values ($M vs $B)
    const detectRevenueUnit = (table) => {
      if (!table || !table.length) return { unit: "$", scale: "M" };
      const sample = [table[0]?.bear, table[0]?.base, table[0]?.bull].filter(Boolean).join(" ");
      if (sample.includes("B")) return { unit: "$", scale: "B" };
      return { unit: "$", scale: "M" };
    };

    // Helper: parse revenue value like "$99M" or "$1.78B" → number
    const parseRevenueValue = (val) => {
      if (!val) return 0;
      const s = String(val).replace(/[$,\s]/g, '');
      const bMatch = s.match(/([\d.]+)\s*B/i);
      if (bMatch) return parseFloat(bMatch[1]) || 0;
      const mMatch = s.match(/([\d.]+)\s*M/i);
      if (mMatch) return parseFloat(mMatch[1]) || 0;
      return parseFloat(s) || 0;
    };

    // Helper: extract numeric comparisons from finding bullets for charting
    const parseBulletMetrics = (bullets) => {
      if (!bullets || !Array.isArray(bullets)) return [];
      const metrics = [];
      const units = '(?:%|kg|cm|mg\\/dL|mg|bpm|doses|hours|NRS|VAS|units)?';
      for (const b of bullets) {
        const vsMatch = b.match(new RegExp(`(\\d+[\\d.,]*(?:\\s*[-–]\\s*\\d+[\\d.,]*)?)\\s*${units}\\s+(?:vs|versus)\\s+(\\d+[\\d.,]*(?:\\s*[-–]\\s*\\d+[\\d.,]*)?)\\s*${units}`, 'i'));
        if (vsMatch) {
          const labelMatch = b.match(/^[\d.]+[-–]?[\d.]*%?\s+(?:reduction in|improvement in|longer|lower|of patients|mean)?\s*(.{3,40}?)(?:\s*\(|\s+at\b|\s+vs\b)/i);
          const label = labelMatch ? labelMatch[1].replace(/\s+$/, '') : b.slice(0, 30);
          const v1 = parseNumericValue(vsMatch[1]);
          const v2 = parseNumericValue(vsMatch[2]);
          if (!isNaN(v1) && !isNaN(v2)) {
            metrics.push({ label: label.length > 25 ? label.slice(0, 23) + '…' : label, therapy: v1, comparator: v2 });
          }
        }
      }
      return metrics;
    };

    // Section 1: Executive Summary (sec1 + sec3)
    if (navKey === "executive") {
      const s1 = sec("sec1");
      const s3 = sec("sec3");
      const efficacyTable = s1.efficacy_comparison_table || [];
      const safetyTable = s1.safety_comparison_table || [];
      const revenueTable = s1.revenue_projections_table || [];
      const revUnit = detectRevenueUnit(revenueTable);
      const valuationTable = s1.valuation_summary_table || [];
      const therapyName = therapyConfig?.therapy?.name || "Therapy";
      const comparatorName = therapyConfig?.therapy?.comparators?.[0]?.name || "Comparator";
      const keyFindings = s1.key_findings || [];
      const doseTable = s3.section3_3_dose_table || [];
      const compTable = s3.section3_4_comparison_table || [];

      // Build scenario key → tier name mapping
      const tierIdToName = s3.tier_id_to_name || {};
      const scenarioNameMap = {};
      if (Object.keys(tierIdToName).length > 0) {
        Object.entries(tierIdToName).forEach(([tid, tname]) => {
          scenarioNameMap[tid] = tname;
        });
      } else if (doseTable.length > 0) {
        doseTable.forEach((row, i) => {
          scenarioNameMap[`scenario_${String.fromCharCode(97 + i)}`] = row.tier_name || `Scenario ${String.fromCharCode(65 + i)}`;
        });
      }

      // Build key metrics — use recommended tier for headlines
      // Priority: backend-provided recommended_scenario_key > composite score lookup > fallback
      let bestScenarioKey = s3.recommended_scenario_key || null;
      if (!bestScenarioKey) {
        const compTableRow = compTable.find(r => r.category === "Composite Score") || compTable[0];
        if (compTableRow) {
          let bestScore = -1;
          for (const [key, val] of Object.entries(compTableRow)) {
            if (key.startsWith("scenario_") && val) {
              const score = parseFloat(val);
              if (!isNaN(score) && score > bestScore) {
                bestScore = score;
                bestScenarioKey = key;
              }
            }
          }
        }
      }
      if (!bestScenarioKey && efficacyTable[0]) {
        bestScenarioKey = Object.keys(efficacyTable[0]).find(k => k.startsWith("scenario_"));
      }
      const skipKeys = new Set(["outcome", "endpoint", "metric", "comparator", "category", "objective"]);
      const bestTierName = bestScenarioKey ? (tierIdToName[bestScenarioKey] || scenarioNameMap[bestScenarioKey] || "") : "";
      const bestTierSlug = bestTierName.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const findBestTherapyKey = (row) => {
        if (!row) return null;
        if (bestScenarioKey && row[bestScenarioKey]) return bestScenarioKey;
        if (bestTierSlug) {
          const match = Object.keys(row).find(k => {
            const kSlug = k.toLowerCase().replace(/[^a-z0-9]+/g, "_");
            return kSlug.includes(bestTierSlug) || bestTierSlug.includes(kSlug);
          });
          if (match) return match;
        }
        return Object.keys(row).find(k => !skipKeys.has(k) && !k.includes("comparator") && !k.includes("intravenous") && row[k]);
      };
      const findComparatorKey = (row) => {
        if (!row) return null;
        if (row.comparator !== undefined) return "comparator";
        return Object.keys(row).find(k => k.includes("comparator") || k.includes("intravenous"));
      };
      const effTherapyKey = findBestTherapyKey(efficacyTable[0]);
      const effCompKey = findComparatorKey(efficacyTable[0]);
      const safeTherapyKey = findBestTherapyKey(safetyTable[0]);
      const safeCompKey = findComparatorKey(safetyTable[0]);
      const efficacyHeadline = effTherapyKey
        ? `${efficacyTable[0][effTherapyKey]} vs ${effCompKey ? efficacyTable[0][effCompKey] : ""}`
        : (efficacyTable[0]?.advantage || "—");
      const safetyHeadline = safeTherapyKey
        ? `${safetyTable[0][safeTherapyKey]} vs ${safeCompKey ? safetyTable[0][safeCompKey] : ""}`
        : "—";
      const keyMetrics = [
        {
          label: "Efficacy", value: efficacyHeadline,
          detail: efficacyTable[0]?.outcome || efficacyTable[1]?.outcome || "Primary endpoint",
          drillDown: efficacyTable.length > 0 ? efficacyTable.map(e => `${e.outcome || "Endpoint"}: ${e.advantage || "—"} advantage`).join("\n") : null,
          navTarget: "efficacy"
        },
        {
          label: "Safety", value: safetyHeadline, detail: `vs ${comparatorName}`,
          drillDown: safetyTable.length > 0 ? safetyTable.map(e => `${e.endpoint || e.adverse_event || e.outcome || "Event"}: ${e.improvement || "—"}`).join("\n") : null,
          navTarget: "safety"
        },
        {
          label: "Scenarios", value: doseTable.length > 0 ? `${doseTable.length} tiers` : (activeConfig?.dose_scenarios?.length || "—"), detail: "Evaluated",
          drillDown: doseTable.length > 0 ? doseTable.map(d => `${d.tier_name || "Tier"}: ${typeof d.components === 'object' ? Object.entries(d.components).map(([k,v]) => `${k}: ${v}`).join(', ') : d.components || ""}`).join("\n") : null,
          navTarget: "executive"
        },
        {
          label: "Confidence", value: s1.overall_confidence?.score || "Moderate-High", detail: "Overall",
          drillDown: s1.overall_confidence ? `Level: ${s1.overall_confidence.score || "—"}\n${s1.overall_confidence.rationale || s1.overall_confidence.justification || ""}` : null,
          navTarget: "summary"
        }
      ];

      // Helper: render a comparison table inline
      const renderCompTable = (rows, columns) => {
        if (!rows || rows.length === 0) return null;
        // Separate interpretation from data keys
        const metaKeys = new Set(["interpretation"]);
        const allKeys = Object.keys(rows[0]);
        const dataKeys = allKeys.filter(k => !metaKeys.has(k));
        const hasDirection = allKeys.includes("interpretation");
        const headers = columns || dataKeys.map(k =>
          scenarioNameMap[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        );
        // Insert "Direction" header after the first column (label column)
        const finalHeaders = hasDirection && !columns ? [headers[0], "Direction", ...headers.slice(1)] : headers;
        return (
          <table className="w-full text-sm mt-2">
            <thead><tr className={themeTableHead}>
              {finalHeaders.map(h => <th key={h} className={`border ${themeBorder} px-3 py-1.5 ${themeText} text-[10px] font-medium text-left`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => {
                const cells = dataKeys.map(k => <td key={k} className={`border ${themeBorder} px-3 py-1.5 ${themeTextMuted} text-[10px]`}>{r[k]}</td>);
                // Insert direction cell after first cell
                if (hasDirection && !columns) {
                  cells.splice(1, 0, <td key="dir" className={`border ${themeBorder} px-3 py-1.5 ${themeTextMuted} text-[10px] italic`}>{r.interpretation}</td>);
                }
                return (
                  <tr key={i} className={themeTableRow(i)}>
                    {cells}
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      };

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 1: Executive Summary</h2>
            <SectionAgentBadge nk="executive" />
          </div>
          <div className="bg-gradient-to-r from-cyan-600/80 to-blue-700/80 rounded-lg p-5 border border-cyan-500/30">
            <h3 className="font-semibold text-white mb-1">{therapyConfig?.therapy?.name || "Therapy"}: {therapyConfig?.indication?.disease || "Assessment"}</h3>
            <p className="text-cyan-100 text-sm">{s1.positioning_statement || ""}</p>
          </div>
          {/* Therapy Introduction */}
          {s1.therapy_introduction && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <p className={`text-xs leading-relaxed ${themeText}`}>{s1.therapy_introduction}</p>
            </div>
          )}
          {/* Key Metrics — hover pulse + click to expand inline */}
          <div className="grid grid-cols-4 gap-3">
            {keyMetrics.map((m, i) => {
              const isOpen = expandedMetric === i;
              return (
                <div key={i} className="flex flex-col">
                  <div
                    onClick={() => setExpandedMetric(isOpen ? null : i)}
                    className={`border rounded-lg p-3 text-center cursor-pointer transition-all duration-200 group ${
                      isOpen
                        ? (isDark ? "bg-cyan-500/15 border-cyan-400/50 ring-1 ring-cyan-400/30 rounded-b-none" : "bg-cyan-50 border-cyan-400 ring-1 ring-cyan-300 rounded-b-none")
                        : (isDark ? "bg-slate-800/50 border-cyan-500/20 hover:border-cyan-400/40 hover:bg-slate-800/70 hover:scale-[1.03] hover:shadow-[0_0_16px_rgba(6,182,212,0.15)]" : "bg-white border-slate-300 hover:border-cyan-400 hover:shadow-lg hover:scale-[1.03]")
                    }`}
                  >
                    <div className={`text-xl font-bold text-cyan-400 transition-transform duration-200 ${!isOpen ? "group-hover:scale-110" : ""}`}>{m.value}</div>
                    <div className={`text-xs font-medium ${themeText}`}>{m.label}</div>
                    <div className={`text-[10px] ${themeTextMuted}`}>{m.detail}</div>
                    {/* Subtle expand indicator */}
                    <div className={`mt-1.5 flex justify-center transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}>
                      <ChevronDown size={12} className={`${isDark ? "text-cyan-500/40 group-hover:text-cyan-400" : "text-slate-300 group-hover:text-cyan-500"} transition-colors`} />
                    </div>
                  </div>
                  {/* Inline expanded detail */}
                  {isOpen && (
                    <div className={`border border-t-0 rounded-b-lg p-3 animate-[slideDown_0.2s_ease-out] ${isDark ? "bg-cyan-500/5 border-cyan-400/50" : "bg-cyan-50 border-cyan-400"}`}>
                      <pre className={`text-[10px] whitespace-pre-wrap leading-relaxed mb-2 ${themeText}`}>{m.drillDown || "No additional detail available."}</pre>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveSection(m.navTarget); setExpandedMetric(null); }}
                        className="text-[10px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                      ><ChevronRight size={10} /> View full section</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Scenario Introduction (before findings) */}
          {s1.scenario_introduction && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold mb-2 uppercase tracking-wider ${themeTextMuted}`}>Scenarios Evaluated</div>
              <div className={`text-xs leading-relaxed space-y-1 ${themeText}`}>
                {s1.scenario_introduction.split('\n').map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return null;
                  const parts = trimmed.replace(/^[•\-]\s*/, '').split(/\*\*(.+?)\*\*/g);
                  const rendered = parts.map((p, j) => j % 2 === 1 ? <strong key={j} className={themeTextBright}>{p}</strong> : p);
                  if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
                    return <div key={i} className="ml-3 flex gap-1.5"><span className="text-cyan-500">•</span><span>{rendered}</span></div>;
                  }
                  return <p key={i}>{rendered}</p>;
                })}
              </div>
            </div>
          )}

          {/* Key Findings — with inline charts */}
          {keyFindings.length > 0 && keyFindings.map((finding, fi) => {
            const bulletMetrics = parseBulletMetrics(finding.bullets);
            const hasComparisons = bulletMetrics.length >= 2;
            const isCommercialFinding = (finding.title || "").toLowerCase().match(/commercial|revenue|market|opportunity/);
            const isValuationFinding = (finding.title || "").toLowerCase().match(/valuation|investor/);
            const isSafetyFinding = (finding.title || "").toLowerCase().match(/safety|adverse|risk/);

            return (
              <div key={fi} className={`border rounded-lg overflow-hidden ${themeCard}`}>
                <div className="p-4">
                  <div className="text-sm font-semibold text-cyan-400 mb-2">Finding {finding.number}: {finding.title}</div>
                  <p className={`text-xs mb-2 ${themeText}`}>{finding.description}</p>
                  {finding.bullets && (
                    <ul className="space-y-1 mb-2">
                      {finding.bullets.map((b, j) => <li key={j} className={`text-xs ml-4 ${themeText}`}>{"\u2022"} {b}</li>)}
                    </ul>
                  )}
                  {finding.summary && <p className={`text-xs italic ${themeTextMuted}`}>{finding.summary}</p>}
                  {/* Inline comparison tables (supports multi-scenario) */}
                  {finding.number === 1 && efficacyTable.length > 0 && renderCompTable(efficacyTable)}
                  {finding.number === 2 && safetyTable.length > 0 && renderCompTable(safetyTable)}
                </div>
                {/* Inline chart when bullet data has comparisons */}
                {hasComparisons && !isCommercialFinding && !isValuationFinding && (
                  <div className={`relative group px-4 pb-4 pt-2 border-t ${isDark ? "border-slate-700/50 bg-slate-900/30" : "border-slate-100 bg-slate-50/50"}`} data-chart-container="">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`text-[9px] uppercase tracking-wider font-medium ${themeTextMuted}`}>
                        {isSafetyFinding ? "Safety Profile Comparison" : `${therapyName} vs ${comparatorName}`}
                      </div>
                      <div className="hidden group-hover:flex gap-1">
                        <button onClick={() => exportChartAsJSON(bulletMetrics, "therapy-comparison")} className={themeExportBtn}>JSON</button>
                        <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "therapy-comparison")} className={themeExportBtn}>PNG</button>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={bulletMetrics} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} horizontal={false} />
                        <XAxis type="number" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                        <YAxis type="category" dataKey="label" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 9 }} width={100} axisLine={false} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} />
                        <Bar dataKey="therapy" name={therapyName} fill="#06b6d4" radius={[0, 4, 4, 0]} barSize={12} />
                        <Bar dataKey="comparator" name={comparatorName} fill={isDark ? "#475569" : "#94a3b8"} radius={[0, 4, 4, 0]} barSize={12} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Revenue chart for commercial findings */}
                {isCommercialFinding && revenueTable.length > 0 && (
                  <div className={`relative group px-4 pb-4 pt-2 border-t ${isDark ? "border-slate-700/50 bg-slate-900/30" : "border-slate-100 bg-slate-50/50"}`} data-chart-container="">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`text-[9px] uppercase tracking-wider font-medium ${themeTextMuted}`}>Revenue Projections</div>
                      <div className="hidden group-hover:flex gap-1">
                        <button onClick={() => exportChartAsJSON(revenueTable, "revenue-projections")} className={themeExportBtn}>JSON</button>
                        <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "revenue-projections")} className={themeExportBtn}>PNG</button>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={revenueTable.map(r => ({ year: (r.year || "").replace(/\s*\(.*\)/, ''), bear: parseRevenueValue(r.bear), base: parseRevenueValue(r.base), bull: parseRevenueValue(r.bull) }))} margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} />
                        <XAxis dataKey="year" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                        <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} tickFormatter={v => `$${v}${revUnit.scale}`} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} formatter={v => `$${v}${revUnit.scale}`} />
                        <Bar dataKey="bear" name="Bear" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={14} />
                        <Bar dataKey="base" name="Base" fill="#06b6d4" radius={[4, 4, 0, 0]} barSize={14} />
                        <Bar dataKey="bull" name="Bull" fill="#22c55e" radius={[4, 4, 0, 0]} barSize={14} />
                        <Legend wrapperStyle={{ fontSize: 10 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Efficacy comparison chart fallback — temporarily hidden */}
                {false && !hasComparisons && !isCommercialFinding && !isValuationFinding && !isSafetyFinding && efficacyTable.length >= 2 && fi === 0 && (
                  <div className={`relative group px-4 pb-4 pt-2 border-t ${isDark ? "border-slate-700/50 bg-slate-900/30" : "border-slate-100 bg-slate-50/50"}`} data-chart-container="">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`text-[9px] uppercase tracking-wider font-medium ${themeTextMuted}`}>Efficacy Comparison</div>
                      <div className="hidden group-hover:flex gap-1">
                        <button onClick={() => exportChartAsJSON(efficacyTable, "efficacy-comparison")} className={themeExportBtn}>JSON</button>
                        <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "efficacy-comparison")} className={themeExportBtn}>PNG</button>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={Math.max(160, (efficacyTable.length || 4) * 50)}>
                      {(() => {
                        const skipKeys = new Set(["outcome", "interpretation", "direction", "advantage"]);
                        const scenarioKeys = efficacyTable.length > 0 ? Object.keys(efficacyTable[0]).filter(k => !skipKeys.has(k)) : [];
                        const scenarioColors = ["#2563eb", "#0891b2", "#059669", isDark ? "#64748b" : "#9ca3af"];
                        const chartData = efficacyTable.map(e => {
                          const row = { endpoint: (e.outcome || "").length > 28 ? (e.outcome || "").slice(0, 26) + '…' : (e.outcome || "") };
                          scenarioKeys.forEach(k => { row[k] = Math.abs(parseNumericValue(e[k])) || 0; });
                          return row;
                        });
                        return (
                          <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} horizontal={false} />
                            <XAxis type="number" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                            <YAxis type="category" dataKey="endpoint" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 9 }} width={150} axisLine={false} />
                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} />
                            {scenarioKeys.map((k, i) => (
                              <Bar key={k} dataKey={k} fill={scenarioColors[i % scenarioColors.length]} radius={[0, 4, 4, 0]} barSize={8} />
                            ))}
                            <Legend wrapperStyle={{ fontSize: 9 }} />
                          </BarChart>
                        );
                      })()}
                    </ResponsiveContainer>
                  </div>
                )}
                {/* Safety comparison for safety findings — temporarily hidden */}
                {false && isSafetyFinding && !hasComparisons && safetyTable.length >= 2 && (
                  <div className={`relative group px-4 pb-4 pt-2 border-t ${isDark ? "border-slate-700/50 bg-slate-900/30" : "border-slate-100 bg-slate-50/50"}`} data-chart-container="">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`text-[9px] uppercase tracking-wider font-medium ${themeTextMuted}`}>Safety Profile Comparison</div>
                      <div className="hidden group-hover:flex gap-1">
                        <button onClick={() => exportChartAsJSON(safetyTable, "safety-comparison")} className={themeExportBtn}>JSON</button>
                        <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "safety-comparison")} className={themeExportBtn}>PNG</button>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={Math.max(160, (safetyTable.length || 4) * 50)}>
                      {(() => {
                        const skipKeys = new Set(["endpoint", "adverse_event", "interpretation", "direction", "advantage"]);
                        const scenarioKeys = safetyTable.length > 0 ? Object.keys(safetyTable[0]).filter(k => !skipKeys.has(k)) : [];
                        const scenarioColors = ["#2563eb", "#0891b2", "#059669", isDark ? "#64748b" : "#9ca3af"];
                        const chartData = safetyTable.map(e => {
                          const row = { endpoint: (e.endpoint || e.adverse_event || "").length > 28 ? (e.endpoint || e.adverse_event || "").slice(0, 26) + '…' : (e.endpoint || e.adverse_event || "") };
                          scenarioKeys.forEach(k => { row[k] = Math.abs(parseNumericValue(e[k])) || 0; });
                          return row;
                        });
                        return (
                          <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} horizontal={false} />
                            <XAxis type="number" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                            <YAxis type="category" dataKey="endpoint" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 9 }} width={150} axisLine={false} />
                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} />
                            {scenarioKeys.map((k, i) => (
                              <Bar key={k} dataKey={k} fill={scenarioColors[i % scenarioColors.length]} radius={[0, 4, 4, 0]} barSize={8} />
                            ))}
                            <Legend wrapperStyle={{ fontSize: 9 }} />
                          </BarChart>
                        );
                      })()}
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            );
          })}

          {/* Dose Scenarios and Predicted Response tables shown in Section 3 output */}

          {/* Revenue Projections Table */}
          {revenueTable.length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold mb-3 uppercase tracking-wider ${themeTextMuted}`}>Revenue Projections</div>
              {renderCompTable(revenueTable, ["Year", "Bear", "Base", "Bull"])}
            </div>
          )}

          {/* Valuation Summary Table */}
          {valuationTable.length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold mb-3 uppercase tracking-wider ${themeTextMuted}`}>Valuation Summary</div>
              {renderCompTable(valuationTable, ["Phase", "Bear", "Base", "Bull", "Avg"])}
            </div>
          )}
          {/* Investment Thesis */}
          {s1.investment_thesis && (
            <div className={`border rounded-lg p-4 ${isDark ? "bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/20" : "bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200"}`}>
              <div className={`text-xs font-semibold mb-2 uppercase tracking-wider ${themeTextMuted}`}>Investment Thesis</div>
              <p className={`text-xs leading-relaxed ${themeText}`}>{s1.investment_thesis}</p>
            </div>
          )}
        </div>
      );
    }

    // Section 2: Pathway Scores (sec2 + sec3 + sec5)
    if (navKey === "pathway-scores") {
      const s2 = sec("sec2");
      const s3 = sec("sec3");
      const s5 = sec("sec5");
      const confidenceTable = s5.extracted_parameters?.section5_confidence_table || [];

      // Resolve sec2 data — may be nested in "content" key
      const s2Data = s2?.content ? (typeof s2.content === 'string' ? {} : s2.content) : s2;
      const pathwayTable = s2Data?.section2_2_table || s2?.section2_2_table || [];
      const totalPathways = s2Data?.total_pathways || s2?.total_pathways || pathwayTable.length;

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 2: GNQ Analytic Workflow & Pathway Landscape</h2>
            <SectionAgentBadge nk="pathway-scores" />
          </div>

          {/* 2.1 GNQ Analytic Workflow */}
          {/* 2.1: GNQ Analytic Workflow — structured capability cards */}
          <div>
            <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>2.1: GNQ Analytic Workflow</div>
            <div className="grid grid-cols-2 gap-3">
              {GNQ_CAPABILITY_BLOCKS.map(({ id, title, Icon, iconCls, dotCls, items }) => (
                <div key={id} className={`border rounded-lg p-3 hover:border-cyan-500/30 transition-colors ${themeCard}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={14} className={iconCls} />
                    <span className={`text-[11px] font-semibold ${themeTextBright}`}>{title}</span>
                  </div>
                  <ul className="space-y-1">
                    {items.map((item, j) => (
                      <li key={j} className={`text-[10px] ${themeTextMuted} flex items-start gap-1.5`}>
                        <span className={`mt-1 w-1 h-1 rounded-full ${dotCls} shrink-0`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* 2.2 Pathway & Gene Landscape — with table/visual toggle (Task 72/71) */}
          {pathwayTable.length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`text-xs font-semibold uppercase tracking-wider ${themeTextMuted}`}>2.2: Pathway & Gene Landscape ({totalPathways} pathways)</div>
                <div className={`flex items-center gap-1 p-0.5 rounded-md ${isDark ? "bg-slate-700/50" : "bg-slate-100"}`}>
                  <button onClick={() => setViewMode("visual")} className={`p-1 rounded transition-colors ${viewMode === "visual" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Visual view"><BarChart2 size={12} /></button>
                  <button onClick={() => setViewMode("table")} className={`p-1 rounded transition-colors ${viewMode === "table" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Table view"><Table2 size={12} /></button>
                </div>
              </div>
              {viewMode === "table" ? (
                <table className="w-full text-sm">
                  <thead><tr className={themeTableHead}>
                    {["Pathway Category", "Pathways", "Key Genes"].map(h => <th key={h} className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium text-left`}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {pathwayTable.map((r, i) => (
                      <tr key={i} className={themeTableRow(i)}>
                        <td className={`border ${themeBorder} px-3 py-2 text-xs font-medium ${themeText}`}>{r.pathway_category}</td>
                        <td className={`border ${themeBorder} px-3 py-2 text-xs ${themeTextMuted}`}>{r.pathway_names_with_count}</td>
                        <td className={`border ${themeBorder} px-3 py-2 text-xs font-mono ${themeTextMuted}`}>{r.genes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {pathwayTable.map((r, i) => (
                    <div key={i} className={`border rounded-lg p-3 ${isDark ? "bg-slate-900/50 border-slate-700" : "bg-white border-slate-300"}`}>
                      <div className="text-xs font-semibold text-cyan-400 mb-1">{r.pathway_category}</div>
                      <div className={`text-[10px] mb-1 ${themeTextMuted}`}>{r.pathway_names_with_count}</div>
                      <div className="flex flex-wrap gap-1">
                        {(r.genes || "").split(",").map((g, gi) => g.trim() && (
                          <span key={gi} className={`text-[9px] px-1.5 py-0.5 rounded ${isDark ? "bg-cyan-500/10 text-cyan-300" : "bg-cyan-50 text-cyan-600"}`}>{g.trim()}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 2.3 Endpoint-Pathway Mapping — with table/visual toggle (Task 72/71) */}
          {(() => {
            const epMapping = s2Data?.section2_3_endpoint_mapping || s2?.section2_3_endpoint_mapping || [];
            if (epMapping.length === 0) return null;
            return (
              <div className={`border rounded-lg p-4 ${themeCard}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className={`text-xs font-semibold uppercase tracking-wider ${themeTextMuted}`}>2.3: Endpoint-Pathway Mapping</div>
                  <div className={`flex items-center gap-1 p-0.5 rounded-md ${isDark ? "bg-slate-700/50" : "bg-slate-100"}`}>
                    <button onClick={() => setViewMode("visual")} className={`p-1 rounded transition-colors ${viewMode === "visual" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Visual view"><BarChart2 size={12} /></button>
                    <button onClick={() => setViewMode("table")} className={`p-1 rounded transition-colors ${viewMode === "table" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Table view"><Table2 size={12} /></button>
                  </div>
                </div>
                {viewMode === "table" ? (
                  <table className="w-full text-sm">
                    <thead><tr className={themeTableHead}>
                      {["Clinical Endpoint", "Type", "Mapped Pathways", "Objective"].map(h => <th key={h} className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium text-left`}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {epMapping.map((r, i) => (
                        <tr key={i} className={themeTableRow(i)}>
                          <td className={`border ${themeBorder} px-3 py-2 text-xs font-medium ${themeText}`}>{r.endpoint_name}</td>
                          <td className={`border ${themeBorder} px-3 py-2 text-xs`}>
                            <span className={`px-2 py-0.5 rounded text-[10px] ${r.endpoint_type === 'efficacy' ? 'bg-cyan-900/50 text-cyan-300' : 'bg-amber-900/50 text-amber-300'}`}>{r.endpoint_type}</span>
                          </td>
                          <td className={`border ${themeBorder} px-3 py-2 text-xs ${themeTextMuted}`}>{r.mapped_pathways}</td>
                          <td className={`border ${themeBorder} px-3 py-2 text-xs ${themeTextMuted}`}>{r.objective}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="space-y-2">
                    {epMapping.map((r, i) => (
                      <div key={i} className={`border rounded-lg p-3 flex items-start gap-3 ${isDark ? "bg-slate-900/50 border-slate-700" : "bg-white border-slate-300"}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-semibold ${themeText}`}>{r.endpoint_name}</span>
                            <span className={`px-2 py-0.5 rounded text-[9px] ${r.endpoint_type === 'efficacy' ? 'bg-cyan-900/50 text-cyan-300' : 'bg-amber-900/50 text-amber-300'}`}>{r.endpoint_type}</span>
                          </div>
                          <div className={`text-[10px] ${themeTextMuted}`}>{r.objective}</div>
                        </div>
                        <div className="flex flex-wrap gap-1 max-w-[50%]">
                          {(r.mapped_pathways || "").split(",").map((p, pi) => p.trim() && (
                            <span key={pi} className={`text-[9px] px-1.5 py-0.5 rounded ${isDark ? "bg-cyan-500/10 text-cyan-300" : "bg-cyan-50 text-cyan-600"}`}>{p.trim()}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Comparison table and Confidence Scores moved to Section 3 and Section 5 */}
        </div>
      );
    }

    // Section 3: Mechanism Mapping (sec3)
    if (navKey === "mechanism-mapping") {
      const s3 = sec("sec3");
      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 3: Drug Composition, MOA & Dose Translation</h2>
            <SectionAgentBadge nk="mechanism-mapping" />
          </div>
          {/* Drug Composition */}
          <div className={`border rounded-lg p-4 ${themeCard}`}>
            <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>Drug Composition & Synergistic Mechanism</div>
            {renderText(s3.section3_1_static_text, themeText)}
          </div>
          {/* Mechanism of Action */}
          <div className={`border rounded-lg p-4 ${themeCard}`}>
            <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>Mechanism of Action</div>
            {renderText(s3.section3_2_static_text, themeText)}
          </div>
          {/* 3.3 Dose Scenarios */}
          {s3.section3_3_dose_translation_text && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>3.3: Dose Scenarios</div>
              <p className={`text-xs ${themeText} mb-3`}>{s3.section3_3_dose_translation_text}</p>
              {/* Dose Table */}
              {(s3.section3_3_dose_table || []).length > 0 && (
                <table className="w-full text-sm mt-2">
                  <thead><tr className={themeTableHead}>
                    {["Tier", "Scenario", "Key Components", "Description"].map(h => <th key={h} className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium text-left`}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {s3.section3_3_dose_table.map((r, i) => (
                      <tr key={i} className={themeTableRow(i)}>
                        <td className={`border ${themeBorder} px-3 py-2 ${themeTextBright} text-xs font-medium`}>{r.tier_name}</td>
                        <td className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs`}>{r.scenario_id}</td>
                        <td className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-mono`}>
                          {typeof r.components === 'object' ? Object.entries(r.components).map(([k,v]) => `${k}: ${v}`).join('; ') : r.components}
                        </td>
                        <td className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs`}>{r.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* 3.4 Causal Pathway Impact Analysis */}
          {(s3.section3_4_comparison_table || []).length > 0 && (() => {
            const compTable = s3.section3_4_comparison_table;
            // Build local scenario name map for sec3
            const localNameMap = {};
            const tierIdToName3 = s3.tier_id_to_name || {};
            if (Object.keys(tierIdToName3).length > 0) {
              Object.entries(tierIdToName3).forEach(([tid, tname]) => { localNameMap[tid] = tname; });
            } else {
              (s3.section3_3_dose_table || []).forEach((row, i) => {
                localNameMap[`scenario_${String.fromCharCode(97 + i)}`] = row.tier_name || `Scenario ${String.fromCharCode(65 + i)}`;
              });
            }
            // Detect table format: new (endpoint/pathway) vs legacy (category)
            const isPathwayFormat = compTable[0]?.endpoint !== undefined;
            const PATHWAY_COLORS = ['#06b6d4', '#a78bfa', '#f59e0b', '#f87171', '#34d399', '#fb923c'];
            if (isPathwayFormat) {
              // New format: endpoint → pathway → objective → drug impacts
              const fixedKeys = new Set(["endpoint", "pathway", "objective"]);
              const valueKeys = Object.keys(compTable[0]).filter(k => !fixedKeys.has(k));
              const headers = ["Endpoint", "Mapped Pathway", "Objective", ...valueKeys.map(k => localNameMap[k] || k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()))];
              // Radar: aggregate scores per unique pathway
              const uniquePathways = [...new Set(compTable.map(r => r.pathway).filter(Boolean))];
              const pathwayRadarData = uniquePathways.map(pw => {
                const rows = compTable.filter(r => r.pathway === pw);
                const cleanPw = pw.replace(/\s*\(.*\)/, "").trim();
                const entry = {
                  pathway: cleanPw.length > 22 ? cleanPw.slice(0, 20) + '…' : cleanPw,
                  _fullPathway: cleanPw,
                };
                valueKeys.forEach(k => {
                  const vals = rows.map(r => Math.abs(parseFloat(String(r[k] || "0").replace(/[^-\d.]/g, "")))).filter(v => !isNaN(v));
                  entry[k] = vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(4) : 0;
                });
                return entry;
              });
              // Log-normalize for radar display (raw values preserved as _raw_* for tooltip)
              const allRadarVals = pathwayRadarData.flatMap(d => valueKeys.map(k => d[k] || 0)).filter(v => v > 0);
              const rdrGlobalMax = Math.max(...allRadarVals, 1e-9);
              const rdrGlobalMin = Math.min(...allRadarVals, rdrGlobalMax);
              const rdrLogScale = 1 / rdrGlobalMin;
              const rdrLogMax = Math.log1p(rdrGlobalMax * rdrLogScale);
              const pathwayRadarNorm = pathwayRadarData.map(d => {
                const entry = { pathway: d.pathway, _fullPathway: d._fullPathway };
                valueKeys.forEach(k => {
                  const raw = d[k] || 0;
                  entry[`_raw_${k}`] = raw;
                  entry[k] = rdrLogMax > 0 ? +(Math.log1p(raw * rdrLogScale) / rdrLogMax).toFixed(3) : 0;
                });
                return entry;
              });
              return (
                <>
                  <div className={`border rounded-lg p-4 ${themeCard}`}>
                    <div className={`text-xs font-semibold mb-3 uppercase tracking-wider ${themeTextMuted}`}>3.4: Causal Pathway Impact Analysis (Selected Pathways)</div>
                    <table className="w-full text-sm">
                      <thead><tr className={isDark ? "bg-slate-800/80" : "bg-slate-100"}>{headers.map(h => <th key={h} className={`border ${themeBorder} px-2 py-1.5 text-[10px] font-medium ${themeText}`}>{h}</th>)}</tr></thead>
                      <tbody>
                        {compTable.map((r, i) => (
                          <tr key={i} className={themeTableRow(i)}>
                            <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeText}`}>{r.endpoint}</td>
                            <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeTextMuted}`}>{r.pathway}</td>
                            <td className={`border ${themeBorder} px-2 py-1.5 text-center text-[10px]`}>
                              <span className={r.objective === "MAXIMIZE" ? "text-cyan-400" : "text-amber-400"}>{r.objective}</span>
                            </td>
                            {valueKeys.map(k => (
                              <td key={k} className={`border ${themeBorder} px-2 py-1.5 text-center font-mono text-[10px] ${r[k]?.startsWith?.("+") ? "text-green-400" : r[k]?.startsWith?.("-") ? "text-red-400" : themeTextMuted}`}>{r[k] || "—"}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pathwayRadarData.length >= 3 && (
                    <div className={`border rounded-lg p-4 ${themeCard}`}>
                      <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider mb-1`}>3.4b Pathway Impact: Dose Scenario Comparison</div>
                      <div className={`text-[9px] ${themeTextMuted} mb-3`}>log-normalized scale · hover for raw scores</div>
                      <ResponsiveContainer width="100%" height={320}>
                        <RadarChart cx="50%" cy="50%" outerRadius="65%" data={pathwayRadarNorm}>
                          <PolarGrid stroke={isDark ? "#334155" : "#e2e8f0"} />
                          <PolarAngleAxis dataKey="pathway" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 10 }} />
                          <PolarRadiusAxis domain={[0, 1]} tick={false} axisLine={false} tickCount={4} />
                          {valueKeys.map((k, ci) => {
                            if (radarHiddenScenarios[k]) return null;
                            return (
                              <Radar key={k} name={localNameMap[k] || k.replace(/_/g, " ")}
                                dataKey={k}
                                stroke={PATHWAY_COLORS[ci % PATHWAY_COLORS.length]}
                                fill={PATHWAY_COLORS[ci % PATHWAY_COLORS.length]}
                                fillOpacity={0.12 + ci * 0.05}
                                strokeWidth={2} />
                            );
                          })}
                          <Legend
                            content={() => (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', justifyContent: 'center', marginTop: 8 }}>
                                {valueKeys.map((k, ci) => {
                                  const hidden = radarHiddenScenarios[k];
                                  const name = localNameMap[k] || k.replace(/_/g, " ");
                                  const color = PATHWAY_COLORS[ci % PATHWAY_COLORS.length];
                                  return (
                                    <span
                                      key={k}
                                      onClick={() => setRadarHiddenScenarios(prev => ({ ...prev, [k]: !prev[k] }))}
                                      style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', opacity: hidden ? 0.3 : 1, fontSize: 11, color: isDark ? '#cbd5e1' : '#475569', userSelect: 'none', transition: 'opacity 0.15s' }}
                                    >
                                      <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, backgroundColor: color, flexShrink: 0 }} />
                                      {name}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          />
                          <Tooltip
                            content={({ active, payload, label }) => {
                              if (!active || !payload?.length) return null;
                              const fullName = payload[0]?.payload?._fullPathway || label;
                              return (
                                <div style={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, padding: '8px 12px', fontSize: 11, maxWidth: 280 }}>
                                  <div style={{ color: isDark ? '#e2e8f0' : '#1e293b', fontWeight: 600, marginBottom: 4, wordBreak: 'break-word' }}>{fullName}</div>
                                  {payload.map(p => (
                                    <div key={p.dataKey} style={{ color: p.color }}>
                                      {p.name}: {(p.payload[`_raw_${p.dataKey}`] ?? p.value).toFixed(4)}
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              );
            }
            // Legacy format: category/objective with scenario columns
            const comp = collectedConfig?.therapy?.comparators?.[0];
            const comparatorHeader = comp?.name || "Comparator";
            const scenarioKeys = Object.keys(compTable[0]).filter(k => k.startsWith("scenario_"));
            const compKey = Object.keys(compTable[0]).find(k => k === "comparator" || k.includes("comparator"));
            const allHeaders = ["Category", "Objective", ...scenarioKeys.map(k => localNameMap[k] || k), comparatorHeader];
            return (
              <div className={`border rounded-lg p-4 ${themeCard}`}>
                <div className={`text-xs font-semibold mb-3 uppercase tracking-wider ${themeTextMuted}`}>3.4: Predicted Response vs Comparator</div>
                <table className="w-full text-sm">
                  <thead><tr className={isDark ? "bg-slate-800/80" : "bg-slate-100"}>{allHeaders.map(h => <th key={h} className={`border ${themeBorder} px-2 py-1.5 text-[10px] font-medium ${themeText}`}>{h}</th>)}</tr></thead>
                  <tbody>
                    {compTable.map((r, i) => (
                      <tr key={i} className={themeTableRow(i)}>
                        <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeText}`}>{r.category}</td>
                        <td className={`border ${themeBorder} px-2 py-1.5 text-center text-[10px] ${themeTextMuted}`}>{r.objective}</td>
                        {scenarioKeys.map(k => (
                          <td key={k} className={`border ${themeBorder} px-2 py-1.5 text-center font-mono text-[10px] text-cyan-400`}>{r[k] || "—"}</td>
                        ))}
                        <td className={`border ${themeBorder} px-2 py-1.5 text-center font-mono text-[10px] ${themeTextMuted}`}>{r[compKey] || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* 3.5 Pathway Performance & Coverage */}
          {s3.section3_5_introduction && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>3.5: Pathway Performance & Coverage</div>
              {renderText(s3.section3_5_introduction, themeText)}
            </div>
          )}
        </div>
      );
    }

    // Section 4: Real World Data & Clinical Evidence (sec4)
    if (navKey === "causal-inference") {
      const s4 = sec("sec4");
      const params = s4.extracted_parameters || {};
      const animalStudies = params.animal_study_results || [];
      const humanStudies = params.human_clinical_evidence || [];
      const hasStudies = animalStudies.length > 0 || humanStudies.length > 0;

      // Render a single study card from StudyExtractor output
      const renderStudyCard = (study, idx) => {
        const identity = study.study_identity || {};
        const design = study.study_design || {};
        const pop = study.population || {};
        const interventions = study.interventions || [];
        const efficacy = study.efficacy_results || [];
        const safety = study.safety_results || {};
        const conclusions = study.conclusions || {};
        const classification = study.classification || "unknown";
        const isAnimal = classification.includes("animal") || classification.includes("vitro");

        return (
          <div key={idx} className="bg-slate-800/50 border border-cyan-500/20 rounded-lg overflow-hidden">
            {/* Header: Study Identity */}
            <div className="bg-slate-800/80 px-4 py-3 border-b border-slate-700/50">
              <div className="text-xs font-semibold text-slate-200 leading-relaxed">{identity.title || "Untitled Study"}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[10px] text-cyan-400">{identity.authors || "Unknown"}, {identity.year || ""}</span>
                {identity.journal && <span className="text-[10px] text-slate-500">• {identity.journal}</span>}
                <span className={`text-[9px] px-1.5 py-0.5 rounded ${isAnimal ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"}`}>
                  {design.type || classification.replace(/_/g, " ")}
                </span>
                {design.blinding && design.blinding !== "not_applicable" && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400 border border-slate-600/30">{design.blinding.replace(/_/g, " ")}</span>
                )}
                {identity.registry_id && <span className="text-[9px] text-slate-500">{identity.registry_id}</span>}
              </div>
              {study.filename && <div className="text-[9px] text-slate-600 mt-1">Source: {study.filename}</div>}
            </div>

            {/* Design & Population */}
            <div className="px-4 py-2.5 border-b border-slate-700/30">
              <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Design & Population</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-300">
                {design.sample_size && <span>N = {design.sample_size}</span>}
                {design.n_sites && <span>{design.n_sites} sites</span>}
                {design.duration_treatment && <span>Treatment: {design.duration_treatment}</span>}
                {design.duration_followup && <span>Follow-up: {design.duration_followup}</span>}
                {design.species_model && <span>Model: {design.species_model}</span>}
              </div>
              {pop.description && <p className="text-[10px] text-slate-400 mt-1">{pop.description}</p>}
              {interventions.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {interventions.map((intv, j) => (
                    <span key={j} className={`text-[9px] px-1.5 py-0.5 rounded ${intv.is_comparator ? "bg-slate-700/50 text-slate-400" : "bg-cyan-500/10 text-cyan-400"} border border-slate-600/30`}>
                      {intv.arm_name || intv.drug}: {intv.dose} {intv.route || ""}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Efficacy Results Table */}
            {efficacy.length > 0 && (
              <div className="px-4 py-2.5 border-b border-slate-700/30">
                <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Key Efficacy Results</div>
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="text-slate-500">
                      <th className="text-left py-1 pr-2 font-medium">Endpoint</th>
                      <th className="text-left py-1 pr-2 font-medium">Type</th>
                      <th className="text-right py-1 pr-2 font-medium">Effect</th>
                      <th className="text-right py-1 pr-2 font-medium">95% CI</th>
                      <th className="text-right py-1 pr-2 font-medium">p-value</th>
                      <th className="text-center py-1 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {efficacy.map((ep, j) => {
                      const isFav = ep.direction === "favorable";
                      const isUnfav = ep.direction === "unfavorable";
                      return (
                        <tr key={j} className="border-t border-slate-700/20">
                          <td className="py-1 pr-2 text-slate-300">{ep.endpoint_name}</td>
                          <td className="py-1 pr-2 text-slate-500">{ep.endpoint_type || ""}</td>
                          <td className="py-1 pr-2 text-right font-mono text-slate-300">
                            {ep.effect_measure && ep.effect_value ? `${ep.effect_measure} ${ep.effect_value}` : ep.result_summary || "—"}
                          </td>
                          <td className="py-1 pr-2 text-right font-mono text-slate-400">{ep.ci_95 || "—"}</td>
                          <td className={`py-1 pr-2 text-right font-mono ${ep.p_value && parseFloat(ep.p_value) < 0.05 ? "text-cyan-400" : "text-slate-400"}`}>
                            {ep.p_value || "—"}
                          </td>
                          <td className="py-1 text-center">
                            {isFav && <span className="text-green-400 text-[9px]">Favorable</span>}
                            {isUnfav && <span className="text-red-400 text-[9px]">Unfavorable</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Safety */}
            {(safety.adverse_events?.length > 0 || safety.serious_adverse_events || safety.discontinuation_rate) && (
              <div className="px-4 py-2.5 border-b border-slate-700/30">
                <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Safety</div>
                {safety.adverse_events?.length > 0 && (
                  <table className="w-full text-[10px] mb-1">
                    <thead><tr className="text-slate-500">
                      <th className="text-left py-0.5 pr-2 font-medium">Event</th>
                      <th className="text-right py-0.5 pr-2 font-medium">Treatment</th>
                      <th className="text-right py-0.5 font-medium">Control</th>
                    </tr></thead>
                    <tbody>
                      {safety.adverse_events.map((ae, j) => (
                        <tr key={j} className="border-t border-slate-700/20">
                          <td className="py-0.5 pr-2 text-slate-300">{ae.event}</td>
                          <td className="py-0.5 pr-2 text-right font-mono text-slate-300">{ae.treatment_rate || "—"}</td>
                          <td className="py-0.5 text-right font-mono text-slate-400">{ae.control_rate || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {safety.serious_adverse_events && <p className="text-[10px] text-slate-400">SAEs: {safety.serious_adverse_events}</p>}
                {safety.discontinuation_rate && <p className="text-[10px] text-slate-400">Discontinuation: {safety.discontinuation_rate}</p>}
                {safety.noael_loael && <p className="text-[10px] text-slate-400">NOAEL/LOAEL: {safety.noael_loael}</p>}
              </div>
            )}

            {/* Conclusions */}
            {(conclusions.main_conclusion || conclusions.limitations) && (
              <div className="px-4 py-2.5">
                <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Authors' Conclusions</div>
                {conclusions.main_conclusion && <p className="text-[10px] text-slate-300 leading-relaxed">{conclusions.main_conclusion}</p>}
                {conclusions.limitations && <p className="text-[10px] text-slate-500 mt-1">Limitations: {conclusions.limitations}</p>}
              </div>
            )}
          </div>
        );
      };

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 4: Real World Data & Clinical Evidence</h2>
            <SectionAgentBadge nk="causal-inference" />
          </div>

          {/* No data fallback — no documents uploaded at all */}
          {!hasStudies && !params.animal_study_description && !params.human_clinical_description && params.section_description && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <p className={`text-xs ${themeText} leading-relaxed`}>{params.section_description}</p>
            </div>
          )}

          {/* 4.1 Animal / Preclinical Studies — always show heading */}
          {(hasStudies || params.animal_study_description || params.human_clinical_description) && (
            <div className="space-y-3">
              <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>4.1 Animal & Preclinical Studies ({animalStudies.length})</div>
              {animalStudies.length > 0 ? (
                animalStudies.map((study, i) => renderStudyCard(study, i))
              ) : (
                <div className={`border rounded-lg p-4 ${themeCard}`}>
                  <p className={`text-xs ${themeText} leading-relaxed`}>{params.animal_study_description || "No animal or preclinical study documents were provided for this assessment."}</p>
                </div>
              )}
            </div>
          )}

          {/* 4.2 Human Clinical Evidence — always show heading */}
          {(hasStudies || params.animal_study_description || params.human_clinical_description) && (
            <div className="space-y-3">
              <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>4.2 Human Clinical Evidence ({humanStudies.length})</div>
              {humanStudies.length > 0 ? (
                humanStudies.map((study, i) => renderStudyCard(study, i))
              ) : (
                <div className={`border rounded-lg p-4 ${themeCard}`}>
                  <p className={`text-xs ${themeText} leading-relaxed`}>{params.human_clinical_description || "No human clinical study documents were provided for this assessment."}</p>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Section 5: Patient Stratification (sec5)
    if (navKey === "stratification") {
      const s5 = sec("sec5");
      const stratTable = s5.extracted_parameters?.section5_1_stratification_table || [];
      const confidenceTable = s5.extracted_parameters?.section5_confidence_table || [];
      const groupNarratives = s5.extracted_parameters?.section5_2_group_narratives || {};
      const confidenceRationale = s5.extracted_parameters?.section5_confidence_rationale || {};
      const responses = s5.response || [];
      const confidenceLookup = {};
      confidenceTable.forEach(c => { confidenceLookup[c.group_number] = parseInt(c.confidence_score) || 0; });

      // SVG confidence ring helper
      const ConfidenceRing = ({ value, size = 40 }) => {
        const r = (size - 4) / 2;
        const circ = 2 * Math.PI * r;
        const offset = circ - (circ * (value / 100));
        const color = value >= 70 ? "#06b6d4" : value >= 50 ? "#f59e0b" : "#f87171";
        return (
          <svg width={size} height={size} className="flex-shrink-0">
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={isDark ? "#334155" : "#e2e8f0"} strokeWidth={3} />
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={3} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} className="transition-all duration-700" />
            <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central" className="text-[9px] font-mono" fill={color}>{value}%</text>
          </svg>
        );
      };

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 5: Patient Stratification & Biomarker Strategy</h2>
            <SectionAgentBadge nk="stratification" />
          </div>
          {/* Introduction */}
          {s5.extracted_parameters?.section5_introduction && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <p className={`text-xs leading-relaxed ${themeText}`}>{s5.extracted_parameters.section5_introduction}</p>
            </div>
          )}
          {/* Response Writeup */}
          {responses.map((r, i) => (
            <div key={i} className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="text-xs font-semibold text-cyan-400 mb-2">{r.theme}</div>
              <p className={`text-xs leading-relaxed ${themeText}`}>{r.response}</p>
            </div>
          ))}

          {/* Expandable Patient Group Cards */}
          <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>Patient Groups — click to expand</div>
          <div className="grid grid-cols-2 gap-3">
            {stratTable.map(g => {
              const confidence = confidenceLookup[g.group_number] || 0;
              const markers = (g.key_genomic_markers || "").split(", ").filter(Boolean);
              const narrative = groupNarratives[`group${g.group_number}_narrative`];
              const rationale = confidenceRationale[`group${g.group_number}_rationale`];
              const isOpen = expandedGroup === g.group_number;
              return (
                <div key={g.group_number} className={`border rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${themeCard} ${isOpen ? "col-span-2" : ""}`} onClick={() => setExpandedGroup(isOpen ? null : g.group_number)}>
                  {/* Collapsed header — always visible */}
                  <div className="flex items-center gap-3 p-3">
                    <ConfidenceRing value={confidence} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-cyan-400 truncate">{g.group_name}</span>
                        <ChevronRight size={14} className={`${themeTextMuted} transition-transform duration-300 flex-shrink-0 ${isOpen ? "rotate-90" : ""}`} />
                      </div>
                      <div className={`text-[10px] ${themeTextMuted}`}>{g.population_percent} of population {g.dominant_category && `· ${g.dominant_category}`}</div>
                    </div>
                  </div>
                  {/* Expanded detail */}
                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
                    <div className={`border-t ${themeBorder} p-4 space-y-3`}>
                      {/* Genomic Markers */}
                      {markers.length > 0 && (
                        <div>
                          <div className={`text-[10px] font-semibold ${themeTextMuted} uppercase tracking-wider mb-1`}>Genomic Markers</div>
                          <div className="flex flex-wrap gap-1">{markers.map(m => <span key={m} className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>{m.trim()}</span>)}</div>
                        </div>
                      )}
                      {/* Age / Comorbidities / Clinical Scenario */}
                      {g.age_group && <div><span className={`text-[10px] font-semibold ${themeTextMuted}`}>Age: </span><span className={`text-[10px] ${themeText}`}>{g.age_group}</span></div>}
                      {g.comorbidities && <div><span className={`text-[10px] font-semibold ${themeTextMuted}`}>Comorbidities: </span><span className={`text-[10px] ${themeText}`}>{g.comorbidities}</span></div>}
                      {g.clinical_scenario && <div><span className={`text-[10px] font-semibold ${themeTextMuted}`}>Clinical Scenario: </span><span className={`text-[10px] ${themeText}`}>{g.clinical_scenario}</span></div>}
                      {/* Pathway Profile & Gaps */}
                      {g.pathway_profile && <div><span className={`text-[10px] font-semibold ${themeTextMuted}`}>Pathway Profile: </span><span className={`text-[10px] ${themeText}`}>{g.pathway_profile}</span></div>}
                      {g.pathway_gaps && <div><span className={`text-[10px] font-semibold ${themeTextMuted}`}>Pathway Gaps: </span><span className={`text-[10px] ${themeText}`}>{g.pathway_gaps}</span></div>}
                      {/* Confidence Rationale */}
                      {rationale && (
                        <div className={`${isDark ? "bg-slate-900/50" : "bg-slate-50"} rounded-lg p-3 mt-2`}>
                          <div className={`text-[10px] font-semibold ${themeTextMuted} uppercase tracking-wider mb-1`}>Confidence Rationale</div>
                          <p className={`text-[10px] leading-relaxed whitespace-pre-line ${themeText}`}>{rationale}</p>
                        </div>
                      )}
                      {/* Narrative */}
                      {narrative && (
                        <div className={`${isDark ? "bg-slate-900/50" : "bg-slate-50"} rounded-lg p-3`}>
                          <div className={`text-[10px] font-semibold ${themeTextMuted} uppercase tracking-wider mb-1`}>Clinical Narrative</div>
                          <p className={`text-[10px] leading-relaxed ${themeText}`}>{narrative}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Shared helper: render a projection table with optional drill-down
    const DOSE_COLORS = ['#06b6d4', '#a78bfa', '#f59e0b', '#f87171', '#34d399', '#fb923c'];
    const renderProjectionTable = (rows, therapies, { stratifiedRows, drillable } = {}) => {
      // Auto-detect value keys: try therapy1_value, therapy2_value (legacy) or any key ending in _value
      const getValueKeys = (row) => {
        if (!row) return [];
        const allKeys = Object.keys(row);
        const legacyKeys = therapies.map((_, i) => `therapy${i + 1}_value`);
        if (legacyKeys.every(k => k in row)) return legacyKeys;
        const dynamicKeys = allKeys.filter(k => k.endsWith("_value") && k !== "endpoint");
        return dynamicKeys.length === therapies.length ? dynamicKeys : legacyKeys;
      };
      const valueKeys = rows.length > 0 ? getValueKeys(rows[0]) : [];
      const hasDirection = rows.length > 0 && rows[0].interpretation;

      return (
      <table className="w-full text-sm">
        <thead><tr className={themeTableHead}>
          <th className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium`}>Endpoint</th>
          {hasDirection && <th className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium`}>Direction</th>}
          {therapies.map(t => <th key={t} className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium`}>{t}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((e, i) => {
            const isOpen = drillable && expandedEndpoint === e.endpoint;
            const barData = therapies.map((t, ti) => ({ name: t.length > 18 ? t.slice(0, 16) + '…' : t, value: parseFloat(e[valueKeys[ti]]) || 0, fill: DOSE_COLORS[ti % DOSE_COLORS.length] }));
            const matchingStrat = stratifiedRows ? Object.entries(
              stratifiedRows.filter(s => s.endpoint === e.endpoint).reduce((acc, s) => { const g = s.group_name || "Unknown"; if (!acc[g]) acc[g] = s; return acc; }, {})
            ) : [];
            return (
              <Fragment key={i}>
                <tr className={`${themeTableRow(i)} ${drillable ? "cursor-pointer hover:bg-cyan-500/10 transition-colors" : ""}`} onClick={drillable ? () => setExpandedEndpoint(isOpen ? null : e.endpoint) : undefined}>
                  <td className={`border ${themeBorder} px-3 py-2 text-xs ${themeText}`}>
                    <div className="flex items-center gap-1.5">
                      {drillable && <ChevronRight size={12} className={`${themeTextMuted} transition-transform duration-200 flex-shrink-0 ${isOpen ? "rotate-90" : ""}`} />}
                      <span>{e.endpoint}</span>
                      {e.unit && <span className={themeTextMuted}>({e.unit})</span>}
                    </div>
                  </td>
                  {hasDirection && <td className={`border ${themeBorder} px-3 py-2 text-xs ${themeTextMuted} italic`}>{e.interpretation}</td>}
                  {valueKeys.map((vk, ti) => (
                    <td key={ti} className={`border ${themeBorder} px-3 py-2 text-center font-mono text-xs ${ti < therapies.length - 1 ? "text-cyan-400" : themeTextMuted}`}>
                      {e[vk] ?? ""}
                    </td>
                  ))}
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={therapies.length + (hasDirection ? 2 : 1)} className={`border ${themeBorder} p-0`}>
                      <div className={`p-4 space-y-3 ${isDark ? "bg-slate-900/50" : "bg-slate-50"}`}>
                        {/* Bar chart comparison */}
                        <div className={`text-[10px] font-semibold ${themeTextMuted} uppercase tracking-wider`}>{e.endpoint} — Therapy Comparison</div>
                        <ResponsiveContainer width="100%" height={140}>
                          <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} />
                            <XAxis type="number" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 10 }} />
                            <YAxis dataKey="name" type="category" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} width={120} />
                            <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} />
                            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                              {barData.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                        {/* Stratified breakdown for this endpoint */}
                        {matchingStrat.length > 0 && (
                          <>
                            <div className={`text-[10px] font-semibold ${themeTextMuted} uppercase tracking-wider mt-2`}>Stratified by Patient Group</div>
                            <table className="w-full text-[10px]">
                              <thead><tr className={themeTableHead}>
                                <th className={`border ${themeBorder} px-2 py-1 ${themeText} font-medium`}>Group</th>
                                {therapies.map(t => <th key={t} className={`border ${themeBorder} px-2 py-1 ${themeText} font-medium`}>{t}</th>)}
                              </tr></thead>
                              <tbody>
                                {matchingStrat.map(([groupName, row], gi) => (
                                  <tr key={groupName} className={themeTableRow(gi)}>
                                    <td className={`border ${themeBorder} px-2 py-1 ${themeText}`}>{groupName}</td>
                                    {valueKeys.map((vk, ti) => (
                                      <td key={ti} className={`border ${themeBorder} px-2 py-1 text-center font-mono ${ti < therapies.length - 1 ? "text-cyan-400" : themeTextMuted}`}>{row[vk] ?? ""}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      );
    };

    // Helper: group rows by group_name preserving insertion order
    const groupByName = (rows) => {
      const groups = {};
      rows.forEach(row => {
        const g = row.group_name || "Unknown";
        if (!groups[g]) groups[g] = [];
        groups[g].push(row);
      });
      return groups;
    };

    // Build dynamic therapy column fallback from config
    const buildTherapyFallback = () => {
      const scenarios = (therapyConfig?.dose_scenarios || []).map(s => s.id || s.name || "Scenario");
      const comparators = (therapyConfig?.therapy?.comparators || []).map(c => c.name || "Comparator");
      return scenarios.length > 0 ? [...scenarios, ...comparators] : ["Scenario A", "Scenario B", "Scenario C", "Comparator"];
    };

    // Shared: dose toggle chip row
    const DoseToggles = ({ therapies: t }) => (
      <div className="flex flex-wrap gap-1.5 mb-2">
        {t.map((name, i) => {
          const active = visibleDoses[name] !== false;
          return (
            <button key={name} onClick={() => setVisibleDoses(prev => ({ ...prev, [name]: !active }))} className={`flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-full border transition-all ${active ? `${isDark ? "border-slate-600 bg-slate-800" : "border-slate-300 bg-white"} ${themeText}` : `${isDark ? "border-slate-700 bg-slate-900 opacity-40" : "border-slate-300 bg-slate-100 opacity-40"} line-through`}`}>
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: DOSE_COLORS[i % DOSE_COLORS.length] }} />
              {name.length > 25 ? name.slice(0, 23) + '…' : name}
            </button>
          );
        })}
      </div>
    );

    // Shared: themed radar chart wrapper
    const ThemedRadarSection = ({ title, therapies: t, radarData, dataKeyField = "endpoint", exportFilename }) => {
      const shown = t.filter(name => visibleDoses[name] !== false);
      return (
        <div className={`group border rounded-lg p-4 ${themeCard}`} data-chart-container="">
          <div className="flex items-center justify-between mb-2">
            <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>{title}</div>
            {exportFilename && (
              <div className="hidden group-hover:flex gap-1">
                <button onClick={() => exportChartAsJSON(radarData, exportFilename)} className={themeExportBtn}>JSON</button>
                <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), exportFilename)} className={themeExportBtn}>PNG</button>
              </div>
            )}
          </div>
          <DoseToggles therapies={t} />
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
              <PolarGrid stroke={isDark ? "#334155" : "#e2e8f0"} />
              <PolarAngleAxis dataKey={dataKeyField} tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 10 }} />
              <PolarRadiusAxis tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 9 }} axisLine={false} />
              {shown.map((name) => {
                const ci = t.indexOf(name);
                return <Radar key={name} name={name} dataKey={name} stroke={DOSE_COLORS[ci % DOSE_COLORS.length]} fill={DOSE_COLORS[ci % DOSE_COLORS.length]} fillOpacity={0.12 + ci * 0.05} strokeWidth={2} />;
              })}
              <Legend wrapperStyle={{ fontSize: 11, color: isDark ? '#cbd5e1' : '#475569' }} />
              <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} labelStyle={{ color: isDark ? '#e2e8f0' : '#1e293b' }} itemStyle={{ color: isDark ? '#94a3b8' : '#475569' }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      );
    };

    // Section 6: Efficacy (sec6)
    if (navKey === "efficacy") {
      const s6 = sec("sec6");
      const ep = s6.extracted_parameters || {};
      const endpoints = ep.overall_endpoints || [];
      const stratified = ep.stratified_projections || [];
      const therapies = ep.therapies || buildTherapyFallback();
      const responses = s6.response || [];
      const stratGroups = groupByName(stratified);

      // Lazy-init dose visibility
      if (therapies.length > 0 && !Object.keys(visibleDoses).length) {
        setVisibleDoses(Object.fromEntries(therapies.map(t => [t, true])));
      }

      // Build radar data
      const baseData = endpoints.map(e => {
        const row = { endpoint: e.endpoint?.length > 22 ? e.endpoint.slice(0, 20) + '…' : e.endpoint };
        therapies.forEach((t, ti) => { row[t] = parseFloat(e[`therapy${ti + 1}_value`]) || 0; });
        return row;
      });
      const stratEndpoints = stratified.reduce((acc, r) => {
        if (!acc.find(a => a.endpoint === r.endpoint)) {
          const row = { endpoint: r.endpoint?.length > 22 ? r.endpoint.slice(0, 20) + '…' : r.endpoint };
          therapies.forEach((t, ti) => { row[t] = parseFloat(r[`therapy${ti + 1}_value`]) || 0; });
          acc.push(row);
        }
        return acc;
      }, []);
      const combined = [...baseData];
      stratEndpoints.forEach(se => { if (!combined.find(c => c.endpoint === se.endpoint) && combined.length < 8) combined.push(se); });
      const radarData = combined.length >= 3 ? combined : [...combined, ...Array.from({ length: 3 - combined.length }, () => ({ endpoint: '—', ...Object.fromEntries(therapies.map(t => [t, 0])) }))];

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 6: Efficacy Projections</h2>
            <SectionAgentBadge nk="efficacy" />
          </div>
          {responses.map((r, i) => (
            <div key={i} className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="text-xs font-semibold text-cyan-400 mb-2">{r.theme}</div>
              <p className={`text-xs leading-relaxed ${themeText}`}>{r.response}</p>
            </div>
          ))}
          {/* Radar with dose toggles */}
          {endpoints.length > 0 && therapies.length > 0 && (
            <ThemedRadarSection title="Efficacy Radar: Endpoints × Dose Scenarios" therapies={therapies} radarData={radarData} exportFilename="efficacy-radar" />
          )}

          <h3 className={`text-sm font-semibold ${themeText} mt-4`}>6.1: Overall Population Efficacy Projections</h3>
          <p className={`text-[10px] ${themeTextMuted} -mt-2`}>Click any endpoint row to drill down</p>
          {renderProjectionTable(endpoints, therapies, { stratifiedRows: stratified, drillable: true })}
          {endpoints.some(e => e.reference_timeframe) && (
            <div className={`text-[10px] ${themeTextMuted} mt-1 leading-relaxed`}>
              <span className={themeText}>Measurement timeframes: </span>
              {endpoints.filter(e => e.reference_timeframe).map((e, i, arr) => (
                <span key={i}>{e.endpoint}: {e.reference_timeframe}{i < arr.length - 1 ? "; " : "."}</span>
              ))}
            </div>
          )}
          {Object.keys(stratGroups).length > 0 && (
            <>
              <h3 className={`text-sm font-semibold ${themeText} mt-6`}>6.2: Stratified Efficacy Projections by Group</h3>
              {Object.entries(stratGroups).map(([groupName, rows]) => (
                <div key={groupName} className="mt-3">
                  <h4 className="text-xs font-semibold text-cyan-400 mb-2">{groupName}</h4>
                  {renderProjectionTable(rows, therapies)}
                </div>
              ))}
            </>
          )}
        </div>
      );
    }

    // Section 7: Safety (sec7)
    if (navKey === "safety") {
      const s7 = sec("sec7");
      const ep = s7.extracted_parameters || {};
      const endpoints = ep.overall_safety_endpoints || [];
      const stratified = ep.stratified_safety_projections || [];
      const therapies = ep.therapies || buildTherapyFallback();
      const responses = s7.response || [];
      const stratGroups = groupByName(stratified);

      // Lazy-init dose visibility
      if (therapies.length > 0 && !Object.keys(visibleDoses).length) {
        setVisibleDoses(Object.fromEntries(therapies.map(t => [t, true])));
      }

      // Build radar data
      const baseData = endpoints.map(e => {
        const row = { endpoint: e.endpoint?.length > 22 ? e.endpoint.slice(0, 20) + '…' : e.endpoint };
        therapies.forEach((t, ti) => { row[t] = parseFloat(e[`therapy${ti + 1}_value`]) || 0; });
        return row;
      });
      const stratEndpoints = stratified.reduce((acc, r) => {
        if (!acc.find(a => a.endpoint === r.endpoint)) {
          const row = { endpoint: r.endpoint?.length > 22 ? r.endpoint.slice(0, 20) + '…' : r.endpoint };
          therapies.forEach((t, ti) => { row[t] = parseFloat(r[`therapy${ti + 1}_value`]) || 0; });
          acc.push(row);
        }
        return acc;
      }, []);
      const combined = [...baseData];
      stratEndpoints.forEach(se => { if (!combined.find(c => c.endpoint === se.endpoint) && combined.length < 8) combined.push(se); });
      const radarData = combined.length >= 3 ? combined : [...combined, ...Array.from({ length: 3 - combined.length }, () => ({ endpoint: '—', ...Object.fromEntries(therapies.map(t => [t, 0])) }))];

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 7: Safety Projections</h2>
            <SectionAgentBadge nk="safety" />
          </div>
          {responses.map((r, i) => (
            <div key={i} className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="text-xs font-semibold text-cyan-400 mb-2">{r.theme}</div>
              <p className={`text-xs leading-relaxed ${themeText}`}>{r.response}</p>
            </div>
          ))}
          {/* Radar with dose toggles */}
          {endpoints.length > 0 && therapies.length > 0 && (
            <ThemedRadarSection title="Safety Radar: Endpoints × Dose Scenarios" therapies={therapies} radarData={radarData} exportFilename="safety-radar" />
          )}

          <h3 className={`text-sm font-semibold ${themeText} mt-4`}>7.1: Overall Population Safety Projections</h3>
          <p className={`text-[10px] ${themeTextMuted} -mt-2`}>Click any endpoint row to drill down</p>
          {renderProjectionTable(endpoints, therapies, { stratifiedRows: stratified, drillable: true })}
          {endpoints.some(e => e.reference_timeframe) && (
            <div className={`text-[10px] ${themeTextMuted} mt-1 leading-relaxed`}>
              <span className={themeText}>Observation periods: </span>
              {endpoints.filter(e => e.reference_timeframe).map((e, i, arr) => (
                <span key={i}>{e.endpoint}: {e.reference_timeframe}{i < arr.length - 1 ? "; " : "."}</span>
              ))}
            </div>
          )}
          {Object.keys(stratGroups).length > 0 && (
            <>
              <h3 className={`text-sm font-semibold ${themeText} mt-6`}>7.2: Stratified Safety Projections by Group</h3>
              {Object.entries(stratGroups).map(([groupName, rows]) => (
                <div key={groupName} className="mt-3">
                  <h4 className="text-xs font-semibold text-cyan-400 mb-2">{groupName}</h4>
                  {renderProjectionTable(rows, therapies)}
                </div>
              ))}
            </>
          )}
        </div>
      );
    }

    // Combined Clinical Radar: Sections 5, 6, 7 across doses
    if (activeSection === "clinical-radar") {
      const s5 = sec("sec5");
      const s6 = sec("sec6");
      const s7 = sec("sec7");
      const ep6 = s6.extracted_parameters || {};
      const ep7 = s7.extracted_parameters || {};
      const efficacyEndpoints = ep6.overall_endpoints || [];
      const safetyEndpoints = ep7.overall_safety_endpoints || [];
      const therapies = ep6.therapies || ep7.therapies || buildTherapyFallback();
      const stratTable = s5.extracted_parameters?.section5_1_stratification_table || [];
      const confidenceTable = s5.extracted_parameters?.section5_confidence_table || [];
      const confLookup = {};
      confidenceTable.forEach(c => { confLookup[c.group_number] = parseInt(c.confidence_score) || 0; });

      // Lazy-init dose visibility
      if (therapies.length > 0 && !Object.keys(visibleDoses).length) {
        setVisibleDoses(Object.fromEntries(therapies.map(t => [t, true])));
      }

      // Dynamic slice: show up to 6 endpoints per type (not hardcoded 4)
      const maxEffAxes = Math.min(efficacyEndpoints.length, 6);
      const maxSafAxes = Math.min(safetyEndpoints.length, 6);

      // Build combined radar data: one axis per key metric across all 3 sections
      const combinedData = [];
      efficacyEndpoints.slice(0, maxEffAxes).forEach(e => {
        const row = { metric: (e.endpoint?.length > 20 ? e.endpoint.slice(0, 18) + '…' : e.endpoint) + ' (eff)', type: 'efficacy' };
        therapies.forEach((t, ti) => { row[t] = parseFloat(e[`therapy${ti + 1}_value`]) || 0; });
        combinedData.push(row);
      });
      safetyEndpoints.slice(0, maxSafAxes).forEach(e => {
        const row = { metric: (e.endpoint?.length > 20 ? e.endpoint.slice(0, 18) + '…' : e.endpoint) + ' (saf)', type: 'safety' };
        therapies.forEach((t, ti) => { row[t] = parseFloat(e[`therapy${ti + 1}_value`]) || 0; });
        combinedData.push(row);
      });
      stratTable.slice(0, 3).forEach(g => {
        const row = { metric: (g.group_name?.length > 20 ? g.group_name.slice(0, 18) + '…' : g.group_name) + ' (conf)', type: 'strat' };
        therapies.forEach(t => { row[t] = confLookup[g.group_number] || 0; });
        combinedData.push(row);
      });

      const hasData = combinedData.length > 0;
      const shown = therapies.filter(name => visibleDoses[name] !== false);

      // Per-dose individual radars (only for visible doses)
      const doseEfficacyRadars = therapies.filter(t => visibleDoses[t] !== false).map((therapy) => {
        const ti = therapies.indexOf(therapy);
        const data = efficacyEndpoints.map(e => ({
          endpoint: e.endpoint?.length > 20 ? e.endpoint.slice(0, 18) + '…' : e.endpoint,
          value: parseFloat(e[`therapy${ti + 1}_value`]) || 0,
        }));
        return { therapy, data, color: DOSE_COLORS[ti % DOSE_COLORS.length] };
      });
      const doseSafetyRadars = therapies.filter(t => visibleDoses[t] !== false).map((therapy) => {
        const ti = therapies.indexOf(therapy);
        const data = safetyEndpoints.map(e => ({
          endpoint: e.endpoint?.length > 20 ? e.endpoint.slice(0, 18) + '…' : e.endpoint,
          value: parseFloat(e[`therapy${ti + 1}_value`]) || 0,
        }));
        return { therapy, data, color: DOSE_COLORS[ti % DOSE_COLORS.length] };
      });

      const tooltipStyle = { backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 };
      const tickFill = isDark ? '#94a3b8' : '#64748b';
      const gridStroke = isDark ? "#334155" : "#e2e8f0";

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Clinical Projections Radar — All Doses</h2>
            <SectionAgentBadge nk="clinical-radar" />
          </div>
          <p className={`text-xs ${themeTextMuted}`}>Combined spiderweb view of Patient Stratification (Sec 5), Efficacy (Sec 6), and Safety (Sec 7) across dose scenarios.</p>

          {/* Dose Toggle */}
          <DoseToggles therapies={therapies} />

          {/* Combined Overlay Radar */}
          {hasData && (
            <div className={`group border rounded-lg p-4 ${themeCard}`} data-chart-container="">
              <div className="flex items-center justify-between mb-2">
                <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>Combined: Efficacy + Safety + Stratification</div>
                <div className="hidden group-hover:flex gap-1">
                  <button onClick={() => exportChartAsJSON(combinedData, "combined-overlay")} className={themeExportBtn}>JSON</button>
                  <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "combined-overlay")} className={themeExportBtn}>PNG</button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <RadarChart cx="50%" cy="50%" outerRadius="65%" data={combinedData}>
                  <PolarGrid stroke={gridStroke} />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: tickFill, fontSize: 9 }} />
                  <PolarRadiusAxis tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 9 }} axisLine={false} />
                  {shown.map((name) => {
                    const ci = therapies.indexOf(name);
                    return <Radar key={name} name={name} dataKey={name} stroke={DOSE_COLORS[ci % DOSE_COLORS.length]} fill={DOSE_COLORS[ci % DOSE_COLORS.length]} fillOpacity={0.1 + ci * 0.05} strokeWidth={2} />;
                  })}
                  <Legend wrapperStyle={{ fontSize: 11, color: isDark ? '#cbd5e1' : '#475569' }} />
                  <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: isDark ? '#e2e8f0' : '#1e293b' }} itemStyle={{ color: tickFill }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Per-Dose Efficacy Radars */}
          {doseEfficacyRadars.some(d => d.data.length > 0) && (
            <div className={`group border rounded-lg p-4 ${themeCard}`} data-chart-container="">
              <div className="flex items-center justify-between mb-3">
                <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>Efficacy by Dose</div>
                <div className="hidden group-hover:flex gap-1">
                  <button onClick={() => exportChartAsJSON(doseEfficacyRadars, "dose-efficacy")} className={themeExportBtn}>JSON</button>
                  <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "dose-efficacy")} className={themeExportBtn}>PNG</button>
                </div>
              </div>
              <div className={`grid gap-3 ${doseEfficacyRadars.length <= 2 ? 'grid-cols-2' : doseEfficacyRadars.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {doseEfficacyRadars.map(({ therapy, data, color }) => data.length > 0 && (
                  <div key={therapy}>
                    <div className="text-[10px] font-semibold text-center mb-1" style={{ color }}>{therapy}</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
                        <PolarGrid stroke={gridStroke} />
                        <PolarAngleAxis dataKey="endpoint" tick={{ fill: tickFill, fontSize: 8 }} />
                        <PolarRadiusAxis tick={false} axisLine={false} />
                        <Radar dataKey="value" stroke={color} fill={color} fillOpacity={0.25} strokeWidth={2} />
                        <Tooltip contentStyle={tooltipStyle} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-Dose Safety Radars */}
          {doseSafetyRadars.some(d => d.data.length > 0) && (
            <div className={`group border rounded-lg p-4 ${themeCard}`} data-chart-container="">
              <div className="flex items-center justify-between mb-3">
                <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>Safety by Dose</div>
                <div className="hidden group-hover:flex gap-1">
                  <button onClick={() => exportChartAsJSON(doseSafetyRadars, "dose-safety")} className={themeExportBtn}>JSON</button>
                  <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "dose-safety")} className={themeExportBtn}>PNG</button>
                </div>
              </div>
              <div className={`grid gap-3 ${doseSafetyRadars.length <= 2 ? 'grid-cols-2' : doseSafetyRadars.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {doseSafetyRadars.map(({ therapy, data, color }) => data.length > 0 && (
                  <div key={therapy}>
                    <div className="text-[10px] font-semibold text-center mb-1" style={{ color }}>{therapy}</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
                        <PolarGrid stroke={gridStroke} />
                        <PolarAngleAxis dataKey="endpoint" tick={{ fill: tickFill, fontSize: 8 }} />
                        <PolarRadiusAxis tick={false} axisLine={false} />
                        <Radar dataKey="value" stroke={color} fill={color} fillOpacity={0.25} strokeWidth={2} />
                        <Tooltip contentStyle={tooltipStyle} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!hasData && (
            <div className={`border rounded-lg p-8 text-center ${themeCard}`}>
              <p className={`text-sm ${themeTextMuted}`}>No projection data available yet. Run the assessment to populate sections 5, 6, and 7.</p>
            </div>
          )}
        </div>
      );
    }

    // Section 8: Clinical Summary (sec8)
    if (navKey === "summary") {
      const s8 = sec("sec8");
      const ep8 = s8.extracted_parameters || {};
      const rows = ep8.comparison_rows || [];
      const therapies = ep8.therapies || buildTherapyFallback();
      const conclusion = ep8.clinical_conclusion || {};

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 8: Comparative Therapy Analysis</h2>
            <SectionAgentBadge nk="summary" />
          </div>
          <div className="flex items-center justify-between">
            <h3 className={`text-sm font-semibold ${themeText}`}>8.1: Comparison Summary</h3>
            <div className={`flex items-center gap-1 p-0.5 rounded-md ${isDark ? "bg-slate-700/50" : "bg-slate-100"}`}>
              <button onClick={() => setViewMode("visual")} className={`p-1 rounded transition-colors ${viewMode === "visual" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Visual view"><BarChart2 size={12} /></button>
              <button onClick={() => setViewMode("table")} className={`p-1 rounded transition-colors ${viewMode === "table" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Table view"><Table2 size={12} /></button>
            </div>
          </div>
          {(() => {
            const hasDir = rows.length > 0 && rows[0].interpretation;
            return viewMode === "table" ? (
              <table className="w-full text-sm">
                <thead><tr className={themeTableHead}>
                  {["Category", "Metric"].map(h => <th key={h} className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium`}>{h}</th>)}
                  {hasDir && <th className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium`}>Direction</th>}
                  {therapies.map(t => <th key={t} className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium`}>{t}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={themeTableRow(i)}>
                      <td className={`border ${themeBorder} px-3 py-2 text-xs ${themeText}`}>{r.category}</td>
                      <td className={`border ${themeBorder} px-3 py-2 text-xs ${themeText}`}>{r.metric}</td>
                      {hasDir && <td className={`border ${themeBorder} px-3 py-2 text-xs ${themeTextMuted} italic`}>{r.interpretation}</td>}
                      {therapies.map((_, ti) => (
                        <td key={ti} className={`border ${themeBorder} px-3 py-2 text-center font-mono text-xs ${ti < therapies.length - 1 ? "text-cyan-400" : themeTextMuted}`}>
                          {r[`therapy${ti + 1}_value`] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="space-y-3">
                {rows.map((r, i) => (
                  <div key={i} className={`border rounded-lg p-3 ${themeCard}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase ${isDark ? "bg-cyan-500/10 text-cyan-300" : "bg-cyan-50 text-cyan-600"}`}>{r.category}</span>
                      <span className={`text-xs font-medium ${themeText}`}>{r.metric}</span>
                    </div>
                    <div className="flex gap-2">
                      {therapies.map((t, ti) => (
                        <div key={ti} className={`flex-1 text-center p-2 rounded ${isDark ? "bg-slate-900/50" : "bg-slate-50"}`}>
                          <div className={`text-xs font-mono font-bold ${ti < therapies.length - 1 ? "text-cyan-400" : themeTextMuted}`}>{r[`therapy${ti + 1}_value`] ?? "—"}</div>
                          <div className={`text-[9px] mt-0.5 ${themeTextMuted}`}>{t}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          <h3 className={`text-sm font-semibold ${themeText} mt-4`}>8.2: Clinical Conclusion</h3>
          {/* Clinical Conclusion — render all keys dynamically */}
          {Object.entries(conclusion).map(([key, text]) => text && (
            <div key={key} className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-2 uppercase tracking-wider`}>{key.replace(/_/g, " ")}</div>
              <p className={`text-xs ${themeText}`}>{text}</p>
            </div>
          ))}
        </div>
      );
    }

    // Section 9: Competitor Analysis & Positioning (sec9) — multi-competitor layout
    if (navKey === "market") {
      const s9 = sec("sec9");
      // Support both old (single-competitor) and new (multi-competitor) schemas
      const primaryAnalyses = s9.primary_analyses || [];
      const secondaryAnalyses = s9.secondary_analyses || [];
      const pipelineWatch = s9.pipeline_watch || [];
      const landscapeOverview = s9.landscape_overview || "";
      const positioning = s9.overall_positioning || s9.positioning_strategy || {};
      const responses = s9.response || [];
      // Fallback for old schema: convert key_competitors to primaryAnalyses format
      const oldCompetitors = s9.key_competitors || [];
      const oldSwot = s9.swot_analysis || {};
      const oldH2h = s9.head_to_head_differentiation || {};

      // Threat overview chart data
      const allCompetitors = [
        ...primaryAnalyses.map(c => ({ name: c.competitor_name, threat: threatScore(c.threat_level), level: c.threat_level, type: "Primary" })),
        ...secondaryAnalyses.map(c => ({ name: c.competitor_name, threat: threatScore(c.threat_level), level: c.threat_level, type: "Secondary" })),
        ...pipelineWatch.map(c => ({ name: c.name, threat: threatScore(c.threat_level), level: c.threat_level, type: "Pipeline" })),
      ].filter(c => c.name).sort((a, b) => b.threat - a.threat);

      // Scorecard radar data
      const scorecardCompetitors = primaryAnalyses.filter(pa => pa.scorecard?.efficacy && pa.scorecard?.safety);
      const scorecardRadarData = scorecardCompetitors.length >= 2 ? [
        { axis: "Efficacy", ...Object.fromEntries(scorecardCompetitors.map(pa => [pa.competitor_name, ratingScore(pa.scorecard.efficacy?.rating)])) },
        { axis: "Safety",   ...Object.fromEntries(scorecardCompetitors.map(pa => [pa.competitor_name, ratingScore(pa.scorecard.safety?.rating)])) },
      ] : [];

      const threatColor = (level) => level === "high" ? "bg-red-500/20 text-red-400" : level === "medium" ? "bg-amber-500/20 text-amber-400" : (isDark ? "bg-slate-700 text-slate-400" : "bg-slate-100 text-slate-500");
      const impactColor = (impact) => impact === "high" ? "text-cyan-400" : impact === "medium" ? themeText : themeTextMuted;
      const ratingColor = (rating) => {
        if (!rating) return themeTextMuted;
        const r = rating.toLowerCase();
        if (r.includes("strong advantage")) return "text-green-400";
        if (r.includes("advantage")) return "text-green-300";
        if (r.includes("comparable")) return "text-amber-400";
        if (r.includes("disadvantage")) return "text-red-400";
        return themeTextMuted;
      };

      // Render SWOT grid (reused for each primary competitor)
      const renderSwot = (swot) => (
        <div className="grid grid-cols-2 gap-2 mt-3">
          {[["Strengths", swot.strengths, "text-green-400", "border-green-500/20"],
            ["Weaknesses", swot.weaknesses, "text-red-400", "border-red-500/20"],
            ["Opportunities", swot.opportunities, "text-cyan-400", "border-cyan-500/20"],
            ["Threats", swot.threats, "text-amber-400", "border-amber-500/20"]
          ].map(([title, items, titleColor, borderColor]) => (
            <div key={title} className={`${isDark ? "bg-slate-800/50" : "bg-white"} border ${borderColor} rounded-lg p-2.5`}>
              <div className={`text-[10px] font-semibold ${titleColor} mb-1.5 uppercase tracking-wider`}>{title}</div>
              <ul className="space-y-1">
                {(items || []).map((item, i) => (
                  <li key={i} className={`text-[10px] ${themeText}`}>
                    <span className={`font-medium ${impactColor(item.impact)}`}>{item.factor}:</span>{" "}
                    <span className={themeTextMuted}>{item.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 9: Competitor Analysis & Positioning Strategy</h2>
            <SectionAgentBadge nk="market" />
          </div>

          {/* Landscape Overview */}
          {landscapeOverview && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="text-xs font-semibold text-cyan-400 mb-2">9.1 Competitive Landscape Overview</div>
              <p className={`text-xs ${themeText} leading-relaxed whitespace-pre-line`}>{landscapeOverview}</p>
            </div>
          )}

          {/* Narrative Writeups (response array) */}
          {!landscapeOverview && responses.map((r, i) => (
            <div key={i} className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="text-xs font-semibold text-cyan-400 mb-2">{r.theme}</div>
              <p className={`text-xs ${themeText} leading-relaxed whitespace-pre-line`}>{r.response}</p>
            </div>
          ))}

          {/* Threat Overview Bar Chart */}
          {allCompetitors.length > 0 && (
            <div className={`group border rounded-lg p-4 ${themeCard}`} data-chart-container="">
              <div className="flex items-center justify-between mb-3">
                <div className={`text-xs font-semibold uppercase tracking-wider ${themeTextMuted}`}>Competitive Threat Overview</div>
                <div className="hidden group-hover:flex gap-1">
                  <button onClick={() => exportChartAsJSON(allCompetitors, "competitive-threat")} className={themeExportBtn}>JSON</button>
                  <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "competitive-threat")} className={themeExportBtn}>PNG</button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={Math.max(120, allCompetitors.length * 32)}>
                <BarChart data={allCompetitors} layout="vertical" margin={{ left: 4, right: 40, top: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} horizontal={false} />
                  <XAxis type="number" domain={[0, 3]} ticks={[1, 2, 3]} tickFormatter={v => ["", "Low", "Med", "High"][v] || ""} tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: isDark ? '#cbd5e1' : '#475569', fontSize: 9 }} width={130} axisLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }}
                    formatter={(v, n, p) => [p.payload.level, "Threat"]}
                    labelStyle={{ color: isDark ? '#e2e8f0' : '#1e293b' }}
                  />
                  <Bar dataKey="threat" radius={[0, 4, 4, 0]} barSize={14}>
                    {allCompetitors.map((c, i) => (
                      <Cell key={i} fill={c.level === "high" ? "#f87171" : c.level === "medium" ? "#f59e0b" : "#64748b"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* PRIMARY COMPETITORS — Expandable Deep-Dive */}
          {primaryAnalyses.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold ${themeText} mb-3`}>9.2 Primary Competitor Deep-Dive</h3>
              <div className="space-y-3">
                {primaryAnalyses.map((pa, i) => {
                  const isOpen = expandedCompetitor === i;
                  return (
                    <div key={i} className={`border rounded-lg overflow-hidden ${isDark ? "bg-slate-800/30 border-cyan-500/15" : "bg-white border-slate-200 shadow-sm"}`}>
                      {/* Clickable header — always visible */}
                      <button
                        className={`w-full flex items-center gap-2 p-4 text-left transition-colors ${isDark ? "hover:bg-slate-800/60" : "hover:bg-slate-50"}`}
                        onClick={() => setExpandedCompetitor(isOpen ? null : i)}
                      >
                        <ChevronRight size={14} className={`${themeTextMuted} flex-shrink-0 transition-transform duration-300 ${isOpen ? "rotate-90" : ""}`} />
                        <span className="text-sm font-semibold text-cyan-400 flex-1 text-left">{pa.competitor_name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded flex-shrink-0 ${threatColor(pa.threat_level)}`}>{pa.threat_level}</span>
                        {pa.status && <span className={`text-[9px] flex-shrink-0 ${themeTextMuted}`}>{pa.status}</span>}
                        {pa.company && <span className={`text-[10px] flex-shrink-0 ${themeTextMuted} hidden sm:block`}>{pa.company}</span>}
                      </button>
                      {/* Always-visible mechanism line */}
                      {pa.mechanism && <div className={`px-4 pb-2 text-[10px] ${themeTextMuted}`}>{pa.mechanism}</div>}
                      {/* Collapsible detail */}
                      <div className={`transition-all duration-300 overflow-hidden ${isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}>
                        <div className={`px-4 pb-4 space-y-3 border-t ${themeBorder}`}>
                          {pa.threat_rationale && <p className={`text-[10px] italic mt-3 ${themeTextMuted}`}>{pa.threat_rationale}</p>}
                          {pa.narrative && <p className={`text-xs leading-relaxed ${themeText}`}>{pa.narrative}</p>}
                          {pa.head_to_head?.dimensions?.length > 0 && (
                            <div>
                              <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${themeTextMuted}`}>Head-to-Head</div>
                              <table className="w-full text-xs">
                                <thead><tr className={themeTableHead}>
                                  <th className={`border ${themeBorder} px-2 py-1.5 text-[10px] text-left font-medium ${themeTextMuted}`}>Dimension</th>
                                  <th className={`border ${themeBorder} px-2 py-1.5 text-[10px] text-left font-medium ${themeTextMuted}`}>{s9.therapy?.name || "Therapy"} Advantage</th>
                                  <th className={`border ${themeBorder} px-2 py-1.5 text-[10px] text-left font-medium ${themeTextMuted}`}>{pa.competitor_name} Advantage</th>
                                </tr></thead>
                                <tbody>
                                  {pa.head_to_head.dimensions.map((dim, di) => (
                                    <tr key={di} className={themeTableRow(di)}>
                                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] text-cyan-400 font-medium`}>{dim.dimension}</td>
                                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeText}`}>{dim.therapy_advantage}</td>
                                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeText}`}>{dim.competitor_advantage}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {pa.head_to_head.net_assessment && <p className={`text-[10px] mt-1.5 italic ${themeTextMuted}`}>{pa.head_to_head.net_assessment}</p>}
                            </div>
                          )}
                          {pa.scorecard && (
                            <div className="flex gap-2 flex-wrap">
                              {["efficacy", "safety"].map(dim => pa.scorecard[dim] && (
                                <div key={dim} className={`rounded px-3 py-1.5 ${isDark ? "bg-slate-800/60" : "bg-slate-100"}`}>
                                  <span className={`text-[9px] uppercase ${themeTextMuted}`}>{dim}: </span>
                                  <span className={`text-[10px] font-medium ${ratingColor(pa.scorecard[dim].rating)}`}>{pa.scorecard[dim].rating}</span>
                                  {pa.scorecard[dim].detail && <span className={`text-[9px] ml-1 ${themeTextMuted}`}>— {pa.scorecard[dim].detail}</span>}
                                </div>
                              ))}
                              {pa.scorecard.overall_position && (
                                <div className={`rounded px-3 py-1.5 ${isDark ? "bg-slate-800/60" : "bg-slate-100"}`}>
                                  <span className={`text-[9px] uppercase ${themeTextMuted}`}>Overall: </span>
                                  <span className={`text-[10px] font-medium ${ratingColor(pa.scorecard.overall_position)}`}>{pa.scorecard.overall_position}</span>
                                </div>
                              )}
                            </div>
                          )}
                          {pa.swot && Object.keys(pa.swot).length > 0 && renderSwot(pa.swot)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Scorecard Comparison Radar */}
          {scorecardRadarData.length >= 2 && (
            <div className={`group border rounded-lg p-4 ${themeCard}`} data-chart-container="">
              <div className="flex items-center justify-between mb-3">
                <div className={`text-xs font-semibold uppercase tracking-wider ${themeTextMuted}`}>Competitor Scorecard Comparison</div>
                <div className="hidden group-hover:flex gap-1">
                  <button onClick={() => exportChartAsJSON(scorecardRadarData, "scorecard-comparison")} className={themeExportBtn}>JSON</button>
                  <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "scorecard-comparison")} className={themeExportBtn}>PNG</button>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={scorecardRadarData}>
                  <PolarGrid stroke={isDark ? "#334155" : "#e2e8f0"} />
                  <PolarAngleAxis dataKey="axis" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 11 }} />
                  <PolarRadiusAxis domain={[0, 5]} tick={false} axisLine={false} />
                  {scorecardCompetitors.map((pa, ci) => (
                    <Radar key={pa.competitor_name} name={pa.competitor_name} dataKey={pa.competitor_name}
                      stroke={DOSE_COLORS[ci % DOSE_COLORS.length]} fill={DOSE_COLORS[ci % DOSE_COLORS.length]}
                      fillOpacity={0.12 + ci * 0.05} strokeWidth={2} />
                  ))}
                  <Legend wrapperStyle={{ fontSize: 11, color: isDark ? '#cbd5e1' : '#475569' }} />
                  <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} labelStyle={{ color: isDark ? '#e2e8f0' : '#1e293b' }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Fallback for old single-competitor schema */}
          {primaryAnalyses.length === 0 && oldCompetitors.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold ${themeTextBright} mb-3`}>Key Competitors</h3>
              <div className="grid grid-cols-1 gap-2">
                {oldCompetitors.map((c, i) => (
                  <div key={i} className={`border rounded-lg p-3 ${themeCard}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-cyan-400">{c.name}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${threatColor(c.threat_level)}`}>{c.threat_level}</span>
                    </div>
                    <div className={`text-[10px] ${themeTextMuted} mb-1`}>{c.company} &middot; {c.mechanism}</div>
                    {c.threat_rationale && <p className={`text-[10px] ${themeTextMuted}`}>{c.threat_rationale}</p>}
                  </div>
                ))}
              </div>
              {/* Old scorecard */}
              {(oldH2h.scorecard || []).length > 0 && (
                <table className="w-full text-sm mt-3">
                  <thead><tr className={themeTableHead}>
                    {["Competitor", "Efficacy", "Safety", "Overall"].map(h => (
                      <th key={h} className={`border ${themeBorder} px-3 py-2 ${themeText} text-xs font-medium`}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {oldH2h.scorecard.map((row, i) => (
                      <tr key={i} className={themeTableRow(i)}>
                        <td className={`border ${themeBorder} px-3 py-2 text-xs text-cyan-400 font-medium`}>{row.competitor}</td>
                        {["efficacy", "safety", "overall_position"].map(dim => (
                          <td key={dim} className={`border ${themeBorder} px-3 py-2 text-center text-xs ${themeText}`}>{row[dim]?.rating || row[dim] || ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {/* Old SWOT */}
              {Object.keys(oldSwot).length > 0 && renderSwot(oldSwot)}
            </div>
          )}

          {/* SECONDARY COMPETITORS — Summary */}
          {secondaryAnalyses.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold ${themeTextBright} mb-3`}>9.3 Additional Competitors</h3>
              <div className="space-y-2">
                {secondaryAnalyses.map((sa, i) => (
                  <div key={i} className={`border rounded-lg p-3 ${themeCard}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`text-xs font-semibold ${themeText}`}>{sa.competitor_name}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${threatColor(sa.threat_level)}`}>{sa.threat_level}</span>
                      <span className={`text-[9px] ${themeTextMuted}`}>{sa.status}</span>
                      <span className={`text-[10px] ${themeTextMuted}`}>{sa.company}</span>
                    </div>
                    {sa.narrative && <p className={`text-[10px] ${themeTextMuted} leading-relaxed mb-2`}>{sa.narrative}</p>}
                    {(sa.key_differentiators || []).length > 0 && (
                      <ul className="space-y-0.5">
                        {sa.key_differentiators.map((d, di) => (
                          <li key={di} className={`text-[10px] ${themeTextMuted} flex gap-1.5`}>
                            <span className="text-slate-600 flex-shrink-0">•</span>
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PIPELINE WATCH */}
          {pipelineWatch.length > 0 && (
            <div>
              <h3 className={`text-sm font-semibold ${themeTextBright} mb-3`}>9.4 Pipeline Watch</h3>
              <table className="w-full text-xs">
                <thead><tr className={themeTableHead}>
                  {["Candidate", "Company", "Mechanism", "Stage", "Launch", "Threat", "Assessment"].map(h => (
                    <th key={h} className={`border ${themeBorder} px-2 py-1.5 ${themeTextMuted} text-[10px] font-medium`}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {pipelineWatch.map((pw, i) => (
                    <tr key={i} className={themeTableRow(i)}>
                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] text-cyan-400 font-medium`}>{pw.name}</td>
                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeText}`}>{pw.company}</td>
                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeText}`}>{pw.mechanism}</td>
                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeText}`}>{pw.stage}</td>
                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeText}`}>{pw.expected_launch}</td>
                      <td className={`border ${themeBorder} px-2 py-1.5 text-center`}><span className={`text-[9px] px-1.5 py-0.5 rounded ${threatColor(pw.threat_level)}`}>{pw.threat_level}</span></td>
                      <td className={`border ${themeBorder} px-2 py-1.5 text-[10px] ${themeTextMuted}`}>{pw.one_liner}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* OVERALL POSITIONING */}
          {positioning.core_positioning && (
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg p-4 space-y-3">
              <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>Overall Positioning Strategy</div>
              <p className={`text-xs ${themeText} leading-relaxed`}>{positioning.core_positioning}</p>
              {(positioning.key_advantages || []).length > 0 && (
                <div>
                  <div className="text-[10px] text-green-400 font-medium mb-1">Key Advantages</div>
                  <ul className="space-y-0.5">{positioning.key_advantages.map((a, i) => <li key={i} className={`text-[10px] ${themeText} flex gap-1.5`}><span className="text-green-400/60">+</span>{a}</li>)}</ul>
                </div>
              )}
              {(positioning.key_risks || []).length > 0 && (
                <div>
                  <div className="text-[10px] text-red-400 font-medium mb-1">Key Risks</div>
                  <ul className="space-y-0.5">{positioning.key_risks.map((r, i) => <li key={i} className={`text-[10px] ${themeText} flex gap-1.5`}><span className="text-red-400/60">-</span>{r}</li>)}</ul>
                </div>
              )}
              {positioning.strategy_rationale && <p className={`text-[10px] ${themeTextMuted} italic`}>{positioning.strategy_rationale}</p>}
              {positioning.key_messages && (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[["Prescribers", positioning.key_messages.prescribers], ["Payers", positioning.key_messages.payers], ["Patients", positioning.key_messages.patients]].map(([label, msg]) => msg && (
                    <div key={label} className={`rounded p-2 ${isDark ? "bg-slate-800/60" : "bg-slate-100"}`}>
                      <div className={`text-[9px] ${themeTextMuted} uppercase mb-0.5`}>{label}</div>
                      <div className={`text-[10px] ${themeText}`}>{msg}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Section 10: Commercial Opportunity (sec10)
    // Matches reference DOCX Section 9: overview, market assumptions, market sizing, revenue projections
    if (navKey === "valuation") {
      const s10 = sec("sec10");
      const sizing = s10.market_sizing || {};
      const years = sizing.table_years || [];
      const products = sizing.product_types || [];
      const projections = s10.annual_projections || [];


      const tamChartData = years.map((y, yi) => {
        const row = { year: y };
        products.forEach(p => { row[p.name] = parseRevenueValue(String(p.values?.[yi] || "0")); });
        return row;
      });

      const revenueChartData = projections.map(p => ({
        year: p.year,
        Bear: parseValStr(p.revenue_bear || p.revenue_bear_case || "0"),
        Base: parseValStr(p.revenue_base || p.revenue_base_case || "0"),
        Bull: parseValStr(p.revenue_bull || p.revenue_bull_case || "0"),
      }));

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 10: Commercial Opportunity</h2>
            <SectionAgentBadge nk="valuation" />
          </div>
          {/* Headline + Overview */}
          {(s10.market_summary_headline || s10.market_overview) && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              {s10.market_summary_headline && <p className="text-xs font-semibold text-cyan-400 mb-2">{s10.market_summary_headline}</p>}
              {s10.market_overview && <p className={`text-xs leading-relaxed ${themeText}`}>{s10.market_overview}</p>}
            </div>
          )}
          {/* 10.1 Key Market Assumptions */}
          {(s10.market_assumptions || []).length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold mb-2 uppercase tracking-wider ${themeTextMuted}`}>10.1 Key Market Assumptions</div>
              <ul className="space-y-1.5">
                {s10.market_assumptions.map((a, i) => (
                  <li key={i} className={`text-xs flex gap-2 ${themeText}`}><span className="text-cyan-500 mt-0.5">•</span><span>{a}</span></li>
                ))}
              </ul>
            </div>
          )}
          {/* 10.2 Market Sizing & TAM — with table/visual toggle (Task 72) */}
          {products.length > 0 && years.length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`text-xs font-semibold uppercase tracking-wider ${themeTextMuted}`}>10.2 Market Sizing & TAM</div>
                <div className={`flex items-center gap-1 p-0.5 rounded-md ${isDark ? "bg-slate-700/50" : "bg-slate-100"}`}>
                  <button onClick={() => setViewMode("visual")} className={`p-1 rounded transition-colors ${viewMode === "visual" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Visual view"><BarChart2 size={12} /></button>
                  <button onClick={() => setViewMode("table")} className={`p-1 rounded transition-colors ${viewMode === "table" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Table view"><Table2 size={12} /></button>
                </div>
              </div>
              {viewMode === "table" ? (
              <table className="w-full text-sm">
                <thead><tr className={`text-xs ${themeTextMuted}`}>
                  <th className="pb-2 text-left font-medium">Product Type</th>
                  {years.map(y => <th key={y} className="pb-2 text-left font-medium">{y}</th>)}
                  <th className="pb-2 text-left font-medium">CAGR</th>
                </tr></thead>
                <tbody>
                  {products.map((p, i) => (
                    <tr key={i} className={`border-t ${themeBorder}/50`}>
                      <td className={`py-2 text-xs ${themeText}`}>{p.name}</td>
                      {(p.values || []).map((v, j) => <td key={j} className={`py-2 font-mono text-xs ${themeText}`}>{v}</td>)}
                      <td className="py-2 font-mono text-xs text-green-400">{p.cagr}</td>
                    </tr>
                  ))}
                  {(sizing.total_tam_by_year || []).length > 0 && (
                    <tr className="border-t-2 border-cyan-500/30">
                      <td className="py-2 text-xs font-semibold text-cyan-400">Total TAM</td>
                      {sizing.total_tam_by_year.map((v, j) => <td key={j} className="py-2 font-mono text-xs font-semibold text-cyan-400">{v}</td>)}
                      <td className="py-2 font-mono text-xs font-semibold text-green-400">{sizing.overall_cagr}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              ) : (
              <div className="group" data-chart-container="">
                <div className="hidden group-hover:flex gap-1 justify-end mb-1">
                  <button onClick={() => exportChartAsJSON(tamChartData, "tam-growth")} className={themeExportBtn}>JSON</button>
                  <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "tam-growth")} className={themeExportBtn}>PNG</button>
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={tamChartData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} />
                    <XAxis dataKey="year" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                    <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                    <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    {products.map((p, i) => (
                      <Bar key={p.name} dataKey={p.name} stackId="tam" fill={DOSE_COLORS[i % DOSE_COLORS.length]} radius={i === products.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} barSize={20} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              )}
            </div>
          )}
          {/* 10.3 Market Penetration & Revenue Projections */}
          {projections.length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`text-xs font-semibold uppercase tracking-wider ${themeTextMuted}`}>10.3 Market Penetration & Revenue Projections</div>
                <div className={`flex items-center gap-1 p-0.5 rounded-md ${isDark ? "bg-slate-700/50" : "bg-slate-100"}`}>
                  <button onClick={() => setRevenueView("chart")} className={`p-1 rounded transition-colors ${revenueView === "chart" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Chart view"><BarChart2 size={12} /></button>
                  <button onClick={() => setRevenueView("table")} className={`p-1 rounded transition-colors ${revenueView === "table" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Table view"><Table2 size={12} /></button>
                </div>
              </div>
              {revenueView === "table" ? (
                <table className="w-full text-sm">
                  <thead><tr className={`text-xs ${themeTextMuted}`}>
                    {["Year", "% of Peak", "Bear", "Base", "Bull"].map(h => <th key={h} className="pb-2 text-left font-medium">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {projections.map((p, i) => (
                      <tr key={i} className={`border-t ${themeBorder}/50 ${p.pct_of_peak === "100%" ? "bg-cyan-500/5" : ""}`}>
                        <td className={`py-2 text-xs ${themeText}`}>{p.year}</td>
                        <td className={`py-2 font-mono text-xs ${themeTextMuted}`}>{p.pct_of_peak}</td>
                        <td className={`py-2 font-mono text-xs ${themeTextMuted}`}>{p.revenue_bear}</td>
                        <td className="py-2 font-mono text-xs text-cyan-400">{p.revenue_base}</td>
                        <td className="py-2 font-mono text-xs text-green-400">{p.revenue_bull}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="group" data-chart-container="">
                  <div className="hidden group-hover:flex gap-1 justify-end mb-1">
                    <button onClick={() => exportChartAsJSON(revenueChartData, "revenue-projections")} className={themeExportBtn}>JSON</button>
                    <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "revenue-projections")} className={themeExportBtn}>PNG</button>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={revenueChartData} margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} />
                      <XAxis dataKey="year" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                      <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Line type="monotone" dataKey="Bear" stroke="#64748b" strokeWidth={2} strokeDasharray="3 3" dot={false} />
                      <Line type="monotone" dataKey="Base" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Bull" stroke="#22c55e" strokeWidth={2} strokeDasharray="3 3" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Section 11: Valuation Framework (sec11)
    // Matches reference DOCX Section 10: 10.1-10.10
    if (navKey === "valuation-framework") {
      const s11 = sec("sec11");
      const probs = s11.phase_probabilities || {};
      const normProb = (v) => { const n = v || 0; return n <= 1 ? Math.round(n * 100) : n; };
      const phases = [
        { phase: "IND Application", probability: normProb(probs.ind_to_phase1) },
        { phase: "Phase I/II", probability: normProb(probs.phase1_2) },
        { phase: "Phase III", probability: normProb(probs.phase2_to_3) },
        { phase: "NDA Approval", probability: normProb(probs.phase3_to_nda) }
      ];
      const overallSuccess = normProb(probs.overall_success);

      const valByPhase = s11.valuation_by_phase || {};
      const valuations = Object.entries(valByPhase)
        .filter(([key]) => key !== "upside_to_current")
        .map(([key, v]) => ({
          phase: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
          timeline: v.timeline,
          bear: v.bear,
          base: v.base,
          bull: v.bull,
          avg: v.average
        }));

      const scenarios = s11.valuation_scenarios || {};
      const exitScenarios = (s11.exit_scenarios || []).filter(e => e.enterprise_value);

      const valChartData = valuations.map(v => ({
        phase: (v.timeline || v.phase || "").replace(/\(.+?\)/, "").trim().slice(0, 16),
        Bear: parseValStr(v.bear),
        Base: parseValStr(v.base),
        Bull: parseValStr(v.bull),
        Avg:  parseValStr(v.avg),
      })).filter(d => d.Base > 0 || d.Bear > 0 || d.Bull > 0);

      return (
        <div className="space-y-4">
          <div className="flex items-center flex-wrap gap-1">
            <h2 className={`text-lg font-semibold ${themeTextBright}`}>Section 11: Valuation Framework</h2>
            <SectionAgentBadge nk="valuation-framework" />
          </div>
          {/* 11.1 Methodology */}
          {s11.methodology && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-2 uppercase tracking-wider`}>11.1 Methodology</div>
              <p className={`text-xs ${themeText} leading-relaxed whitespace-pre-line`}>{s11.methodology}</p>
            </div>
          )}
          {/* 11.2 Clinical Development Phases & Success Probabilities */}
          <div className={`border rounded-lg p-4 ${themeCard}`}>
            <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>11.2 Clinical Development Phases & Success Probabilities</div>
            {(s11.clinical_development_phases || []).length > 0 && (
              <table className="w-full text-sm mb-4">
                <thead><tr className={`${themeTextMuted} text-xs`}>
                  {["Phase", "Primary Endpoint", "Success Probability"].map(h => <th key={h} className="pb-2 text-left font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {s11.clinical_development_phases.map((p, i) => (
                    <tr key={i} className="border-t border-slate-700/50">
                      <td className={`py-1.5 text-xs ${themeText}`}>{p.phase}</td>
                      <td className={`py-1.5 text-xs ${themeTextMuted}`}>{p.primary_endpoint}</td>
                      <td className="py-1.5 font-mono text-xs text-cyan-400">{p.success_probability}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(s11.phase_success_assumptions || []).length > 0 && (
              <ul className="space-y-1">
                {s11.phase_success_assumptions.map((a, i) => (
                  <li key={i} className={`text-[10px] ${themeTextMuted} flex gap-2`}><span className="text-cyan-500 mt-0.5">•</span><span>{a}</span></li>
                ))}
              </ul>
            )}
            {phases.some(p => p.probability > 0) && (
              <div className="mt-4 space-y-2">
                {phases.filter(p => p.probability > 0).map(p => (
                  <div key={p.phase}>
                    <div className={`flex justify-between text-[10px] mb-0.5 ${themeText}`}>
                      <span>{p.phase}</span>
                      <span className="font-mono text-cyan-400">{p.probability}%</span>
                    </div>
                    <div className={`h-1.5 rounded-full ${isDark ? "bg-slate-700" : "bg-slate-200"}`}>
                      <div className="h-full rounded-full bg-cyan-500/70 transition-all duration-700"
                           style={{ width: `${Math.min(p.probability, 100)}%` }} />
                    </div>
                  </div>
                ))}
                {overallSuccess > 0 && (
                  <div className={`mt-3 pt-3 border-t ${themeBorder}`}>
                    <div className="flex justify-between text-xs font-semibold mb-1">
                      <span className={themeTextBright}>Overall Success Probability</span>
                      <span className="text-cyan-400 font-mono">{overallSuccess}%</span>
                    </div>
                    <div className={`h-2 rounded-full ${isDark ? "bg-slate-700" : "bg-slate-200"}`}>
                      <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-700"
                           style={{ width: `${Math.min(overallSuccess, 100)}%` }} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* 11.3 Valuation Inputs & Assumptions */}
          {(s11.valuation_inputs || []).length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>11.3 Valuation Inputs & Assumptions</div>
              <table className="w-full text-sm">
                <thead><tr className={`${themeTextMuted} text-xs`}>
                  {["Input", "Base Case", "Notes"].map(h => <th key={h} className="pb-2 text-left font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {s11.valuation_inputs.map((v, i) => (
                    <tr key={i} className="border-t border-slate-700/50">
                      <td className={`py-1.5 text-xs ${themeText}`}>{v.input}</td>
                      <td className="py-1.5 font-mono text-xs text-cyan-400">{v.base_case}</td>
                      <td className={`py-1.5 text-[10px] ${themeTextMuted}`}>{v.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* 11.4 Scenario Definitions */}
          {(scenarios.bear || scenarios.base || scenarios.bull) && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>11.4 Scenario Definitions</div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: "bear", label: "Bear Case", color: "text-slate-400", border: "border-slate-600" },
                  { key: "base", label: "Base Case", color: "text-cyan-400", border: "border-cyan-500/30" },
                  { key: "bull", label: "Bull Case", color: "text-green-400", border: "border-green-500/30" }
                ].map(s => {
                  const sc = scenarios[s.key];
                  if (!sc) return null;
                  return (
                    <div key={s.key} className={`border ${s.border} rounded-lg p-3 ${isDark ? "bg-slate-900/50" : "bg-white"}`}>
                      <div className={`text-xs font-semibold ${s.color} mb-1`}>{s.label} ({sc.probability})</div>
                      <div className={`text-[10px] ${themeTextMuted} mb-2`}>Peak Sales: <span className={`font-mono font-semibold ${themeTextBright}`}>{sc.peak_sales}</span></div>
                      <ul className="space-y-1">
                        {(sc.key_assumptions || []).map((a, i) => (
                          <li key={i} className={`text-[10px] ${themeTextMuted} flex gap-1`}><span className="opacity-50">•</span><span>{a}</span></li>
                        ))}
                      </ul>
                      {sc.probability && (
                        <div className={`h-1 rounded-full mt-2 ${isDark ? "bg-slate-700" : "bg-slate-200"}`}>
                          <div
                            className={`h-full rounded-full ${s.key === "bear" ? "bg-slate-400" : s.key === "base" ? "bg-cyan-400" : "bg-green-400"}`}
                            style={{ width: sc.probability }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* 11.5 Enterprise Valuation Results */}
          {valuations.length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>11.5 Enterprise Valuation Results</div>
              {/* Phase valuation detail cards */}
              {s11.phase_valuation_details && Object.entries(s11.phase_valuation_details).map(([key, detail]) => (
                <div key={key} className="mb-4">
                  <div className="text-xs font-semibold text-cyan-400 mb-2">{detail.title}</div>
                  {detail.metrics && (
                    <table className="w-full text-sm mb-2">
                      <thead><tr className={`${themeTextMuted} text-[10px]`}>
                        {["Metric", "Bear", "Base", "Bull"].map(h => <th key={h} className="pb-1 text-left font-medium">{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {[
                          { label: "Peak Sales", keys: ["peak_sales_bear", "peak_sales_base", "peak_sales_bull"] },
                          { label: "Terminal FCF", keys: ["terminal_fcf_bear", "terminal_fcf_base", "terminal_fcf_bull"] },
                          { label: "Prob-Weighted", keys: ["prob_weighted_bear", "prob_weighted_base", "prob_weighted_bull"] },
                          { label: "NPV", keys: ["npv_bear", "npv_base", "npv_bull"] }
                        ].map(row => (
                          <tr key={row.label} className="border-t border-slate-700/50">
                            <td className={`py-1 text-[10px] ${themeTextMuted}`}>{row.label}</td>
                            <td className={`py-1 font-mono text-[10px] ${themeTextMuted}`}>{detail.metrics[row.keys[0]]}</td>
                            <td className="py-1 font-mono text-[10px] text-cyan-400">{detail.metrics[row.keys[1]]}</td>
                            <td className="py-1 font-mono text-[10px] text-green-400">{detail.metrics[row.keys[2]]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* 11.6 Valuation Summary & De-risking Progression */}
          {valuations.length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`text-xs font-semibold ${themeTextMuted} uppercase tracking-wider`}>11.6 Valuation Summary & De-risking Progression</div>
                <div className={`flex items-center gap-1 p-0.5 rounded-md ${isDark ? "bg-slate-700/50" : "bg-slate-100"}`}>
                  <button onClick={() => setValView("chart")} className={`p-1 rounded transition-colors ${valView === "chart" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Chart view"><BarChart2 size={12} /></button>
                  <button onClick={() => setValView("table")} className={`p-1 rounded transition-colors ${valView === "table" ? "bg-cyan-500/20 text-cyan-400" : `${themeTextMuted} hover:text-cyan-400`}`} title="Table view"><Table2 size={12} /></button>
                </div>
              </div>
              {valView === "table" ? (
                <table className="w-full text-sm">
                  <thead><tr className={`${themeTextMuted} text-xs`}>{["Development Phase", "Bear Case", "Base Case", "Bull Case", "Avg (Prob-Wt.)"].map(h => <th key={h} className="pb-2 text-left font-medium">{h}</th>)}</tr></thead>
                  <tbody>
                    {valuations.map((v, i) => (
                      <tr key={i} className="border-t border-slate-700/50">
                        <td className={`py-2 text-xs ${themeText}`}>{v.timeline || v.phase}</td>
                        <td className={`py-2 font-mono text-xs ${themeTextMuted}`}>{v.bear}</td>
                        <td className="py-2 font-mono text-xs text-cyan-400">{v.base}</td>
                        <td className="py-2 font-mono text-xs text-green-400">{v.bull}</td>
                        <td className="py-2 font-mono text-xs text-amber-400 font-semibold">{v.avg}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : valChartData.length > 0 ? (
                <div className="group" data-chart-container="">
                  <div className="hidden group-hover:flex gap-1 justify-end mb-1">
                    <button onClick={() => exportChartAsJSON(valChartData, "valuation-by-phase")} className={themeExportBtn}>JSON</button>
                    <button onClick={e => exportChartAsPNG(e.currentTarget.closest('[data-chart-container]'), "valuation-by-phase")} className={themeExportBtn}>PNG</button>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={valChartData} barCategoryGap="20%" margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e2e8f0"} />
                      <XAxis dataKey="phase" tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} />
                      <YAxis tick={{ fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} axisLine={false} label={{ value: "$M", angle: -90, position: "insideLeft", fill: isDark ? '#94a3b8' : '#64748b', fontSize: 9 }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: 8, fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="Bear" fill="#64748b" />
                      <Bar dataKey="Base" fill="#06b6d4" />
                      <Bar dataKey="Bull" fill="#22c55e" />
                      <Bar dataKey="Avg" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
            </div>
          )}
          {/* 11.7 Key Value Drivers & Sensitivities */}
          {(s11.key_value_drivers || []).length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-2 uppercase tracking-wider`}>11.7 Key Value Drivers & Sensitivities</div>
              <ul className="space-y-1.5">
                {s11.key_value_drivers.map((d, i) => (
                  <li key={i} className={`text-xs ${themeText} flex gap-2`}><span className="text-amber-500 mt-0.5">•</span><span>{d}</span></li>
                ))}
              </ul>
            </div>
          )}
          {/* 11.8 Comparable Transaction Analysis */}
          {(s11.comparable_transactions || []).length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>11.8 Comparable Transaction Analysis</div>
              <table className="w-full text-sm">
                <thead><tr className={`${themeTextMuted} text-xs`}>
                  {["Comparable", "Deal Value", "Peak Sales", "EV/Peak Sales", "Notes"].map(h => <th key={h} className="pb-2 text-left font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {s11.comparable_transactions.map((t, i) => (
                    <tr key={i} className="border-t border-slate-700/50">
                      <td className={`py-1.5 text-xs ${themeText}`}>{t.comparable}</td>
                      <td className={`py-1.5 font-mono text-xs ${themeTextMuted}`}>{t.deal_value || "—"}</td>
                      <td className={`py-1.5 font-mono text-xs ${themeTextMuted}`}>{t.peak_sales || "—"}</td>
                      <td className="py-1.5 font-mono text-xs text-cyan-400">{t.ev_peak_sales_multiple}</td>
                      <td className={`py-1.5 text-[10px] ${themeTextMuted}`}>{t.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* 11.9 Investment Returns & Exit Scenarios */}
          {exitScenarios.length > 0 && (
            <div className={`border rounded-lg p-4 ${themeCard}`}>
              <div className={`text-xs font-semibold ${themeTextMuted} mb-3 uppercase tracking-wider`}>11.9 Investment Returns & Exit Scenarios</div>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {exitScenarios.map((e, i) => (
                  <div key={i} className={`border rounded-lg p-3 ${themeCard}`}>
                    <div className={`text-[9px] uppercase ${themeTextMuted} mb-0.5`}>{e.acquirer_type}</div>
                    <div className={`text-xs font-semibold ${themeTextBright} mb-1`}>{e.scenario}</div>
                    <div className="text-xl font-bold text-cyan-400 font-mono">{e.enterprise_value}</div>
                    <div className={`flex items-center justify-between mt-2 text-[10px] ${themeTextMuted}`}>
                      <span>{e.timing}</span>
                      <span className="font-semibold text-green-400">{e.returns_5yr}</span>
                    </div>
                  </div>
                ))}
              </div>
              <table className="w-full text-sm">
                <thead><tr className={`${themeTextMuted} text-xs`}>
                  {["Exit Scenario", "Acquirer Type", "Est. Enterprise Value", "Timing", "Returns"].map(h => <th key={h} className="pb-2 text-left font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {exitScenarios.map((e, i) => (
                    <tr key={i} className="border-t border-slate-700/50">
                      <td className={`py-1.5 text-xs ${themeText}`}>{e.scenario}</td>
                      <td className={`py-1.5 text-xs ${themeTextMuted}`}>{e.acquirer_type}</td>
                      <td className="py-1.5 font-mono text-xs text-cyan-400">{e.enterprise_value}</td>
                      <td className={`py-1.5 text-xs ${themeTextMuted}`}>{e.timing}</td>
                      <td className="py-1.5 font-mono text-xs text-green-400">{e.returns_5yr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* 11.10 Investment Thesis Conclusion */}
          {s11.investment_thesis && (
            <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg p-4">
              <div className={`text-xs font-semibold ${themeTextMuted} mb-2 uppercase tracking-wider`}>11.10 Investment Thesis Conclusion</div>
              <p className={`text-xs ${themeText} leading-relaxed whitespace-pre-line`}>{s11.investment_thesis}</p>
              {(s11.key_inflection_points || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-cyan-500/20">
                  <div className={`text-[10px] font-semibold ${themeTextMuted} mb-2 uppercase`}>Key Inflection Points</div>
                  <div className="space-y-2">
                    {s11.key_inflection_points.map((point, i) => {
                      const parts = point.split(": ");
                      return (
                        <div key={i} className="border-l-2 border-cyan-500/40 pl-3">
                          <div className="text-xs font-semibold text-cyan-400">{parts[0]}</div>
                          <p className={`text-[10px] ${themeText} mt-0.5`}>{parts.slice(1).join(": ")}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Default: no matching section
    return null;
    }; // end renderSectionByNav

    // Store renderSectionByNav in ref for print container access
    sectionRendererRef.current = renderSectionByNav;

    // Render the active section using the shared renderer
    return renderSectionByNav(activeSection);
  };

  return (
    <>
    {/* Hidden print container — rendered always but only visible during window.print() */}
    <div className="print-container">
      {assessmentData && sectionRendererRef.current && (
        <div className="print-report">
          {/* Repeating header — logo top-right on every page */}
          <div className="print-header-repeat">
            <img src="/gnq-logo.svg" alt="GNQ Insilico" className="print-header-logo" />
          </div>
          {/* Repeating footer — copyright on every page */}
          <div className="print-footer-repeat">
            Copyright &copy; {new Date().getFullYear()} GNQ Insilico Inc. All Rights Reserved. THIS DOCUMENT CONTAINS PROPRIETARY TRADE SECRET INFORMATION — DO NOT DISTRIBUTE TO, OR SHARE WITH ANY UNAUTHORIZED PERSONS
          </div>
          {/* Cover page */}
          <div className="print-cover">
            <img src="/gnq-logo.svg" alt="GNQ Insilico" className="print-cover-logo" />
            <h1 className="print-cover-title">{activeConfig?.therapy?.name || "Assessment"}<br />Assessment Report</h1>
            <p className="print-cover-subtitle">Independent Assessment Report</p>
            {activeConfig?.therapy?.company && <p className="print-cover-company">Prepared for: {activeConfig.therapy.company}</p>}
            <p className="print-cover-date">Date: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
            <p className="print-cover-confidential">Confidential</p>
          </div>
          {/* All sections rendered using the same renderers as the interactive UI */}
          {["executive","pathway-scores","mechanism-mapping","causal-inference","stratification","efficacy","safety","summary","market","valuation","valuation-framework"].map(navKey => (
            <div key={navKey} className="print-section">
              {sectionRendererRef.current(navKey)}
            </div>
          ))}
          {/* Disclaimers */}
          <div className="print-section print-disclaimers">
            <h2 className="print-section-title">Important Disclaimers & Limitations</h2>
            <h4>Projections and Assessments — Not Guaranteed Outcomes</h4>
            <p>This analysis is based on projections, computational modeling, literature data, and proprietary preclinical and clinical assessments. The conclusions, efficacy predictions, therapeutic window estimates, and comparative safety profiles presented herein are not guarantees of future clinical performance and should not be construed as such.</p>
            <h4>Regulatory Uncertainty</h4>
            <p>All regulatory pathway assessments, FDA interaction assumptions, and claims regarding scheduling or labeling are contingent on FDA review and approval. Actual regulatory determinations may differ materially from the projections presented. This analysis does not constitute regulatory advice or strategy; regulatory strategy should be obtained from qualified regulatory consultants.</p>
            <h4>No Independent Verification</h4>
            <p>The conclusions in this analysis are based solely on data provided by the therapy developer and publicly available literature. This assessment does not represent independent verification, third-party validation, or FDA evaluation of the therapy's safety or efficacy.</p>
            <h4>Clinical Translation Uncertainty</h4>
            <p>Preclinical findings may not translate directly to human clinical outcomes. All efficacy, safety, and therapeutic window claims require validation through properly designed human clinical trials.</p>
            <h4>Abuse Liability Assessment Limitations</h4>
            <p>Abuse liability projections are based on preclinical models and historical comparator data. Actual human abuse liability patterns may differ based on prescribing patterns, patient populations, real-world misuse contexts, and factors not captured in laboratory settings.</p>
            <h4>Respiratory Depression Risk</h4>
            <p>While preclinical data may show no respiratory depression at tested doses, respiratory effects in diverse human populations — including elderly, opioid-naïve, or those with underlying pulmonary disease — require prospective clinical evaluation.</p>
            <h4>No Recommendations for Clinical Use</h4>
            <p>This analysis is intended for regulatory planning and scientific assessment only. It does not constitute medical advice or clinical recommendations for patient treatment. All clinical decisions should be made by qualified healthcare providers based on individual patient assessment and approved labeling.</p>
            <h4>For External Distribution</h4>
            <p>If this document is shared with external parties (investors, partners, regulatory consultants), recipients should understand that:</p>
            <ul>
              <li>All projections carry inherent uncertainty</li>
              <li>Independent expert review is strongly recommended</li>
              <li>No representations regarding regulatory or commercial success are made</li>
              <li>Recipients assume responsibility for their own due diligence</li>
            </ul>
          </div>
        </div>
      )}
    </div>

    <div className={`h-screen flex flex-col text-sm transition-colors duration-300 print:hidden ${isDark ? "bg-[rgb(8,12,24)] text-slate-200" : "bg-slate-100 text-slate-900"}`}>
      {/* Use Case Switch Confirmation Dialog */}
      {pendingSwitchId && (() => {
        const target = useCases.find(uc => uc.id === pendingSwitchId);
        const current = activeUseCase;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className={`${isDark ? "bg-slate-800 border-cyan-500/30" : "bg-white border-slate-300"} border rounded-xl shadow-2xl p-5 max-w-sm w-full mx-4`}>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={18} className="text-amber-400" />
                <span className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-800"}`}>Switch Use Case?</span>
              </div>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"} mb-1`}>
                {flowStep === "pipeline"
                  ? `The assessment pipeline is running for ${current?.name || "the current therapy"}. Switching will not stop it — you can return to see results.`
                  : `You have unsaved changes for ${current?.name || "the current therapy"}.`}
              </p>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"} mb-4`}>
                Your progress will be saved and you can return anytime.
              </p>
              <div className={`flex items-center gap-2 mb-3 ${isDark ? "bg-slate-900/50" : "bg-slate-100"} rounded-lg p-2.5`}>
                <span className="text-[10px] text-slate-500">{current?.name || "Current"}</span>
                <ChevronRight size={12} className="text-slate-600" />
                <span className="text-[10px] text-cyan-400 font-medium">{target?.name || "New"}</span>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setPendingSwitchId(null)} className={`text-xs ${isDark ? "text-slate-400 hover:text-slate-200 hover:bg-slate-700/50" : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"} px-3 py-1.5 rounded-lg transition-colors`}>Cancel</button>
                <button onClick={() => executeSwitchUseCase(pendingSwitchId)} className="text-xs text-white bg-cyan-600 hover:bg-cyan-500 px-3 py-1.5 rounded-lg transition-colors font-medium">Switch</button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* ═══ TOPBAR: Logo + Stepper + Use Case Selector ═══ */}
      <div className={`border-b px-4 py-2 flex items-center justify-between gap-4 flex-shrink-0 ${isDark ? "bg-slate-900/80 border-cyan-500/20" : "bg-white border-slate-300 shadow-sm"}`}>
        {/* Left: Logo + Title + Version */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-7 h-7 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded flex items-center justify-center"><FlaskConical size={16} className="text-white" /></div>
          <span className="font-semibold text-cyan-400">GNQ InSilico Assessment Platform</span>
          <span className="text-[10px] bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded border border-cyan-500/20">Causal Engine v2</span>
          {d && <span className="text-[10px] bg-green-500/10 text-green-400 px-2 py-0.5 rounded border border-green-500/20">Sections Loaded</span>}
        </div>

        {/* Right: Use Case Selector + User Pill */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="relative" ref={useCaseDropdownRef}>
            <button
              onClick={() => setShowUseCaseDropdown(p => !p)}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-md border transition-colors ${isDark ? "border-cyan-500/30 hover:border-cyan-500/50 bg-slate-800/60" : "border-slate-300 hover:border-cyan-400 bg-white shadow-sm"}`}
            >
              <Beaker size={12} className="text-cyan-400 flex-shrink-0" />
              {activeUseCase ? (
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className={`text-[11px] font-medium flex-shrink-0 ${isDark ? "text-cyan-300" : "text-cyan-600"}`}>{activeUseCase.name}</span>
                  {activeUseCase.indication && <span className={`text-[9px] truncate max-w-[10rem] ${isDark ? "text-slate-500" : "text-slate-500"}`}>{activeUseCase.indication}</span>}
                </div>
              ) : (
                <span className="text-[11px] text-slate-500">Select use case...</span>
              )}
              <ChevronDown size={11} className={`text-slate-500 flex-shrink-0 transition-transform ${showUseCaseDropdown ? "rotate-180" : ""}`} />
            </button>
            {showUseCaseDropdown && (
              <div className="absolute top-full right-0 mt-1 w-64 bg-slate-800 border border-cyan-500/30 rounded-lg shadow-xl z-50 overflow-hidden">
                <div className="px-2.5 py-1 border-b border-slate-700/50">
                  <span className="text-[9px] text-slate-500 uppercase tracking-wider font-medium">Select Use Case</span>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {useCases.length === 0 ? (
                    <p className="text-[10px] text-slate-600 italic px-3 py-2">No use cases yet.</p>
                  ) : (
                    useCases.map(uc => (
                      <div key={uc.id} className={`group flex items-center gap-1 px-2.5 py-1.5 transition-colors ${uc.id === activeUseCaseId ? "bg-cyan-500/10" : "hover:bg-slate-700/50"}`}>
                        {renamingUcId === uc.id ? (
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={() => commitRename(uc.id)}
                            onKeyDown={e => { if (e.key === "Enter") commitRename(uc.id); if (e.key === "Escape") setRenamingUcId(null); }}
                            onClick={e => e.stopPropagation()}
                            className="flex-1 text-[10px] px-1.5 py-0.5 rounded border bg-slate-900 border-cyan-500/50 text-slate-200 outline-none min-w-0"
                          />
                        ) : (
                          <button
                            onClick={() => { switchUseCase(uc.id); setShowUseCaseDropdown(false); }}
                            className="flex items-center gap-1.5 flex-1 text-left min-w-0"
                          >
                            <div className="w-5 h-5 rounded bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-[8px] font-bold text-cyan-400">{(uc.name || "?").slice(0, 2).toUpperCase()}</span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <span className="text-[10px] font-medium text-slate-200 truncate">{uc.name}</span>
                              {uc.id === activeUseCaseId && <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />}
                              <span className="text-[9px] text-slate-500 truncate">{uc.indication || ""}</span>
                            </div>
                            <span className="text-[9px] text-slate-600 flex-shrink-0">{uc.lastRunAt ? new Date(uc.lastRunAt).toLocaleDateString([], { month: "short", day: "numeric" }) : ""}</span>
                          </button>
                        )}
                        <div className="hidden group-hover:flex items-center gap-0.5 flex-shrink-0 ml-0.5">
                          <button
                            onClick={e => { e.stopPropagation(); setRenamingUcId(uc.id); setRenameValue(uc.name); }}
                            className="p-0.5 text-slate-500 hover:text-cyan-400 transition-colors"
                            title="Rename"
                          ><Pencil size={10} /></button>
                          <button
                            onClick={e => { e.stopPropagation(); if (window.confirm(`Clear chat history for "${uc.name}"?`)) clearChatHistory(uc.id); }}
                            className="p-0.5 text-slate-500 hover:text-red-400 transition-colors"
                            title="Clear chat history"
                          ><Trash2 size={10} /></button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="border-t border-slate-700/50">
                  <button
                    onClick={() => { setShowUseCaseDropdown(false); setShowNewUseCaseModal(true); setNewUseCaseName(""); setNewUseCaseIndication(""); }}
                    className="w-full flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-slate-700/50 transition-colors text-left"
                  >
                    <Plus size={10} className="text-cyan-400" />
                    <span className="text-[10px] text-cyan-400 font-medium">New Use Case</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Theme toggle (Task 66/68) */}
          <button
            onClick={toggleTheme}
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className={`p-1.5 rounded-md border transition-colors flex-shrink-0 ${isDark ? "border-slate-700 hover:border-cyan-500/30 text-slate-400 hover:text-cyan-400 bg-slate-800/40" : "border-slate-300 hover:border-cyan-500/50 text-slate-500 hover:text-cyan-600 bg-white/60"}`}
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md flex-shrink-0 ${isDark ? "bg-slate-800/40 border border-slate-700/50" : "bg-white/60 border border-slate-200"}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isDark ? "bg-cyan-500/20" : "bg-cyan-100"}`}>
              <User size={10} className="text-cyan-400" />
            </div>
            <span className={`text-[10px] font-medium ${isDark ? "text-slate-300" : "text-slate-600"}`}>Andrea D.</span>
          </div>
        </div>
      </div>

      {/* Reset confirm bar (full width) */}
      {resetConfirm && (
        <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2 flex-shrink-0">
          <span className="text-[10px] text-red-400 flex-1">Clear all config inputs?</span>
          <button onClick={handleReset} className="text-[10px] text-red-400 hover:text-red-300 font-medium px-1.5 py-0.5 rounded hover:bg-red-500/20 transition-colors">Reset</button>
          <button onClick={() => setResetConfirm(false)} className="text-[10px] text-slate-500 hover:text-slate-300 px-1.5 py-0.5 rounded hover:bg-slate-700/50 transition-colors">Cancel</button>
        </div>
      )}

      {/* ═══ MAIN BODY: Nav (with status pinned bottom) + Wide Content ═══ */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* ── Left: Merged Nav + Left Panel + Status ── */}
        <div className={`w-72 border-r flex flex-col overflow-hidden flex-shrink-0 ${isDark ? "bg-slate-900/50 border-cyan-500/20" : "bg-white border-slate-300"}`}>
          {/* Scrollable: Nav sections + Left panel body + Workspace */}
          <div className="flex-1 overflow-y-auto">
            {/* Section Navigation */}
            <nav className={`py-2 ${flowStep === "query" ? "opacity-40 pointer-events-none select-none" : ""}`}>
              {navSections.map(navSec => (
                <div key={navSec.id}>
                  <button onClick={() => toggleNav(navSec.id)} className="w-full px-3 py-2 flex items-center justify-between hover:bg-cyan-500/10 text-slate-300">
                    <div className="flex items-center gap-2"><navSec.icon size={14} className="text-cyan-400/60" /><span className="text-xs font-medium">{navSec.label}</span></div>
                    {expandedNav.includes(navSec.id) ? <ChevronDown size={14} className="text-cyan-400/60" /> : <ChevronRight size={14} className="text-cyan-400/60" />}
                  </button>
                  {expandedNav.includes(navSec.id) && <div className="ml-5 border-l border-cyan-500/20">
                    {navSec.items.map(item => {
                      const isReady = readySections.has(item.id);
                      const isActive = activeSection === item.id && !showYAMLPreview;
                      const isFlashing = flashingSections.has(item.id);
                      const isGenerating = generatingSections.has(item.id);
                      return (
                        <button key={item.id} onClick={() => { setActiveSection(item.id); setShowYAMLPreview(false); }} className={`w-full px-3 py-1.5 text-left text-xs transition-colors flex items-center gap-2 ${isActive ? "bg-cyan-500/20 text-cyan-400 font-medium" : isReady ? `${isDark ? "text-slate-200" : "text-slate-700"} hover:bg-cyan-500/10 hover:text-cyan-600` : isGenerating ? `${isDark ? "text-slate-400" : "text-slate-500"} pointer-events-none` : `${isDark ? "text-slate-500" : "text-slate-400"} pointer-events-none`}`}>
                          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                            {isFlashing && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />}
                            {isGenerating && !isReady && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />}
                            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${isActive ? "bg-cyan-400" : isReady ? "bg-green-400" : isGenerating ? "bg-amber-400" : isDark ? "bg-slate-700" : "bg-slate-300"}`} />
                          </span>
                          {item.label}
                        </button>
                      );
                    })}
                  </div>}
                </div>
              ))}
            </nav>

            {/* Workspace (collapsible, in nav) */}
            <div className="border-t border-cyan-500/20">
              <div className="flex items-center justify-between px-3 py-2.5 flex-none">
                <button onClick={() => setRightSectionCollapsed(p => ({ ...p, workspace: !p.workspace }))} className="flex items-center gap-2 flex-1 hover:opacity-80 text-left">
                  <FileUp size={13} className="text-cyan-400" />
                  <span className="text-[11px] font-medium text-cyan-400">{activeUseCase ? `${activeUseCase.name} Collection` : "My Collection"}</span>
                  {rightSectionCollapsed.workspace ? <ChevronRight size={13} className="text-slate-500 ml-1" /> : <ChevronDown size={13} className="text-slate-500 ml-1" />}
                </button>
                {(filteredFiles.length > 0 || filteredConfigs.length > 0 || filteredReports.length > 0) && (
                  clearCollectionConfirm ? (
                    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                      <span className="text-[9px] text-slate-500">Clear all?</span>
                      <button onClick={() => { setUploadedFiles(p => p.filter(f => f.useCaseId !== activeUseCaseId)); setExtractedDocs([]); setSavedConfigs(p => p.filter(sc => sc.useCaseId !== activeUseCaseId)); setGeneratedReports(p => p.filter(r => r.useCaseId !== activeUseCaseId)); setClearCollectionConfirm(false); }} className="text-[9px] text-red-400 hover:text-red-300 font-medium px-1 py-0.5 rounded hover:bg-red-500/20 transition-colors">Yes</button>
                      <button onClick={() => setClearCollectionConfirm(false)} className="text-[9px] text-slate-500 hover:text-slate-300 px-1 py-0.5 rounded hover:bg-slate-700/50 transition-colors">No</button>
                    </div>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); setClearCollectionConfirm(true); }} className="text-[9px] text-slate-600 hover:text-slate-400 transition-colors">Clear</button>
                  )
                )}
              </div>
              {!rightSectionCollapsed.workspace && (
                <div className="max-h-48 overflow-y-auto">{renderDocsReportsPanel()}</div>
              )}
            </div>

            {/* Left panel body content (config, review, pipeline states) */}
            {renderLeftPanel()}
          </div>

          {/* Status pinned to nav bottom */}
          {(flowStep === "complete" || flowStep === "stopped") && (
            <div className={`flex-shrink-0 border-t px-3 py-3 space-y-1.5 ${isDark ? "border-cyan-500/20 bg-slate-900/80" : "border-slate-200 bg-slate-50"}`}>
              <button onClick={() => { setIsRerun(true); setConfigChanges(null); setAffectedSections([]); setRerunSummary(null); setFlowStep("review"); }} className="w-full bg-cyan-600 text-white rounded-lg py-2 text-xs font-medium hover:bg-cyan-500 flex items-center justify-center gap-2">
                <RotateCcw size={12} /> Edit Inputs
              </button>
              <button onClick={exportReport} className={`w-full border rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-2 text-cyan-500 hover:bg-cyan-500/10 ${themeBorder}`}>
                <Download size={12} /> Export Report
              </button>
              <button onClick={resetFlow} className={`w-full border rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-2 text-cyan-500 ${isDark ? "border-slate-700 hover:bg-slate-700/50" : "border-slate-300 hover:bg-slate-100"}`}>
                <Sparkles size={12} /> New Assessment
              </button>
            </div>
          )}
        </div>

        {/* ── Right: Wide Content Area ── */}
        <div className={`flex-1 overflow-y-auto p-5 ${isDark ? "bg-[rgb(8,12,24)]" : "bg-slate-100"}`}><div className="max-w-5xl mx-auto">{renderContent()}</div></div>
      </div>

      {/* ═══ BOTTOM PANEL: Chat + Workspace ═══ */}
      <div
        className={`border-t-2 flex flex-col flex-shrink-0 ${!chatBarExpanded ? "transition-all duration-300 ease-in-out" : ""} ${isDark ? "border-cyan-500/40 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900" : "border-cyan-300 bg-gradient-to-r from-white via-cyan-50/30 to-white shadow-[0_-4px_20px_rgba(6,182,212,0.1)]"}`}
        style={{ height: chatBarExpanded ? bottomPanelHeight : 64 }}
      >
        {/* Drag handle (visible when expanded) */}
        {chatBarExpanded && (
          <div
            ref={bottomDragRef}
            onMouseDown={handleBottomDragStart}
            className={`h-1.5 flex-shrink-0 cursor-ns-resize group flex items-center justify-center ${isDark ? "hover:bg-cyan-500/10" : "hover:bg-cyan-50"}`}
          >
            <div className={`w-10 h-0.5 rounded-full transition-colors ${isDark ? "bg-slate-700 group-hover:bg-cyan-500/50" : "bg-slate-300 group-hover:bg-cyan-400"}`} />
          </div>
        )}
        {/* Header (always visible) */}
        <div className={`flex items-center justify-between px-4 h-16 flex-shrink-0`}>
          {/* Left: Tab buttons + icon */}
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer ${isDark ? "bg-cyan-500/15 ring-1 ring-cyan-500/30" : "bg-cyan-100 ring-1 ring-cyan-300/50"}`}
              onClick={() => setChatBarExpanded(p => !p)}
            >
              <Bot size={16} className="text-cyan-400" />
            </div>
            {/* Tabs */}
            <div className={`flex items-center gap-0.5 p-0.5 rounded-lg ${isDark ? "bg-slate-800/60" : "bg-slate-100"}`}>
              <button
                onClick={(e) => { e.stopPropagation(); setBottomPanelTab("chat"); if (!chatBarExpanded) setChatBarExpanded(true); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${bottomPanelTab === "chat" ? (isDark ? "bg-cyan-500/15 text-cyan-400" : "bg-white text-cyan-600 shadow-sm") : (isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700")}`}
              >
                <Bot size={12} /> GNQ Assistant
                {chatLoading && <Loader2 size={10} className="text-cyan-400 animate-spin" />}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setBottomPanelTab("workspace"); if (!chatBarExpanded) setChatBarExpanded(true); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${bottomPanelTab === "workspace" ? (isDark ? "bg-cyan-500/15 text-cyan-400" : "bg-white text-cyan-600 shadow-sm") : (isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700")}`}
              >
                <FileText size={12} /> Reports & History
                {filteredReports.length > 0 && <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-cyan-500/20 text-cyan-300" : "bg-cyan-100 text-cyan-600"}`}>{filteredReports.length}</span>}
              </button>
            </div>
          </div>
          {/* Right: inline input (collapsed) + controls */}
          <div className="flex items-center gap-3">
            {!chatBarExpanded && bottomPanelTab === "chat" && (
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && chatInput.trim()) { sendChat(); setChatBarExpanded(true); } }}
                onClick={e => e.stopPropagation()}
                placeholder="Type a question about the assessment..."
                disabled={chatLoading}
                className={`w-80 text-xs rounded-lg px-3 py-2 transition-colors ${isDark ? "bg-slate-800/70 border border-cyan-500/30 text-slate-200 placeholder:text-slate-500 focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/20" : "bg-white border border-slate-300 text-slate-700 placeholder:text-slate-500 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-300/30"} disabled:opacity-50 outline-none`}
              />
            )}
            {bottomPanelTab === "chat" && (
              <button
                onClick={(e) => { e.stopPropagation(); setChatMessages([{ role: "assistant", content: "Hello! Complete the assessment query to load the report. I can then help you explore pathway scores, efficacy projections, or any section." }]); setContextSent(false); }}
                className={`text-[10px] px-2 py-1 rounded-md transition-colors ${isDark ? "text-slate-500 hover:text-slate-300 hover:bg-slate-700/50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"}`}
              >
                Clear
              </button>
            )}
            <button
              onClick={() => setChatBarExpanded(p => !p)}
              className={`flex items-center gap-1 text-[10px] uppercase tracking-wider ${isDark ? "text-cyan-500/60 hover:text-cyan-400" : "text-cyan-600/50 hover:text-cyan-600"} transition-colors`}
            >
              <span>{chatBarExpanded ? "Collapse" : "Expand"}</span>
              <ChevronDown size={12} className={`transition-transform ${chatBarExpanded ? "" : "rotate-180"}`} />
            </button>
          </div>
        </div>
        {/* Expanded area — tabbed content */}
        {chatBarExpanded && (
          <div className={`flex-1 flex flex-col min-h-0 border-t ${isDark ? "border-cyan-500/20" : "border-slate-300"}`}>
            {bottomPanelTab === "chat" ? (
              renderChatPanel()
            ) : (
              <div className="flex-1 overflow-y-auto p-3">
                {renderDocsReportsPanel()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ NEW USE CASE MODAL (fixed overlay) ═══ */}
      {showNewUseCaseModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={() => setShowNewUseCaseModal(false)}>
          <div className="bg-slate-800 border border-cyan-500/30 rounded-lg shadow-2xl w-64 p-3" onClick={e => e.stopPropagation()}>
            <h3 className="text-xs font-medium text-cyan-400 mb-2.5">New Use Case</h3>
            <div className="flex flex-col gap-2">
              <div>
                <label className="text-[9px] text-slate-400 uppercase tracking-wider font-medium mb-0.5 block">Use Case Name</label>
                <input
                  autoFocus
                  value={newUseCaseName}
                  onChange={e => setNewUseCaseName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && newUseCaseName.trim()) {
                      createUseCase(newUseCaseName.trim(), newUseCaseIndication.trim());
                      setShowNewUseCaseModal(false);
                    }
                  }}
                  placeholder="e.g. TARA-002"
                  className="w-full text-xs bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-slate-200 placeholder-slate-500 outline-none focus:border-cyan-500/50"
                />
              </div>
              <div>
                <label className="text-[9px] text-slate-400 uppercase tracking-wider font-medium mb-0.5 block">Indication</label>
                <input
                  value={newUseCaseIndication}
                  onChange={e => setNewUseCaseIndication(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && newUseCaseName.trim()) {
                      createUseCase(newUseCaseName.trim(), newUseCaseIndication.trim());
                      setShowNewUseCaseModal(false);
                    }
                  }}
                  placeholder="e.g. NMIBC"
                  className="w-full text-xs bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-slate-200 placeholder-slate-500 outline-none focus:border-cyan-500/50"
                />
              </div>
              <div className="flex justify-end gap-1.5 mt-1.5">
                <button
                  onClick={() => setShowNewUseCaseModal(false)}
                  className="text-[10px] text-slate-400 hover:text-slate-200 px-2 py-1 rounded hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newUseCaseName.trim()) {
                      createUseCase(newUseCaseName.trim(), newUseCaseIndication.trim());
                      setShowNewUseCaseModal(false);
                    }
                  }}
                  disabled={!newUseCaseName.trim()}
                  className="text-[10px] font-medium text-slate-900 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1 rounded transition-colors"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
